/*
 * PetalPal slide cart — core (v2)
 * ===============================
 * Theme-native replacement for the Qikify Sticky Cart.
 *
 * WHAT CHANGED FROM v1 (all from Tyler's review):
 *   • Dual reward ladder. The cart now knows about BOTH axes and promotes
 *     whichever reward is cheapest to reach:
 *        value    — free shipping, per-country, from Shopify's own shipping
 *                   settings (35 countries synced nightly)
 *        quantity — the live automatic discounts (3+ = 15%, 6+ = 25%)
 *     Neither was surfaced before. A shopper on 2 items had no idea a third
 *     saves 15% on the whole order — that is the single biggest miss in the
 *     old cart.
 *   • Gift note is collapsed by default (was an always-open textarea eating
 *     the fold for the majority who never write one).
 *   • Upsells render as a snap-scrolling rail with a stated REASON per item.
 *
 * DESIGN RULES (from the 2026-07-22 Qikify incident — do not relax):
 *   1. No raw-HTML config. Everything configurable is a boolean, number, id or
 *      PLAIN-TEXT string, and every string reaches the DOM via textContent.
 *      A merchant-editable HTML field is exactly how a checkout skimmer got
 *      injected into every petalpal.com pageview.
 *   2. No third-party runtime. Config is baked in at render time.
 *   3. Checkout is a plain <form action="/checkout"> submit. Nothing overlays
 *      it, nothing intercepts it.
 *
 * Depends on: pp-cart-track.js (window.PPCartTrack)
 */
(function (w, d) {
  'use strict';

  /* If this cart cannot boot it MUST hand the cart surface back to the theme.
     pp-cart.css hides Dawn's <cart-drawer>/<cart-notification> because this
     cart owns that surface — so a bare `return` here leaves the store with NO
     cart at all: Dawn still opens its drawer and still locks <body> with
     .overflow-hidden, but the drawer is display:none. Add-to-cart looks dead
     and the page looks frozen, which is precisely what one unpublished
     collection did on 2026-08-11 (it rendered a Liquid error into the config
     JSON below and every parse after that threw). Marking <html> lets the
     theme's own drawer come back — a plain drawer beats no cart, always. */
  function bail() {
    try { d.documentElement.classList.add('pp-cart-dead'); } catch (e) {}
  }

  var cfgEl = d.getElementById('pp-cart-config');
  if (!cfgEl) { bail(); return; }

  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent || '{}'); } catch (e) { bail(); return; }
  if (!CFG || CFG.enabled === false) { bail(); return; }

  var REWARDS = CFG.rewards || {};
  var track = w.PPCartTrack || { emit: function () {} };

  /* ─────────────────────── CART V2 PREVIEW GATE ───────────────────────
     The redesigned cart (side rail on desktop, receipt footer, geo delivery
     band) ships DARK. It renders only for a browser that has explicitly
     armed it, so it can be tested on the live storefront without a single
     shopper seeing it:

         https://petalpal.com/?pp_cart_v2=1     arm  (persists)
         https://petalpal.com/?pp_cart_v2=0     disarm

     The flag is sticky in localStorage — the param only has to be visited
     once, then every page on the store shows v2 for that browser. Everyone
     else takes the identical code path they took before this existed: V2 is
     false, the root never gets `pp-cart--v2`, and every v2 CSS rule is
     scoped to that class, so there is nothing for them to match.

     Same shape as the bundle widget's dev mark (`ppb_dev`) and the upsell QA
     override (`pp_force_upsell_cards`) — a persisted string, read once at
     boot, wrapped because Safari private mode throws on localStorage. */
  /* LIVE FOR EVERYONE since 2026-08-27 (Tyler). The metafield is the kill
     switch and it always wins: `cart_v2: false` in pippy.cart_config turns
     the redesign off store-wide with no deploy and no CDN wait. Only when
     it is on does the per-browser override apply, so a stale localStorage
     value can never defeat the switch. */
  var V2 = CFG.cart_v2 !== false;
  try {
    var v2m = (w.location.search || '').match(/[?&]pp_cart_v2=([01])/);
    if (v2m) w.localStorage.setItem('pp_cart_v2', v2m[1]);
    if (V2) {
      var v2s = w.localStorage.getItem('pp_cart_v2');
      if (v2s === '0') V2 = false;      // this browser opted out, for comparison
    }
  } catch (e) { /* private mode — fall back to the store-wide setting */ }

  /* The rail needs both columns on screen (340 + 520). Below that the drawer
     keeps today's single-column layout, so v2 on a tablet is the receipt
     footer plus the existing carousel — not a squeezed rail.
     Evaluated per call, never cached: v2Resize() re-renders on crossing. */
  function v2Rail() {
    return V2 && !!(w.matchMedia && w.matchMedia('(min-width: 1100px)').matches);
  }
  function v2Phone() {
    return V2 && !!(w.matchMedia && w.matchMedia('(max-width: 560px)').matches);
  }

  // ───────────────────────────── state ─────────────────────────────
  var cart = null;
  var open = false;
  var root = null;
  var panelEl = null;         // the focus-trap boundary
  var liveEl = null;          // polite live region for cart changes
  var lastFocus = null;       // what to hand focus back to on close
  var seenUpsells = {};
  var earnedKeys = {};        // reward key -> true, so we celebrate once
  var noteStarted = false;
  var noteOpen = false;

  // ──────────────────────────── icons ──────────────────────────────
  var ICON = {
    close: 'M6 6l12 12M18 6L6 18',
    /* v2's "Popular for" pill. A heart, not a star: the star already means
       "rating" everywhere else in this drawer, and reusing it for an
       audience tag would make a segment look like a score. */
    heart: 'M12 20.3l-1.3-1.2C6 14.9 3 12.2 3 8.9 3 6.2 5.1 4 7.8 4c1.5 0 3 .7 3.9 1.9L12 6l.3-.1C13.2 4.7 14.7 4 16.2 4 18.9 4 21 6.2 21 8.9c0 3.3-3 6-7.7 10.2z',
    trash: 'M4 7h16M9 7V5h6v2M7 7l1 13h8l1-13',
    plus: 'M12 5v14M5 12h14',
    check: 'M4 12l5 5L20 6',
    lock: 'M6 10V7a6 6 0 1112 0v3M5 10h14v11H5z',
    truck: 'M3 16V6h11v10M14 9h4l3 3v4h-7M6.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
    star: 'M12 3.5l2.6 5.3 5.9.85-4.25 4.15 1 5.85L12 16.9l-5.25 2.75 1-5.85L3.5 9.65l5.9-.85z',
    tag: 'M3 11V4h7l10 10-7 7L3 11zM7.5 7.5h.01',
    /* Bouquet in a vase. The previous mark was loose stems that read as a
       smudge at 15px — at this size the silhouette has to carry it, so this is
       a solid vase body with three distinct blooms above the rim. */
    flower: 'M8 13h8l-.7 6.1a2 2 0 01-2 1.8h-2.6a2 2 0 01-2-1.8zM6.8 13h10.4M12 13V9.4M12 5.4a2 2 0 110 4 2 2 0 010-4M9.9 13c-.3-2-1.2-3.2-2.6-3.9M6.9 7.6a1.6 1.6 0 110 3.2 1.6 1.6 0 010-3.2M14.1 13c.3-2 1.2-3.2 2.6-3.9M17.1 7.6a1.6 1.6 0 110 3.2 1.6 1.6 0 010-3.2',
    sparkle: 'M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4zM18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8z',
    back: 'M15 19l-7-7 7-7',
    /* Return arrow for the "30-day returns" trust item. Reusing the bare
       `back` chevron here rendered as a literal "<" at 13px — the shaft is
       what makes it read as an arrow. */
    returns: 'M9 14L4 9l5-5M20 20v-7a4 4 0 00-4-4H4',
    card: 'M3 6h18v12H3zM3 6l9 6 9-6'
  };

  /* ── gift-note AI assist ────────────────────────────────────────────
   * A blank textarea is the hardest part of buying a gift — people stall on
   * what to say, and an unwritten note is an unsold $5 add-on (and, per
   * GIFT_NOTE_INTELLIGENCE_HANDOFF.md, a Tier-A product with a 10.7%
   * repurchase rate).
   *
   * Two taps — who it's for, and the occasion — then three suggestions the
   * shopper can accept and edit. If they've already written something the
   * button becomes "Polish it", which fixes spelling and grammar while keeping
   * their words; it NEVER silently rewrites what someone wrote.
   *
   * Worth noting commercially: those two taps capture recipient and occasion as
   * DECLARED facts at purchase time. That handoff doc currently plans to infer
   * the same signals by running a model over finished messages after the fact —
   * this gets them first-hand, structured, for free.
   */
  /* Quick picks are SHORTCUTS that fill the text field — never the only
     options. Chip-only lists silently excluded most of the people and
     occasions PetalPal actually sells for (nieces, in-laws, teachers,
     new babies, retirements, condolences…). The field is always free text. */
  /* Female roles only (Tyler 2026-08-02): PetalPal's buyers gift to women —
     "Dad" and "Brother" chips were suggesting recipients we don't sell for.
     Free text still accepts anyone; the chips just shouldn't lead there.
     ORDER IS DATA, not taste: declared/inferred relationships across 8,226
     orders (customer_intelligence, queried 2026-08-02) — friend 46%,
     parent 28%, sibling 13%, own child 11%, other family 1%, partner <1%.
     Friend first, however strong the "Dear Mum" instinct is. */
  /* usify(): these are ARRAYS, so they never pass through S() and would have
     kept saying "Mum"/"Aunty" to a US shopper even after the string path was
     localised. The chip is the worst place to get this wrong — tapping it
     writes that exact word into the message the model then builds on. */
  /* AMERICAN ENGLISH FOR AMERICAN SHOPPERS.
     Shopify locales are LANGUAGE-scoped, not market-scoped: the US and AU
     storefronts both render `en`, so both were served the Australian copy this
     cart was written in — a US shopper picking a recipient chip was offered
     "Mum" and "Aunty" (Tyler, on petalpal.com). Shopify can't fix this without
     a separate en-US locale for every string in the theme; the market, however,
     is already on hand — pp-cart.liquid resolves
     `localization.country.iso_code` and ships it as CFG.country.

     Gated on ENGLISH so a German or Dutch storefront is never touched, and
     word-boundary + case-preserving so it can't corrupt a longer word ("Mumbai"
     survives) or flatten a capital. Deliberately a short list of words that
     actually differ and actually appear in gift-note copy — this is not a
     general en-GB→en-US translator and must not grow into one. */
  var US_COUNTRIES = { US: 1, CA: 1 };
  var US_WORDS = [
    ['mum', 'mom'], ['mums', 'moms'], ['mummy', 'mommy'], ['mumma', 'momma'],
    ['aunty', 'aunt'], ['auntie', 'aunt'], ['nan', 'grandma'],
    ['neighbour', 'neighbor'], ['neighbours', 'neighbors'],
    ['favourite', 'favorite'], ['colour', 'color'], ['apologise', 'apologize']
  ];
  var _usRe = null;
  function usEnglish() {
    var loc = String(CFG.locale || 'en').toLowerCase();
    return loc.indexOf('en') === 0 && !!US_COUNTRIES[String(CFG.country || '').toUpperCase()];
  }
  function usify(str) {
    if (typeof str !== 'string' || !str || !usEnglish()) return str;
    if (!_usRe) {
      _usRe = new RegExp('\\b(' + US_WORDS.map(function (p) { return p[0]; }).join('|') + ')\\b', 'gi');
    }
    return str.replace(_usRe, function (m) {
      var lower = m.toLowerCase(), out = m;
      for (var i = 0; i < US_WORDS.length; i++) {
        if (US_WORDS[i][0] === lower) { out = US_WORDS[i][1]; break; }
      }
      // Preserve the original casing: "Mum" -> "Mom", "MUM" -> "MOM".
      if (m === m.toUpperCase() && m.length > 1) return out.toUpperCase();
      if (m[0] === m[0].toUpperCase()) return out.charAt(0).toUpperCase() + out.slice(1);
      return out;
    });
  }


  var NOTE_RECIPIENT_PICKS = (CFG.note_recipient_picks ||
    ['Friend', 'Mum', 'Sister', 'Daughter', 'Grandma', 'Aunty', 'Partner']).map(usify);
  var NOTE_OCCASION_PICKS = (CFG.note_occasion_picks ||
    ['Birthday', 'Thank you', 'Get well', 'Sympathy', 'Anniversary', 'Congratulations']).map(usify);
  /* Chips RENDER capped at four (Tyler 2026-08-02: seven buttons wrapped
     into an orphan second row — bad design). Four is not arbitrary: the top
     four recipients cover 98% of orders with a known recipient (friend 46,
     mum 28, sister 13, daughter 11 — customer_intelligence), and the free
     text field carries the tail. Occasions ordered so the four SHORT,
     high-coverage words render and the long ones (Anniversary,
     Congratulations) stay available to config/translations without ever
     wrapping the row. */
  var CHIP_RENDER_CAP = 4;
  /* Who it's FROM changes the whole message — "thinking of you" from one person
     reads nothing like it does from a household. People send as themselves, as
     a couple, as a family, or from a workplace, and the sign-off has to match. */
  /* Sender chips REMOVED (Tyler 2026-08-02): "Just me" / "Me and my partner"
     are category answers, but this field wants NAMES — the model signs off
     with whatever is given, and "Love, Just me" is exactly the ambiguity
     Tyler flagged. The placeholder already teaches the right shape (Sarah,
     Sarah & Tom, The Wilsons); free text with no chips collects real names. */

  /**
   * Stepped gift-note assistant.
   *
   * REPLACES the message field while it runs rather than appearing beneath it.
   * The earlier version stacked a panel under the textarea and could only be
   * dismissed by re-clicking the same link — an exit nobody would discover.
   * Now it takes over the panel and always offers an explicit Back and Cancel,
   * so there is never a state the shopper can't get out of.
   */
  /* Park the assist's declared facts on the CART as an attribute so they
     land on the order. No checkbox, no promise text (Tyler 2026-08-02:
     "note added + subscribed to our emails = enrolled — don't overcomplicate
     it"). Nothing here is an email or a send: consent is enforced entirely
     server-side — the dispatcher checks Klaviyo email-marketing subscription
     before firing anything (GIFT_NOTE_REMINDER_PLAN.md). Attribute, not line
     property, so it survives the note line being edited. */
  function captureReminderFacts(facts) {
    if (!facts || !(facts.occasion || '').trim()) return;
    var payload = {
      recipient: facts.recipient || '',
      nickname: facts.nickname || '',
      occasion: facts.occasion || '',
      captured_at: new Date().toISOString()
    };
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { __pp_occasion_reminder: JSON.stringify(payload) } })
    }).catch(function () {});
    track.emit('cart_giftnote_reminder', null, {
      recipient: facts.recipient || null,
      occasion: facts.occasion || null,
      clarity_tag: 'giftnote_reminder_captured'
    });
  }

  function openNoteAssist(ta, status, opts) {
    /* `.pp-cart__giftnote-pad` belonged to the old cart-level note block, which
       was removed when notes moved onto individual bouquets. The lookup kept
       returning null, so this bailed out immediately and the button did
       nothing. Match the per-line editor, keeping the old selector so the
       function stays usable if a cart-level note ever returns. */
    var pad = ta.closest('.pp-cart__note-edit, .pp-cart__giftnote-pad');
    if (!pad) return;
    if (pad.querySelector('.pp-cart__assist')) return;   // already running

    var polish = !!(ta.value || '').trim();
    var state = { step: 0, recipient: '', occasion: '', sender: '', detail: '',
                  nickname: '', entry: 'editor', suggestions: [] };
    /* ASK-FIRST flow (Tyler 2026-08-02, replaces the 5-step version the same
       week it shipped). Two chip taps and one button is the whole minimum
       path: who → occasion → one "finishing touches" screen where EVERYTHING
       is optional (from / goes-by / detail) → results. The old required
       'from' step was typing on the critical path; skipped now means the
       model writes no sign-off and she adds her own name while editing,
       which is what people do anyway. */
    var STEPS = polish ? ['results'] : ['who', 'occasion', 'finish', 'results'];

    /* Row-chip fast lane (Tyler 2026-08-02): she tapped "Mum" on the cart row,
       so "who's it for" is already answered — open on the occasion step with
       the chip carried in. Back still reaches step 1 to change the answer. */
    opts = opts || {};
    if (!polish && opts.recipient) {
      state.recipient = opts.recipient;
      state.entry = 'row_chip';
      state.step = 1;
    }

    /* Steps the shopper must answer before Next unlocks (Tyler 2026-08-02 —
       Next used to sail through empty fields). Only who + occasion: an empty
       answer there makes the model guess, and a guessed note reads generic,
       gets rejected, and the add-on dies right there. Everything on the
       finish screen is genuinely optional. */
    var REQUIRED = { who: 'recipient', occasion: 'occasion' };
    /* Rebound by draw() to the current step's Next button; field + chip
       handlers call it so the button unlocks the moment there's an answer. */
    var syncNext = function () {};

    var hidden = [];
    [].forEach.call(pad.children, function (n) { hidden.push([n, n.style.display]); n.style.display = 'none'; });

    var panel = el('div', 'pp-cart__assist');
    pad.appendChild(panel);

    function close() {
      panel.remove();
      hidden.forEach(function (pair) { pair[0].style.display = pair[1] || ''; });
      ta.focus();
    }

    function fieldStep(key, title, placeholder, picks, optional) {
      var wrap = el('div', null);
      wrap.appendChild(el('div', 'pp-cart__assist-q', title));
      var input = d.createElement('input');
      input.type = 'text';
      input.className = 'pp-cart__assist-input';
      input.placeholder = placeholder;
      input.value = state[key] || '';
      input.maxLength = 60;
      /* Chips mirror the field BOTH ways (Tyler 2026-08-02): a lit chip
         tapped again clears itself and the field, and editing the field
         re-lights whichever chip exactly matches what's typed — so
         backspacing a prefilled "Aunty" turns the Aunty chip off. The chip
         is a view of the field, never a separate fact. */
      var chips = null;
      function syncChips() {
        if (!chips) return;
        var cur = (state[key] || '').trim();
        [].forEach.call(chips.children, function (n) {
          n.classList.toggle('is-on', n.textContent === cur);
        });
      }
      input.addEventListener('input', function () {
        state[key] = input.value;
        syncChips();
        syncNext();
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); next(); }
      });
      wrap.appendChild(input);

      if (picks && picks.length) {
        chips = el('div', 'pp-cart__assist-chips');
        picks.forEach(function (v) {
          var c = el('button', 'pp-cart__chip', v);
          c.type = 'button';
          c.addEventListener('click', function () {
            var wasOn = (state[key] || '').trim() === v;
            state[key] = wasOn ? '' : v;
            input.value = state[key];
            syncChips();
            syncNext();
            /* The chip IS the Next button (Tyler 2026-08-02): answering by
               tap advances by itself after a beat — long enough to see the
               chip light, short enough to feel like cause and effect.
               Guarded on step identity so a rapid second tap can't double-
               advance, and toggling OFF never advances. */
            if (!wasOn) {
              var stepAtTap = state.step;
              setTimeout(function () {
                if (state.step === stepAtTap) next();
              }, 160);
            }
          });
          if ((state[key] || '').trim() === v) c.classList.add('is-on');
          chips.appendChild(c);
        });
        wrap.appendChild(chips);
      }
      if (optional) wrap.appendChild(el('div', 'pp-cart__assist-hint', S('assist_optional', 'Optional')));
      /* NO autofocus on chip steps (Tyler 2026-08-02): focusing the input
         pops the phone keyboard over half the drawer for a question a chip
         answers in one tap. The keyboard appears only when SHE taps the
         field. Chip-less steps still focus, where typing is the only path. */
      if (!picks || !picks.length) setTimeout(function () { input.focus(); }, 40);
      return wrap;
    }

    function next() {
      if (STEPS[state.step] === 'results') return;
      // Guards the Enter key too — the button alone being disabled isn't enough.
      var reqKey = REQUIRED[STEPS[state.step]];
      if (reqKey && !(state[reqKey] || '').trim()) return;
      state.step += 1;
      if (STEPS[state.step] === 'results') generate();
      else draw();
    }
    function back() {
      /* Ask-first entry owns its exit: leaving from step 0 cancels the whole
         note editor in ONE tap (opts.onCancel → render()), instead of
         stranding her on the blank textarea she never asked for. */
      if (state.step === 0) {
        if (opts.onCancel) opts.onCancel(); else close();
        return;
      }
      state.step -= 1;
      draw();
    }

    function generate() {
      draw(true);
      requestNoteSuggestions({
        mode: polish ? 'polish' : 'write',
        recipient: state.recipient,
        nickname: state.nickname,
        occasion: state.occasion,
        sender: state.sender,
        draft: polish ? (ta.value || '').slice(0, 400) : state.detail,
        locale: CFG.locale,
        /* The MARKET, not the language. Shopify renders US and AU both as
           `en`, so locale alone left the model free to write "Mum" onto a card
           posting to Ohio. */
        country: CFG.country || '',
        max_length: ta.maxLength
      }).then(function (s) {
        state.suggestions = s || [];
        draw();
        track.emit('cart_giftnote_start', null, {
          assist: true, mode: polish ? 'polish' : 'write',
          entry: state.entry,   // 'row_chip' vs 'editor' — measures the fast lane
          // Declared at purchase — these are exactly the signals the gift-note
          // intelligence work currently infers from finished messages.
          recipient: state.recipient || null,
          occasion: state.occasion || null,
          sender_type: state.sender || null,
          clarity_tag: 'giftnote_ai_used'
        });
      }).catch(function () { state.suggestions = null; draw(); });
    }

    function draw(loading) {
      panel.textContent = '';

      var head = el('div', 'pp-cart__assist-head');
      var backBtn = el('button', 'pp-cart__assist-back');
      backBtn.type = 'button';
      backBtn.setAttribute('aria-label', A11Y('back', 'Back'));
      backBtn.appendChild(icon(ICON.back));
      backBtn.appendChild(el('span', null, state.step === 0 ? (S('assist_cancel', 'Cancel')) : (S('assist_back', 'Back'))));
      backBtn.addEventListener('click', back);
      head.appendChild(backBtn);

      if (!polish) {
        head.appendChild(el('span', 'pp-cart__assist-step',
          Math.min(state.step + 1, STEPS.length) + ' / ' + STEPS.length));
      }
      var x = el('button', 'pp-cart__assist-close');
      x.type = 'button';
      x.setAttribute('aria-label', A11Y('close', 'Close'));
      x.appendChild(icon(ICON.close));
      // In ask-first, X means "abandon the note", not "show me the textarea".
      x.addEventListener('click', function () {
        if (opts.onCancel) opts.onCancel(); else close();
      });
      head.appendChild(x);
      panel.appendChild(head);

      var step = STEPS[state.step];

      if (step === 'who') {
        panel.appendChild(fieldStep('recipient', CFG.assist_who || "Who's it for?",
          S('assist_who_ph', 'e.g. my mum, my niece Ava, my neighbour'),
          NOTE_RECIPIENT_PICKS.slice(0, CHIP_RENDER_CAP)));
        /* Where the message physically goes. The classic editor said this
           above the textarea; ask-first skips that view, so the shopper
           would otherwise never learn what the $5 buys. Same translated
           copy the editor uses. */
        var whereTxt = (CFG.gift_note || {}).description;
        if (whereTxt) panel.appendChild(el('div', 'pp-cart__assist-hint', whereTxt));
      } else if (step === 'occasion') {
        /* Reaching this step means the recipient answer is behind us, so
           remember who the gift is for — the upsell rail addresses them by
           name. Declared by the shopper, kept in sessionStorage only: it never
           leaves the browser and never reaches Pippy. */
        try {
          if (state.recipient) {
            sessionStorage.setItem('pp_note_recipient', state.recipient);
          }
        } catch (e) { /* private mode */ }
        panel.appendChild(fieldStep('occasion', CFG.assist_occasion || "What's the occasion?",
          S('assist_occasion_ph', 'e.g. her 60th, new baby, passed her exams'),
          NOTE_OCCASION_PICKS.slice(0, CHIP_RENDER_CAP)));
      } else if (step === 'finish') {
        /* ONE screen for everything optional (Tyler 2026-08-02) — replaces
           the old required 'from' step and the separate 'detail' step. Three
           small fields, none gating, no autofocus (no keyboard until she
           asks for it). Skipping the lot still writes a good note; the model
           omits the sign-off when no sender is given. Nickname lives here
           ("Dear Nan" lands; "Dear grandmother" doesn't) — never blocks. */
        panel.appendChild(el('div', 'pp-cart__assist-q',
          S('assist_finish', 'A few finishing touches — all optional')));
        var FINISH = [
          ['sender', S('assist_from', 'Who’s it from?'),
            S('assist_from_ph', 'e.g. Sarah, or Sarah & Tom, or The Wilsons')],
          ['nickname', S('assist_nick', 'What do they go by?'),
            S('assist_nick_ph', 'e.g. Nan, Ava, Mumma')],
          ['detail', S('assist_detail', 'Anything you want to say?'),
            S('assist_detail_ph', 'e.g. sorry I can’t be there, thank you for everything')]
        ];
        FINISH.forEach(function (f) {
          var fw = el('div', 'pp-cart__assist-nick');
          fw.appendChild(el('div', 'pp-cart__assist-q pp-cart__assist-q--sub', f[1]));
          var inp = d.createElement('input');
          inp.type = 'text';
          inp.className = 'pp-cart__assist-input';
          inp.maxLength = f[0] === 'detail' ? 120 : 40;
          inp.placeholder = f[2];
          inp.value = state[f[0]] || '';
          inp.addEventListener('input', function () { state[f[0]] = inp.value; });
          inp.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); next(); }
          });
          fw.appendChild(inp);
          panel.appendChild(fw);
        });
      } else if (step === 'results') {
        if (loading) {
          panel.appendChild(el('div', 'pp-cart__assist-loading', S('assist_working', 'Writing a few options…')));
        } else if (!state.suggestions || !state.suggestions.length) {
          panel.appendChild(el('div', 'pp-cart__assist-err',
            CFG.assist_err || "Couldn't get suggestions just now — your own words are always best anyway."));
        } else {
          panel.appendChild(el('div', 'pp-cart__assist-q',
            S('assist_pick', 'Tap one to use it — you can still edit it')));
          var out = el('div', 'pp-cart__assist-out');
          state.suggestions.forEach(function (s) {
            /* Each suggestion is a CARD with its own action cue, not a
               paragraph. Three options rendered as tinted text on a tinted
               panel read as one block of prose — the only affordance was a
               hover border, and on mobile (where most of this traffic is)
               hover does not exist, so there was nothing at all saying these
               were three separate things you could pick.
               "People don't buy what they can't properly evaluate": if you
               can't tell two options apart you can't choose between them. */
            var opt = el('button', 'pp-cart__assist-opt');
            opt.type = 'button';
            opt.appendChild(el('span', 'pp-cart__assist-opt-text', s));
            opt.appendChild(el('span', 'pp-cart__assist-opt-cue',
              S('assist_use_cta', 'Use this one')));
            opt.addEventListener('click', function () {
              // ACCEPTED, not applied on their behalf — lands fully editable.
              // Model output can carry stray blank lines or trailing space.
              /* Stash the declared facts on the field; they're written to
                 the cart only when the note is actually SAVED (an accepted
                 suggestion the shopper then abandons enrols nobody). */
              if (!polish && (state.occasion || '').trim()) {
                ta._ppReminder = { recipient: state.recipient,
                                   nickname: state.nickname,
                                   occasion: state.occasion };
              }
              ta.value = normalizeNote(s, ta.maxLength);
              close();
              ta.dispatchEvent(new Event('input'));
            });
            out.appendChild(opt);
          });
          panel.appendChild(out);
        }
      }

      var foot = el('div', 'pp-cart__assist-foot');
      if (step === 'results') {
        if (!loading) {
          /* SECONDARY on this step, deliberately. As the solid-green primary it
             was the loudest thing on the results screen, which put maximum
             visual weight on "discard these and start over" while the actual
             job — pick one — carried none. The suggestions are the primary
             action here; Try again is the way out if none of them land. */
          var again = el('button', 'pp-cart__assist-skip', S('assist_again_cta', 'Try again'));
          again.type = 'button';
          again.addEventListener('click', generate);
          foot.appendChild(again);
        }
      } else {
        /* A real button BESIDE Next, not a shy underline below it (Tyler
           2026-08-02): writing your own message is a first-class path, so it
           gets first-class furniture — secondary style, left of the primary.
           Ask-first entry only: arriving via the "Help me write" button
           means she chose the wizard a moment ago; Cancel covers the exit. */
        if (step === 'who' && opts.onCancel) {
          var selfWrite = el('button', 'pp-cart__assist-skip',
            S('assist_self_cta', 'I’ll write it myself'));
          selfWrite.type = 'button';
          selfWrite.addEventListener('click', close);
          foot.appendChild(selfWrite);
        }
        var nextBtn = el('button', 'pp-cart__assist-go',
          STEPS[state.step + 1] === 'results'
            ? (S('assist_go_cta', 'Write my note'))
            : (S('assist_next', 'Next')));
        nextBtn.type = 'button';
        nextBtn.addEventListener('click', next);
        var reqKey = REQUIRED[step];
        syncNext = function () {
          if (reqKey) nextBtn.disabled = !(state[reqKey] || '').trim();
        };
        syncNext();
        foot.appendChild(nextBtn);
      }
      panel.appendChild(foot);
    }

    if (polish) generate(); else draw();
  }

  /**
   * One-tap spelling correction, applied straight to the field.
   *
   * Built for the shoppers who need it most: a lot of PetalPal's customers are
   * elderly and type with difficulty. They don't want a panel of rewritten
   * alternatives to compare — they want their own message with the typos gone.
   * So this applies the correction directly and offers Undo, rather than making
   * them evaluate options.
   *
   * The server prompt is deliberately the strictest we run: spelling and
   * punctuation only, never rephrasing, never translating, and names are left
   * alone on the assumption they're spelled as intended.
   */
  function runSpellcheck(ta, status, btn, label, syncCounter) {
    var original = ta.value;
    var before = label.textContent;
    btn.disabled = true;
    label.textContent = S('note_ai_checking', 'Checking…');
    status.textContent = '';
    status.className = 'pp-cart__giftnote-status';

    requestNoteSuggestions({
      mode: 'spellcheck',
      draft: original,
      locale: CFG.locale,
      country: CFG.country || '',
      max_length: ta.maxLength
    }).then(function (list) {
      btn.disabled = false;
      label.textContent = before;
      var fixed = normalizeNote((list && list[0]) || '', ta.maxLength);
      if (!fixed) {
        status.textContent = CFG.note_ai_err || "Couldn't check just now";
        status.className = 'pp-cart__giftnote-status is-error';
        return;
      }
      if (fixed === normalizeNote(original, ta.maxLength)) {
        // Say so explicitly — silence would read as "it didn't work".
        status.textContent = S('note_ai_clean', 'No changes needed');
        status.className = 'pp-cart__giftnote-status is-saved';
        return;
      }
      ta.value = fixed;
      syncCounter();
      status.textContent = '';
      status.className = 'pp-cart__giftnote-status';

      // Undo must be available: an unwanted "correction" to a name or a
      // deliberate turn of phrase has to be reversible in one click.
      var undo = el('button', 'pp-cart__note-link', S('note_ai_undo', 'Undo'));
      undo.type = 'button';
      undo.addEventListener('click', function () {
        ta.value = original;
        syncCounter();
        undo.remove();
      });
      status.appendChild(el('span', null, (S('note_ai_fixed', 'Spelling fixed')) + ' '));
      status.className = 'pp-cart__giftnote-status is-saved';
      status.appendChild(undo);
    }).catch(function (err) {
      btn.disabled = false;
      label.textContent = before;
      // Distinguish "we're being rate-limited" from "it broke", and leave the
      // real error in the console — the previous version swallowed it, which
      // made this failure impossible to diagnose from a screenshot.
      var rateLimited = err && /_(429|503)$/.test(String(err.message || ''));
      status.textContent = rateLimited
        ? (S('note_ai_busy', 'Busy right now — try again in a moment'))
        : (CFG.note_ai_err || "Couldn't check just now");
      status.className = 'pp-cart__giftnote-status is-error';
      try { console.warn('[pp-cart] spellcheck failed:', err); } catch (e) {}
    });
  }

  function requestNoteSuggestions(payload) {
    var base = (CFG.assist_endpoint || 'https://pippy.co/cart/giftnote-suggest');
    return fetch(base, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },   // simple request: no preflight
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('assist_http_' + r.status);
      return r.json();
    }).then(function (j) { return (j && j.suggestions) || []; });
  }

  /* A collection's title is its MERCHANDISING name, which is not always the
     shopper's word for the thing.
     "Signature Bouquets" is an internal distinction: nothing a customer ever
     sees calls a bouquet "signature" — not the product titles, not the
     descriptions, not the size options. So "Add 2 more signature bouquets for
     15% off" asked them to act on a word they had never met, and the ask
     failed on vocabulary before it got to the offer.
     The only size language on the storefront is the "Mini" prefix on the small
     version's title, so the big one is named by what it isn't. Not "large" —
     that implies a small/medium/large range this catalogue doesn't have.
     Keyed by handle, so the collection keeps its own title for merchandising
     and this override stays a display concern. */
  var TIER_NOUNS = {
    'signature-bouquets': ['full-size bouquet', 'full-size bouquets']
  };

  /**
   * The human noun for what a tier applies to — "pop-up bouquet", not "item".
   *
   * "Add 1 more pop-up bouquet to save 15%" tells the shopper exactly what to
   * add; "Add 1 more item" leaves them to guess, and guessing wrong means
   * adding something that doesn't qualify. Derived from the discount's own
   * scoped collection title so it stays correct as the catalogue grows —
   * a candles-scoped tier will say "candle" with no code change — except where
   * TIER_NOUNS says the title isn't customer-legible.
   */
  function tierNoun(tier, plural) {
    var titles = REWARDS.collection_titles || {};
    var nouns = CFG.tier_nouns || TIER_NOUNS;
    var handles = (tier && tier.scope && tier.scope.collections) || [];
    var title = null;
    for (var i = 0; i < handles.length; i++) {
      if (nouns[handles[i]]) return nouns[handles[i]][plural ? 1 : 0];
      if (titles[handles[i]]) { title = titles[handles[i]]; break; }
    }
    if (!title) {
      var fb = CFG.tier_noun_fallback || ['item', 'items'];
      return fb[plural ? 1 : 0];
    }
    var word = String(title).toLowerCase().trim();
    // Collection titles are plural ("Pop-Up Bouquets"); singularise simply.
    var singular = word.replace(/ies$/, 'y').replace(/s$/, '');
    return plural ? word : singular;
  }

  function tierIcon(tier) {
    // Free delivery is a delivery promise, not a discount — it gets the truck.
    // Otherwise: a tier tied to a product group gets the product's own mark;
    // a store-wide tier keeps the generic discount tag.
    if (tier && tier.key === 'ship') return ICON.truck;
    var scoped = tier && tier.scope && (tier.scope.collections || []).length;
    return scoped ? ICON.flower : ICON.tag;
  }
  function icon(pathD, cls) {
    var s = d.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('fill', 'none');
    s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '1.8');
    s.setAttribute('stroke-linecap', 'round');
    s.setAttribute('stroke-linejoin', 'round');
    if (cls) s.setAttribute('class', cls);
    var p = d.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', pathD);
    s.appendChild(p);
    return s;
  }

  // ──────────────────────────── helpers ────────────────────────────
  /**
   * Format money WITH the ISO currency code — "$22.95 AUD", not "$22.95".
   * This is a deliberate trust decision, not decoration: PetalPal's buyers skew
   * elderly and are wary of being charged in an unexpected currency, and the
   * same "$" serves USD, AUD, CAD, NZD, SGD and HKD across our markets. Showing
   * the code removes that ambiguity at every price on screen.
   */
  function money(minor) {
    var cur = CFG.currency || 'USD';
    var v = Number(minor || 0) / 100;
    var s;
    try {
      /* narrowSymbol: Intl's default AUD symbol is "A$", which with the code
         suffix printed "A$50.59 AUD" — the same disambiguation twice
         (Tyler 2026-08-02). The trailing code does that job; the symbol
         stays bare. */
      s = new Intl.NumberFormat(CFG.locale || 'en', {
        style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol'
      }).format(v);
    } catch (e) {
      try {
        s = new Intl.NumberFormat(CFG.locale || 'en', {
          style: 'currency', currency: cur
        }).format(v);
      } catch (e2) {
        s = cur + ' ' + v.toFixed(2);
      }
    }
    // Some locales already render the code (e.g. "USD 22.95") — don't double it.
    if (CFG.show_currency_code !== false && s.indexOf(cur) === -1) s += ' ' + cur;
    return s;
  }

  /**
   * Render "Add {amount} more for {reward}" into `parent`, substituting parts.
   *
   * A TEMPLATE, not concatenation, because concatenation hard-codes English
   * word order. "Add " + amount + " more for " + reward cannot become German,
   * where the pieces land in a different order — the placeholders have to be
   * free to move, and only the template knows where they go.
   *
   * Unknown placeholders are left as literal text rather than blanked: a
   * translation that invents {price} should look obviously wrong in testing,
   * not silently drop a number out of a sentence about money.
   *
   * Every piece goes in via el()/textContent, never innerHTML — these strings
   * arrive from a metafield, and the whole drawer exists because the last cart
   * app rendered merchant-editable content as markup.
   */
  /**
   * A screen-reader label in the shopper's language.
   *
   * Kept as one lookup rather than ten config keys because these are the
   * strings a translation pass forgets: nobody sees them, so nothing looks
   * wrong when they stay English. Grouping them under `a11y` means a missing
   * translation is visible as a missing OBJECT during review, not as ten
   * separately-plausible defaults.
   */
  /**
   * One string, resolved through the three layers that own it.
   *
   *   CFG[key]         a merchant override typed in Pippy — always wins
   *   CFG.strings[key] this locale's translation
   *   fallback         the English source, which lives HERE and nowhere else
   *
   * Resolved in JS rather than enumerated in Liquid on purpose. Threading each
   * key through the snippet by hand left FORTY-FIVE of them unemitted — the
   * whole gift-note assistant, the note controls, and "Saved"/"Delivery"/
   * "Free" in the footer — so they were English on a German page and nothing
   * said so. A per-key list is a list someone has to remember to extend; this
   * makes a new string translatable the moment it is written.
   */
  function S(key, fallback) {
    var v = CFG[key];
    if (typeof v === 'string' && v) return usify(v);
    var t = CFG.strings && CFG.strings[key];
    return usify((typeof t === 'string' && t) ? t : fallback);
  }

  function A11Y(key, fallback) {
    var t = CFG.a11y && CFG.a11y[key];
    return (typeof t === 'string' && t) ? t : fallback;
  }

  /* ONE ROW, ALWAYS (Tyler 2026-08-03 v14): "minimise the length of the
     '3 of 6 — add 3 more full-size bouquets for 25% off' text on mobile so it
     doesn't get compacted and turn into two rows".

     A measured ladder, not a shorter string. The cart is translated into
     eight languages and German runs materially longer than English, so any
     copy hand-tuned to fit a 390px phone in English would still wrap
     somewhere — the only way to guarantee one row in every locale is to
     measure the rendered line and step down until it fits. Same technique as
     fitPopLine(), which solved the identical problem on the upsell cards.

     Variants shed the INFERABLE parts first: the item noun ("full-size
     bouquets", by far the longest token, and already implied by the bar and
     the cart) goes before the endowed count ("3 of 6"), which is the whole
     motivational point of the line — Nunes & Drèze only works if the banked
     progress is stated. The reward itself is never dropped; it's the offer.

     Returns having rendered SOME variant either way; CSS nowrap + ellipsis is
     the backstop for the pathological case. Widths read 0 while the drawer is
     [hidden] — the documented trap — so an unmeasured call bails and leaves
     the refit to _rewardRefit on open. */
  function fitTemplateLine(node, variants) {
    for (var i = 0; i < variants.length; i++) {
      node.textContent = '';
      fillTemplate(node, variants[i].tpl, variants[i].parts);
      if (node.clientWidth <= 0) return;                 // unmeasured
      if (node.scrollWidth <= node.clientWidth + 1) return;   // fits
    }
  }

  function fillTemplate(parent, tpl, parts) {
    String(tpl == null ? '' : tpl).split(/(\{[a-z_]+\})/i).forEach(function (chunk) {
      if (!chunk) return;
      var m = /^\{([a-z_]+)\}$/i.exec(chunk);
      var part = m && parts[m[1]];
      if (!part) { parent.appendChild(el('span', null, chunk)); return; }
      parent.appendChild(el(part.strong ? 'strong' : 'span', null, part.text));
    });
    return parent;
  }

  function el(tag, cls, text) {
    var n = d.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;   // textContent, never innerHTML
    return n;
  }

  /**
   * POST to Shopify's cart API, retrying transient failures.
   *
   * Shopify (and Cloudflare in front of it) answer rate limiting and bot checks
   * with 429/503 and an HTML body — a blind .json() throws a confusing
   * "Unexpected token '<'". Those are transient and worth retrying with
   * backoff: without it a shopper's quantity click or gift-note save simply
   * does nothing, which is far worse than a short pause.
   *
   * 4xx other than 429 are real rejections (e.g. sold out) — fail immediately.
   */
  function api(path, body, attempt) {
    attempt = attempt || 0;
    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      if (r.ok) return r.json();
      var transient = r.status === 429 || r.status === 503 || r.status >= 500;
      if (transient && attempt < 5) {
        var wait = 400 * Math.pow(2, attempt);   // 400ms .. 6.4s
        return new Promise(function (res) { setTimeout(res, wait); })
          .then(function () { return api(path, body, attempt + 1); });
      }
      var err = new Error('cart_http_' + r.status);
      err.status = r.status;
      throw err;
    });
  }

  /**
   * Is this response actually a CART, or just a line item?
   *
   * THIS IS THE BUG THAT EMPTIED THE CART. change.js and update.js return the
   * full cart, but ADD.JS RETURNS THE ADDED LINE ITEM(S) — a shape with
   * `items` but no `token` and no `total_price`. Assigning that to
   * our `cart` variable left the drawer rendering from a line item, so the
   * product vanished from view. The server cart was correct the whole time,
   * which is why everything reappeared on the next add.
   *
   * Every response is funnelled through here now; anything that isn't a real
   * cart triggers a proper read instead of being trusted.
   */
  function asCart(c) {
    return (c && typeof c === 'object' && c.token && c.items && ('total_price' in c))
      ? expAdjustCart(c) : null;   // idempotent — a flag guards double-apply
  }

  /**
   * Take the cart from the SERVER after a mutation — never from the mutation's
   * own response.
   *
   * A MUTATION RESPONSE IS NOT AUTHORITATIVE ABOUT MONEY. Measured against the
   * live storefront 2026-07-30, /cart/change.js returns a total_price that
   * INCLUDES SHIPPING once the cart drops back under the free-delivery
   * threshold:
   *
   *     set qty 4 -> change.js 4760 | cart.js 4760   agree
   *     set qty 3 -> change.js 5000 | cart.js 3570   +1430 = the 450g rate
   *     set qty 2 -> change.js 3958 | cart.js 2798   +1160 = the 300g rate
   *
   * Adopting that number is the whole of the bug Tyler reported. A $35.70 cart
   * read as $50.00, which cleared the $45 threshold, so the drawer printed
   * "Free delivery" — and then valued the delivery it had just given away as a
   * $14.30 saving, while `saved` went to zero because original_total_price
   * (4197) was now BELOW the inflated total. Four wrong figures, one bad field.
   *
   * The response's line contents and quantities are fine and still drive the
   * optimistic repaint; the totals come from /cart.js, always. That costs one
   * round trip per mutation, which the previous comment here ("change.js
   * returns the updated cart, so no follow-up getCart is needed") was trying to
   * save. It isn't a saving if the number is wrong.
   */
  /* ── offer-experiment coherence (t1+) ────────────────────────────────
     While an offer-class bundle experiment is live (bundle_config.experiment
     carries an `offer` client contract), checkout prices come from a per-arm
     DISCOUNT CODE the widget auto-applies — which /cart.js cannot see. Left
     alone, the drawer would charge-display a mini the widget promised free,
     and full-price signatures it promised 10% off. This block restores the
     automatic-discount era's coherence:
       - the arm's rung is injected into REWARDS.quantity_tiers (arm a), or
         the dormant free-gift machinery is armed at the experiment's own
         threshold (arm b) — arm-gated, NEVER via the global
         free_gift.enabled flag;
       - every server cart passes through expAdjustCart() (getCart + asCart,
         the two choke points), which prices the pending code into the line
         items the way Shopify will (per-line Math.round — the widget's
         lineDisc convention), so struck prices, totals, the savings line
         and every threshold check inherit it with no further code.
     The arm comes from the widget's sticky assignment (localStorage), with
     the `_pp_exp` cart attribute as fallback; the offer's market scope
     gates everything. A preview payload is honoured only under the
     widget's persisted dev mark AND an already-forced arm — a shopper can
     never wander into a rehearsal. */
  var EXPX = null;
  function expResolve(fromCart) {
    if (EXPX) return;
    try {
      var e = (CFG.experiment && CFG.experiment.id) ? CFG.experiment : null;
      var pv = CFG.experiment_preview;
      var xdev = false;
      try { xdev = localStorage.getItem('ppb_dev') === '1'; } catch (e1) {}
      if (xdev && pv && pv.id && pv.offer && (!e || !e.offer)) {
        var pvArm = null;
        try { pvArm = localStorage.getItem('ppb_exp_' + pv.id); } catch (e2) {}
        if (pvArm && (pv.arms || []).indexOf(pvArm) >= 0) e = pv;
      }
      if (!e || !e.offer) return;
      var o = e.offer;
      if (o.markets && o.markets.length
          && o.markets.indexOf(CFG.country) < 0) return;
      var arm = null;
      try { arm = localStorage.getItem('ppb_exp_' + e.id); } catch (e3) {}
      if ((e.arms || []).indexOf(arm) < 0) arm = null;
      if (!arm && fromCart && fromCart.attributes) {
        var tag = String(fromCart.attributes._pp_exp || '').split('|');
        if (tag[0] === e.id && (e.arms || []).indexOf(tag[1]) >= 0) arm = tag[1];
      }
      if (!arm) return;
      EXPX = { id: e.id, arm: arm, offer: o, def: (o[arm] || {}) };
      applyExpOffer();
    } catch (e4) {}
  }
  function applyExpOffer() {
    if (!EXPX) return;
    var def = EXPX.def || {};
    var rung = def.rung;
    if (rung) {
      /* Replace any metafield rung at the same count (the overlap window
         while cart_rewards still carries the removed automatic), then
         inject the arm's — a 0% gift rung injects nothing, it only
         clears the stale percent so the rail can't promise a discount
         this arm's checkout won't apply. */
      var xq = Number(rung.q);
      REWARDS.quantity_tiers = (REWARDS.quantity_tiers || []).filter(function (t) {
        return Number(t.min_qty) !== xq;
      });
      if (Number(rung.pct) > 0) {
        REWARDS.quantity_tiers.push({
          min_qty: xq, percent: Number(rung.pct),
          scope: { collections: [EXPX.offer.scope_collection || 'signature-bouquets'] }
        });
        REWARDS.quantity_tiers.sort(function (a, b) {
          return (a.min_qty || 0) - (b.min_qty || 0);
        });
      }
    }
    if (def.gift) {
      var fg = CFG.free_gift = CFG.free_gift || {};
      fg.enabled = true;   // arm-gated: EXPX only exists for an assigned, in-market shopper
      fg.min_qty = Number(def.gift.min_qty) || Number((rung || {}).q) || fg.min_qty;
      fg.max_below_qty = Number(def.gift.max_below_qty) || null;
      /* pool + earn_products stay as Liquid emitted them — the same
         collections the BXGY code references, so they cannot drift. */
    }
    /* Belt-and-braces re-assert of the arm's code cookie: the drawer runs
       on every page, the widget only on PDPs. Fire-and-forget. */
    try {
      var xcode = EXPX.offer.codes && EXPX.offer.codes[EXPX.arm];
      if (xcode) {
        fetch('/discount/' + encodeURIComponent(xcode),
          { credentials: 'same-origin' }).catch(function () {});
      }
    } catch (e5) {}
  }
  function expAdjustCart(c) {
    try {
      if (!c || !c.items) return c;
      expResolve(c);
      if (!EXPX || c.__pp_exp_adjusted) return c;
      c.__pp_exp_adjusted = true;
      var def = EXPX.def || {};
      var gprop = (CFG.free_gift || {}).prop || '_free_gift';
      var rung = def.rung;
      if (rung && Number(rung.pct) > 0) {
        var pool = ((REWARDS.collection_products || {})[EXPX.offer.scope_collection])
                || (CFG.free_gift || {}).earn_products || [];
        var allowed = {};
        pool.forEach(function (pid) { allowed[String(pid)] = true; });
        var units = 0;
        c.items.forEach(function (i) {
          if (i.properties && i.properties[gprop]) return;
          if (allowed[String(i.product_id)]) units += i.quantity;
        });
        /* DON'T PROMISE A CODE SHOPIFY HAS ALREADY REFUSED.
           The simulation exists for the window before the code lands. But
           this store's discounts do not stack, so once ANY line already
           carries a real discount the code will not be applied on top — and
           simulating it there promises money checkout never gives.

           Measured 2026-08-26, AU arm A: 3 bouquets alone -> the code applied
           (server -$6.27). Add 4 minis (which pull their own 4+ 15%, plus a
           BXGY free mini) -> the signature lines stayed at full price across
           two server reads 6s apart, the code never applied, yet the drawer
           still advertised -$26.51 against a real -$20.22.

           So: if Shopify has already discounted something, show what Shopify
           actually did and simulate nothing. In a clean cart (nothing
           discounted yet) the simulation still does its job. Combined with
           the Math.min below, the drawer can now neither under- nor
           over-state the saving. */
        var alreadyDiscounted = false;
        c.items.forEach(function (i) {
          if ((i.total_discount || 0) > 0
              || ((i.line_level_discount_allocations || []).length > 0)) {
            alreadyDiscounted = true;
          }
        });
        if (units >= Number(rung.q) && !alreadyDiscounted) {
          var dsc = Number(rung.pct) / 100;
          c.items.forEach(function (i) {
            if (i.properties && i.properties[gprop]) return;
            if (!allowed[String(i.product_id)]) return;
            var op = i.original_price || i.final_price || 0;
            var ol = i.original_line_price || i.final_line_price || 0;
            /* NEVER PRICE ABOVE WHAT SHOPIFY HAS ALREADY APPLIED.
               This simulates the arm's PENDING code, and it used to write the
               simulated price straight over the real one. Once a bigger
               automatic had already applied, that overwrote a genuine
               discount with a smaller imaginary one: measured 2026-08-26 on
               AU arm A with 6 signature bouquets, the drawer showed −$12.57
               (the arm's 10%) while the cart really held −$25.14 (the 6+ 20%
               tier) — the shopper was quoted half the saving they were
               actually getting, and the receipt's rate pill correctly refused
               to name a rate that disagreed with the amount beside it.
               A pending code can only ever IMPROVE on the cart, so take the
               better of the two per line and per unit. */
            var realUnit = (i.final_price != null) ? i.final_price : op;
            var realLine = (i.final_line_price != null) ? i.final_line_price : ol;
            i.original_price = op;
            i.original_line_price = ol;
            i.final_price = Math.min(realUnit, op - Math.round(op * dsc));
            i.final_line_price = Math.min(realLine, ol - Math.round(ol * dsc));
          });
        }
      }
      if (def.gift) {
        var g = CFG.free_gift || {};
        var need = Number(g.min_qty || 0), qual = 0;
        var epool = {};
        (g.earn_products || []).forEach(function (pid) { epool[String(pid)] = true; });
        c.items.forEach(function (i) {
          if (i.properties && i.properties[gprop]) return;
          if (epool[String(i.product_id)]) qual += i.quantity;
        });
        if (need && qual >= need
            && !(g.max_below_qty && qual >= g.max_below_qty)) {
          c.items.forEach(function (i) {
            if (!(i.properties && i.properties[gprop])) return;
            /* The BXGY zeroes exactly ONE unit (usesPerOrderLimit 1). A
               transient qty-2 gift line (two tabs racing syncFreeGift —
               its own >1 guard normalises next pass) must not display
               both units free: zero one unit's worth only. */
            var gq = Math.max(1, i.quantity || 1);
            i.original_price = i.original_price || i.final_price || 0;
            i.original_line_price = i.original_line_price || i.final_line_price || 0;
            i.final_price = gq === 1 ? 0 : i.original_price;
            i.final_line_price = Math.max(0,
              i.original_line_price - i.original_price);
          });
        }
      }
      var xorig = 0, xfin = 0;
      c.items.forEach(function (i) {
        xorig += i.original_line_price || i.final_line_price || 0;
        xfin += i.final_line_price || 0;
      });
      c.original_total_price = xorig;
      c.total_price = xfin;
    } catch (e6) {}
    return c;
  }
  expResolve(null);

  function adoptCart(c) {
    return getCart().then(function (fresh) { cart = fresh; return cart; })
      .catch(function (err) {
        /* Fall back to the mutation response rather than leaving `cart` stale:
           a slightly wrong total beats a drawer showing the pre-click cart. The
           next successful read corrects it. */
        var real = asCart(c);
        if (real) { cart = real; return cart; }
        throw err;
      });
  }

  /* Same retry policy as api(). This was previously an unprotected fetch, so a
     single transient 429 anywhere in a chain rejected the whole operation even
     though the mutation itself may have succeeded. */
  /* CONCURRENT reads share one request. A single quantity tap that crosses
     the gift threshold was measured firing NINE cart requests — change.js,
     add.js, update.js, two shipping-rate quotes and THREE /cart.js reads —
     and a few taps in a row is enough for Shopify/Cloudflare to answer 429,
     which surfaced to the shopper as "Too many requests" (Tyler 2026-08-26).

     Only overlapping reads are shared, never sequential ones: a read issued
     after a mutation has to see the new server state, so the promise is
     released the moment it settles. Two callers reading at the same instant
     would receive the same bytes anyway. */
  var _getCartInflight = null;
  function getCart(attempt) {
    if (!attempt && _getCartInflight) return _getCartInflight;
    var pr = _getCartRaw(attempt);
    if (!attempt) {
      _getCartInflight = pr;
      var clear = function () { _getCartInflight = null; };
      pr.then(clear, clear);
    }
    return pr;
  }
  function _getCartRaw(attempt) {
    attempt = attempt || 0;
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) {
        if (r.ok) return r.json().then(expAdjustCart);
        var transient = r.status === 429 || r.status === 503 || r.status >= 500;
        if (transient && attempt < 5) {
          var wait = 400 * Math.pow(2, attempt);
          return new Promise(function (res) { setTimeout(res, wait); })
            .then(function () { return _getCartRaw(attempt + 1); });
        }
        var err = new Error('cart_http_' + r.status);
        err.status = r.status;
        throw err;
      });
  }

  /* Items excluded from "real product" counts: the gift note is a mechanism,
     not something the shopper chose, so it must not silently push them over a
     quantity tier. Counting it would make the cart promise a discount the
     checkout may not apply. */
  /* The auto-added Buy-X-Get-Y gift, identified by the property the drawer
     stamps on it when it adds it (see _syncFreeGiftPass). */
  function isFreeGiftLine(item) {
    if (!item) return false;
    var key = (CFG.free_gift || {}).prop || '_free_gift';
    if (item.properties && item.properties[key]) return true;
    /* renderLines works on GROUPED items, which carry `lines` rather than
       `properties` — checking only the latter meant the gift group never
       identified itself and kept its stepper. */
    if (item.lines) {
      for (var i = 0; i < item.lines.length; i++) {
        var pr = item.lines[i] && item.lines[i].properties;
        if (pr && pr[key]) return true;
      }
    }
    return false;
  }

  function isMechanismLine(item) {
    var g = CFG.gift_note || {};
    return g.variant_id && String(item.variant_id) === String(g.variant_id);
  }

  function realItemCount() {
    if (!cart) return 0;
    return cart.items.reduce(function (n, i) {
      return n + (isMechanismLine(i) ? 0 : i.quantity);
    }, 0);
  }

  /**
   * How many items in the cart actually COUNT toward a given quantity tier.
   *
   * PetalPal's "buy 3 save 15%" / "buy 6 save 25%" are scoped to the Pop-Up
   * Bouquets collection. Counting every line would tell a shopper holding three
   * candles that they're about to unlock 15% — a discount checkout would then
   * refuse to apply. The cart must never promise something Shopify won't honour,
   * so eligibility is resolved from the discount's own scope.
   *
   * Unscoped ("all items") tiers count everything except the gift-note
   * mechanism line.
   */
  function eligibleItemCount(tier) {
    if (!cart) return 0;
    var scope = tier && tier.scope;
    if (!scope || scope.all) return realItemCount();

    var allowed = {};
    (scope.collections || []).forEach(function (h) {
      ((REWARDS.collection_products || {})[h] || []).forEach(function (pid) {
        allowed[String(pid)] = true;
      });
    });
    // Product-scoped discounts arrive as handles; Liquid resolves collections to
    // ids, so handle-scoped products are matched separately below.
    var allowedHandles = {};
    (scope.products || []).forEach(function (h) { allowedHandles[h] = true; });

    return cart.items.reduce(function (n, i) {
      if (isMechanismLine(i)) return n;
      var byId = allowed[String(i.product_id)];
      var byHandle = i.handle && allowedHandles[i.handle];
      return n + (byId || byHandle ? i.quantity : 0);
    }, 0);
  }

  // ─────────────────────── delivery estimate ──────────────────────
  /**
   * Resolve the actual delivery cost for the current cart from the synced
   * Shopify rate bands — no address required.
   *
   * Why this matters: shipping cost first appearing at checkout is one of the
   * largest single causes of abandonment. Showing it in the cart also sharpens
   * our own funnel data — an abandon after seeing the real cost is a different
   * signal from an abandon before it, and the pixel can now tell them apart.
   *
   * Bands are weight-conditioned (Shopify reports cart.total_weight in grams).
   * Validated against Shopify's own /cart/shipping_rates.json: a 150g US cart
   * resolves to the 100-150g band at $8.90, and Shopify returns $8.90.
   *
   * Returns { free:true } | { minor:<amount> } | null (unknown -> caller must
   * fall back to "calculated at checkout" rather than invent a figure).
   */
  /* ── live rate lookup ────────────────────────────────────────────────
   * Shopify's own /cart/shipping_rates.json returns rates in the shopper's
   * PRESENTMENT currency for any destination — verified: browsing the US store
   * (USD), quoting to AU and DE both came back in USD. That makes it the only
   * source that's correct in every market, which the synced Admin-API bands
   * are not (those are stored in the shop's base currency and Shopify converts
   * at presentment, so they could only ever be quoted to USD shoppers).
   *
   * The endpoint needs an address (422 without a zip), but PetalPal's rates are
   * country-zone + weight based, so ANY valid address in the shopper's own
   * country yields exactly the rate they'll be charged. We therefore send a
   * representative address for their country — no shopper input required.
   *
   * Async and cached per (country, weight, subtotal). The synced bands remain
   * the instant first paint and the offline fallback; this refines it.
   */
  var RATE_PROBE = {
    US: { province: 'California', zip: '90210' },
    AU: { province: 'New South Wales', zip: '2000' },
    CA: { province: 'Ontario', zip: 'M5H 2N2' },
    GB: { zip: 'SW1A 1AA' },
    IE: { province: 'Dublin', zip: 'D02 X285' },
    DE: { zip: '10115' },
    FR: { zip: '75001' },
    NL: { zip: '1011 AB' },   // 4 digits alone are rejected: NL wants '1011 AB'
    BE: { zip: '1000' },
    AT: { zip: '1010' },
    ES: { province: 'Madrid', zip: '28001' },
    IT: { province: 'Roma', zip: '00118' },
    NZ: { zip: '1010' },
    SE: { zip: '111 29' },
    DK: { zip: '1050' },
    NO: { zip: '0150' },
    FI: { zip: '00100' },
    CH: { zip: '8001' },
    SG: { zip: '018956' },
    HK: { province: 'Kowloon' },
    JP: { province: 'Tokyo', zip: '100-0001' },
    MY: { province: 'Kuala Lumpur', zip: '50050' },
    ZA: { province: 'Gauteng', zip: '2000' },
    /* The twelve below were missing, and their absence was silent. Without a
       probe fetchLiveRate() returns null, the code falls back to the synced
       Admin-API bands, and those are denominated in the shop's base currency —
       so they're filtered out everywhere except the US and Bahrain. Net effect:
       these markets showed no delivery cost at all, which is the one thing this
       feature was built to avoid.
       "No postal system, so send country only" was wrong for AE and HK: probed
       against the live endpoint 2026-07-30, Shopify REQUIRES a province for
       AE ("Select an emirate"), HK ("Select a region"), KR, TH, MY, IE, ES and
       PT, and rejects a bare 4-digit NL postcode. Only BH and QA genuinely take
       a country on its own. Every one of the 34 entries below is now confirmed
       to return a real rate — see PROBE_COUNTRY for why the parameter changed. */
    AE: { province: 'Dubai' },
    BH: {},
    QA: {},
    CY: { zip: '1010' },
    IL: { zip: '6100001' },
    IS: { zip: '101' },
    KR: { province: 'Seoul', zip: '04524' },
    PL: { zip: '00-001' },
    PT: { province: 'Lisboa', zip: '1000-001' },
    SA: { zip: '11564' },
    SI: { zip: '1000' },
    TH: { province: 'Bangkok', zip: '10200' }
  };
  /* Shopify's /cart/shipping_rates.json wants shipping_address[country] with the
     country NAME. `shipping_address[country_code]` is rejected outright —
     HTTP 422 {"country":["Select a country/region"]}, measured against the live
     storefront 2026-07-30 — which means this probe had NEVER ONCE SUCCEEDED.
     Every call failed, .catch() cached null, and deliveryEstimate() fell through
     to the synced Admin-API bands. Those hold paid rates in the shop's BASE
     currency, so the currency guard in paidBandMinor() filtered them out
     everywhere except USD presentment: the result was "Calculated at checkout"
     in every non-US market, the one outcome the live probe was built to prevent.
     Silent because a rejected quote is indistinguishable from a cart with no
     rates. */
  var PROBE_COUNTRY = {
    US: 'United States', AU: 'Australia', CA: 'Canada', GB: 'United Kingdom',
    IE: 'Ireland', DE: 'Germany', FR: 'France', NL: 'Netherlands',
    BE: 'Belgium', AT: 'Austria', ES: 'Spain', IT: 'Italy', NZ: 'New Zealand',
    SE: 'Sweden', DK: 'Denmark', NO: 'Norway', FI: 'Finland',
    CH: 'Switzerland', SG: 'Singapore', HK: 'Hong Kong SAR', JP: 'Japan',
    MY: 'Malaysia', ZA: 'South Africa', AE: 'United Arab Emirates',
    BH: 'Bahrain', QA: 'Qatar', CY: 'Cyprus', IL: 'Israel', IS: 'Iceland',
    KR: 'South Korea', PL: 'Poland', PT: 'Portugal', SA: 'Saudi Arabia',
    SI: 'Slovenia', TH: 'Thailand'
  };

  function probeCountryName(code) {
    if (PROBE_COUNTRY[code]) return PROBE_COUNTRY[code];
    /* So a market added to Shopify later still gets a live quote instead of
       quietly reverting to the base-currency bands. Explicit names above stay
       authoritative because Shopify's spelling wins over the browser's where
       they differ (its "Hong Kong SAR" vs ICU's "Hong Kong SAR China"). */
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || '';
    } catch (e) { return ''; }
  }

  var _rateCache = {};
  var _rateInflight = null;

  function liveRateKey() {
    return [CFG.country, cart && cart.total_weight, cart && cart.total_price].join('|');
  }

  function fetchLiveRate() {
    if (!cart || !CFG.country) return Promise.resolve(null);
    var key = liveRateKey();
    if (Object.prototype.hasOwnProperty.call(_rateCache, key)) {
      return Promise.resolve(_rateCache[key]);
    }
    var probe = RATE_PROBE[CFG.country];
    var cname = probeCountryName(CFG.country);
    if (!probe || !cname) { _rateCache[key] = null; return Promise.resolve(null); }

    var qs = 'shipping_address[country]=' + encodeURIComponent(cname);
    if (probe.province) qs += '&shipping_address[province]=' + encodeURIComponent(probe.province);
    if (probe.zip) qs += '&shipping_address[zip]=' + encodeURIComponent(probe.zip);

    _rateInflight = fetch('/cart/shipping_rates.json?' + qs, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var rates = (j && j.shipping_rates) || [];
        if (!rates.length) { _rateCache[key] = null; return null; }
        var cheapest = rates.reduce(function (a, b) {
          return parseFloat(b.price) < parseFloat(a.price) ? b : a;
        });
        /* Only ONE rate ever comes back, and it is the winning one — Shopify
           collapses the same-named method definitions and quotes the best
           match, so above the free-shipping threshold the paid band is not
           returned alongside the $0 rate, it's replaced by it. Measured
           against the live storefront 2026-07-30, same cart, AU address:
           6 units / $63 -> [0.00], 1 unit / $13.99 -> [5.90]. Never both.
           That's why "what delivery WOULD have cost" can't be read from here
           and comes from the synced weight bands instead — see
           deliverySavedMinor(). */
        /* A live quote is the same pre-rounding figure the weight bands give
           — Shopify applies the market's rounding at checkout, not here — so
           it needs the same treatment, or the live path would quietly
           re-introduce the under-quote that chargedMinor() exists to fix.
           Skipped for a base-currency shopper, whose rate was never
           converted and so is never rounded. chargedMinor() is idempotent,
           so this stays correct even if Shopify starts rounding earlier. */
        var base = baseCurrency();
        var raw = Math.round(parseFloat(cheapest.price) * 100);
        var val = {
          minor: (base && cheapest.currency && cheapest.currency !== base)
            ? chargedMinor(raw) : raw,
          name: cheapest.presentment_name || cheapest.name,
          currency: cheapest.currency,
          live: true
        };
        _rateCache[key] = val;
        return val;
      })
      .catch(function () { _rateCache[key] = null; return null; });
    return _rateInflight;
  }

  /**
   * Does THIS market round a converted price up at checkout?
   *
   * Shopify's `market.currencySettings.roundingEnabled`. Tyler turned it OFF
   * on all 34 markets that had it on (2026-08-03; Australia was already off),
   * so today the answer is "nowhere" and the raw converted band is exactly
   * what checkout charges.
   *
   * Read from `cart_rewards` rather than hardcoded, because a hardcoded copy
   * of a Shopify setting is a cached fact with no staleness check — the same
   * failure class as the stale upsell table, and the reason the drawer
   * under-quoted every rounding market for five weeks. Absent the published
   * flag we assume no rounding, which matches the store as it stands; when
   * the cart-fx lane lands the ASK, the drawer starts honouring the real
   * setting per market with no further change here.
   */
  function marketRounds() {
    var r = REWARDS.presentment_rounding;
    if (!r || !CFG.country) return false;
    var m = r[CFG.country];
    return !!(m && m.enabled);
  }

  /**
   * The shop's base currency, as named by the synced rate table itself.
   *
   * Taken from the bands rather than hardcoded so it can't drift from the
   * table it is used to interpret. Free bands are skipped — they are
   * denominated per-market, so their currency is the shopper's, not the
   * shop's.
   */
  function baseCurrency() {
    var rates = REWARDS.shipping_rates || [];
    for (var i = 0; i < rates.length; i++) {
      if (rates[i].rate_currency && rates[i].price_minor > 0) return rates[i].rate_currency;
    }
    return null;
  }

  /**
   * What checkout will actually CHARGE for a converted rate, in minor units.
   *
   * Shopify Markets does not present a converted delivery rate as-is: it
   * rounds it UP to the market's rounding increment — a whole unit in most
   * currencies, a `.95` ending in the euro markets. The drawer used to show
   * the raw converted band and was under-quoting every market with rounding
   * on: a 150g GB cart read £3.02 while checkout took £4.00 (order #53000),
   * and a 150g CA cart read $7.27 against $8.00 charged (#53005).
   *
   * Verified against 593 real orders placed 2026-06-27..08-03 across 19
   * markets: this rule reproduces the charged rate on 591 of them (99.7%).
   * The two misses are one manually-priced NZ order and Iceland, whose ISK
   * increment is coarser than a whole unit (n=1, left alone deliberately).
   *
   * Idempotent by construction — an already-rounded amount is returned
   * unchanged — so it is safe to apply to a live Shopify quote as well as to
   * our own converted band, without needing to know which of the two already
   * had rounding applied.
   */
  function chargedMinor(minor) {
    var m = Number(minor);
    if (!isFinite(m) || m <= 0) return minor;
    if (!marketRounds()) return minor;
    /* The euro markets settle on a .95 ending (NL/FR/DE/ES/PT/IE all did
       while rounding was on); every other currency rounds to a whole unit. */
    var tail = CFG.currency === 'EUR' ? 95 : 0;
    var over = (((m - tail) % 100) + 100) % 100;
    return over === 0 ? m : m + (100 - over);
  }

  /**
   * A band's price in the currency THIS shopper is charged in, or null when we
   * can't say it honestly.
   *
   * Shopify keeps one rate table in the shop's base currency and converts at
   * presentment, so `price_minor` is only quotable to a base-currency shopper.
   * `presentment_minor` is the same band measured against this market's own
   * storefront by the nightly sync — that's what makes every other market
   * quotable at all. Both are checked against a named currency; an unnamed
   * amount is not a price.
   *
   * Only the CONVERTED branch gets chargedMinor(): a base-currency shopper's
   * rate was never converted, so Shopify never rounds it (US orders match the
   * band to the cent), and rounding it here would invent a charge.
   */
  function bandMinor(r) {
    var pc = REWARDS.presentment_currency;
    if (r.presentment_minor != null && pc && CFG.currency && pc === CFG.currency) {
      return chargedMinor(r.presentment_minor);
    }
    if (r.rate_currency && CFG.currency && r.rate_currency !== CFG.currency) return null;
    return r.price_minor;
  }

  /**
   * The PAID delivery band for this cart's weight, from the synced Shopify
   * rate bands — regardless of whether a free-shipping rate currently beats it.
   *
   * Split out from deliveryEstimate() because two callers ask different
   * questions: "what will delivery cost?" (free shipping changes the answer)
   * and "what would delivery have cost?" (it doesn't). Folding both into one
   * function is what made the second unanswerable.
   */
  function paidBandMinor() {
    var rates = REWARDS.shipping_rates || [];
    if (!cart || !rates.length) return null;

    var grams = Number(cart.total_weight || 0);
    var subtotal = cart.total_price;

    var match = [];
    rates.forEach(function (r) {
      if (r.price_minor === 0) return;                // free bands aren't a price
      var amount = bandMinor(r);
      if (amount == null || amount <= 0) return;
      if (r.min_weight_g != null && grams < r.min_weight_g) return;
      if (r.max_weight_g != null && grams > r.max_weight_g) return;
      /* Subtotal conditions are denominated per-market already (that's where
         the free-shipping threshold comes from), so they compare directly
         against the cart. */
      if (r.min_subtotal_minor != null && subtotal < r.min_subtotal_minor) return;
      if (r.max_subtotal_minor != null && subtotal > r.max_subtotal_minor) return;
      match.push({ minor: amount, name: r.name });
    });
    if (!match.length) return null;

    // Cheapest qualifying band — that's the one presented as standard delivery.
    return match.reduce(function (a, b) {
      return b.minor < a.minor ? b : a;
    });
  }

  function deliveryEstimate() {
    if (!cart) return null;
    var thresh = REWARDS.shipping_threshold_minor;
    if (thresh != null && cart.total_price >= thresh) return { free: true };

    /* Prefer a live Shopify quote once it has resolved for this cart state —
       EXCEPT while an offer-experiment code is pending: Shopify quotes the
       UNDISCOUNTED cart (it can't see the code before checkout), so its
       quote can claim free delivery that checkout — pricing the code first,
       dropping the subtotal under the threshold — will then charge for.
       The synced bands + the adjusted-total threshold check above stay
       honest on both sides. */
    if (!EXPX) {
      var cached = _rateCache[liveRateKey()];
      if (cached) return cached;
    }

    return paidBandMinor();
  }

  /**
   * What free delivery is worth on THIS cart, in minor units, or null.
   *
   * Tyler: "should that savings amount also include the delivery cost that the
   * customer is saving when they achieve the free shipping threshold?" Yes —
   * on a small order it's the larger half of the win, and the cart was
   * quietly under-claiming it.
   *
   * But it is only ever shown NEXT TO the discount figure, never folded
   * silently into it. Shopify's checkout itemises "Discount" and
   * "Shipping: Free" as two separate lines, so one merged total would stop
   * reconciling with the very next page the shopper sees. That's the same
   * failure as the $7.69 price: correct arithmetic making a claim the
   * shopper can't verify.
   *
   * Sourced from the synced weight bands, NOT the live quote: Shopify returns
   * only the winning rate, so once the threshold is crossed the paid figure is
   * gone from the response entirely (measured — see fetchLiveRate). The bands
   * are the same numbers: a 150g cart to AU quoted $5.90 live and the matching
   * band is $5.90. Where the band currency doesn't match presentment,
   * paidBandMinor() returns null and no delivery saving is claimed — "roughly
   * this much" is not a thing to say about someone's money.
   */
  function deliverySavedMinor() {
    var thresh = REWARDS.shipping_threshold_minor;
    if (!cart || thresh == null || cart.total_price < thresh) return null;
    var band = paidBandMinor();
    return band && band.minor > 0 ? band.minor : null;
  }

  // ──────────────────── the dual reward ladder ─────────────────────
  /**
   * Build every tier the shopper could reach, marking which are already
   * earned. Value tiers come from Shopify shipping settings + subtotal
   * discounts; quantity tiers from the active automatic discounts.
   */
  function buildTiers() {
    var tiers = [];
    var subtotal = cart ? cart.total_price : 0;
    var qty = realItemCount();

    if (REWARDS.shipping_threshold_minor) {
      tiers.push({
        key: 'ship',
        axis: 'value',
        at: REWARDS.shipping_threshold_minor,
        earned: subtotal >= REWARDS.shipping_threshold_minor,
        label: CFG.reward_labels && CFG.reward_labels.shipping || 'free delivery'
      });
    }
    (REWARDS.value_tiers || []).forEach(function (t, i) {
      tiers.push({
        key: 'val' + i, axis: 'value', at: t.min_subtotal_minor,
        earned: subtotal >= t.min_subtotal_minor,
        label: (t.percent
          ? S('percent_off', '{percent}% off').replace('{percent}', t.percent)
          : t.title || S('a_discount', 'a discount'))
      });
    });
    /* The gift is a rung like any other, so the one-goal-at-a-time ladder
       orders it against the discounts automatically instead of it needing its
       own banner. A shopper at 3 bouquets sees the mini as the next thing to
       reach, not as a surprise at the end. */
    var _g = CFG.free_gift || {};
    /* Past max_below_qty the gift is no longer a rung on this shopper's
       ladder (the 20% tier displaced it — see giftEarned) — showing it
       "unearned at 3" with 6 in the cart would also poison nearestReward
       with a negative remaining. */
    var _gq = giftQualifyingQty();
    if (_g.enabled && _g.min_qty && !(_g.max_below_qty && _gq >= _g.max_below_qty)) {
      tiers.push({
        key: 'gift', axis: 'quantity', at: _g.min_qty,
        earned: giftEarned(), current: _gq,
        label: _g.label || 'a free mini bouquet',
        scope: { collections: [_g.earn_collection], products: _g.earn_products || [] }
      });
    }
    (REWARDS.quantity_tiers || []).forEach(function (t, i) {
      // Count only items the discount actually applies to (see
      // eligibleItemCount) — not the whole cart.
      var eligible = eligibleItemCount(t);
      tiers.push({
        key: 'qty' + i, axis: 'quantity', at: t.min_qty,
        earned: eligible >= t.min_qty,
        current: eligible,
        label: (t.percent
          ? S('percent_off', '{percent}% off').replace('{percent}', t.percent)
          : t.title || S('a_discount', 'a discount')),
        scope: t.scope
      });
    });
    return tiers;
  }

  /**
   * Pick the reward to promote: the one requiring the LEAST additional effort.
   * To compare across axes we express both in "items still needed", using the
   * cart's own average item price as the yardstick. This is why the cart can
   * say "add 1 more to save 15%" instead of "spend $30 more for free delivery"
   * when the second is a far bigger ask.
   */
  function nearestReward(tiers) {
    var subtotal = cart ? cart.total_price : 0;
    var qty = realItemCount();
    var avg = qty > 0 ? Math.max(1, subtotal / qty) : 0;

    var best = null;
    tiers.forEach(function (t) {
      if (t.earned) return;
      var effort, remaining;
      if (t.axis === 'value') {
        remaining = t.at - subtotal;
        effort = avg > 0 ? remaining / avg : Number.MAX_SAFE_INTEGER;
      } else {
        remaining = t.at - qty;
        effort = remaining;
      }
      if (!best || effort < best.effort) {
        best = { tier: t, effort: effort, remaining: remaining };
      }
    });
    return best;
  }

  /**
   * The reward path, as ONE ladder rather than two competing bars.
   *
   * The previous version rendered free delivery and the quantity discount as
   * independent blocks, each with its own progress. With a third reward (the
   * free mini) that becomes a menu, and a menu is not a goal — a shopper
   * looking at four simultaneous offers has to work out which one is worth
   * chasing, which is exactly the work the cart should be doing for them.
   *
   * So: WON rewards collapse to a row of ticks, ONE next goal is promoted at
   * full weight with the bar tracking it, and at most one further rung is
   * hinted so there's a sense of a path rather than a cliff.
   *
   * Ordering across the two axes reuses nearestReward()'s yardstick — both are
   * expressed as "items still needed" — so "1 more bouquet" correctly beats
   * "$30 more" even though they're different units.
   *
   * The bar measures progress from the LAST reward earned to the next one, not
   * from zero. It therefore refills after each win instead of sitting
   * permanently near-full, and that reset is the point: it lands at the same
   * moment as the celebration, so it reads as levelling up rather than as
   * losing progress. (Deliberately NOT variable-ratio: randomised rewards on a
   * real store break price trust. The pull here is the goal-gradient effect —
   * a fixed, visible, honest next step.)
   */
  function ladderTiers() {
    var giftPid = null;
    var gl = giftLine();
    if (gl) giftPid = String(gl.product_id);
    var tiers = buildTiers().filter(function (t) {
      // A quantity tier the cart's contents can't reach is noise, and
      // advertising it would promise a discount checkout won't apply.
      if (t.axis !== 'quantity') return true;
      if ((t.current || 0) <= 0) return false;
      /* And a tier whose ONLY eligible item is the free gift isn't reachable
         either — the free mini made the mini ladder look one-quarter complete
         to someone buying signature bouquets, so the drawer told them "then 4
         mini bouquets -> 15% off". They aren't shopping for minis; we put that
         one in their cart. */
      if (t.key !== 'gift' && giftPid && (t.current || 0) === 1) {
        var onlyGift = itemCountsTowardTier({ product_id: giftPid }, t);
        if (onlyGift) return false;
      }
      return true;
    });

    /* ── ONE quantity ladder, not one per collection ──────────────────────
     *
     * The rungs are scoped per collection (signature 3/15% + 6/25%, mini
     * 4/15% + 8/25%), and every collection the cart touches used to add its
     * own pair. So the TRACK'S SEGMENT COUNT moved with cart composition:
     * three full-size bouquets drew a 3-segment bar, and adding a single mini
     * redrew it with 5. Tyler saw exactly that.
     *
     * It is the same failure the segmented track was built to fix, arriving by
     * the other door. That fix made the NUMERATOR monotonic — won rewards
     * never un-win. This one is the DENOMINATOR: with rungs going 3 → 5, the
     * identical cart progress (1 reward banked, nearly at free delivery) reads
     * 60% and then 40%, every banked segment visibly narrows, and the shopper
     * is punished on screen for adding to their cart.
     *
     * So the ladder promotes the collection they are actually shopping — the
     * one with the most eligible items — and the other collections' rungs are
     * dropped rather than interleaved. That keeps the bar at a stable three
     * rungs across both of Tyler's carts, and it also stops the drawer telling
     * someone holding three full-size bouquets to go and buy three minis.
     * Nothing reachable is lost: the other collection is still one tap away in
     * the upsell rail, which is the surface for discovery. This block is the
     * goal, and "a menu is not a goal" is the rule the whole ladder is built
     * on.
     */
    var famKey = function (t) {
      var s = t.scope || {};
      return (s.collections || []).slice().sort().join('|') ||
             (s.products || []).slice().sort().join('|') || '*';
    };
    var families = {};
    tiers.forEach(function (t) {
      if (t.axis !== 'quantity' || t.key === 'gift') return;
      var k = famKey(t);
      var f = families[k] || (families[k] = { key: k, current: 0, remaining: Infinity });
      f.current = Math.max(f.current, t.current || 0);
      f.remaining = Math.min(f.remaining, Math.max(0, t.at - (t.current || 0)));
    });
    var famKeys = Object.keys(families);
    if (famKeys.length > 1) {
      // Most engaged wins; ties go to the nearer reward, then to the name so
      // the choice can't flicker between two renders of the same cart.
      famKeys.sort(function (a, b) {
        var A = families[a], B = families[b];
        return (B.current - A.current) || (A.remaining - B.remaining) ||
               (A.key < B.key ? -1 : A.key > B.key ? 1 : 0);
      });
      var keep = famKeys[0];
      tiers = tiers.filter(function (t) {
        if (t.axis !== 'quantity' || t.key === 'gift') return true;
        return famKey(t) === keep;
      });
    }

    var subtotal = cart ? cart.total_price : 0;
    var qty = realItemCount();
    var avg = qty > 0 ? Math.max(1, subtotal / qty) : 0;
    tiers.forEach(function (t) {
      if (t.axis === 'value') {
        t._remaining = Math.max(0, t.at - subtotal);
        t._effort = avg > 0 ? t._remaining / avg : Number.MAX_SAFE_INTEGER;
        t._current = subtotal;
      } else {
        t._remaining = Math.max(0, t.at - (t.current || 0));
        t._effort = t._remaining;
        t._current = t.current || 0;
      }
    });
    var won = tiers.filter(function (t) { return t.earned; })
                   .sort(function (a, b) { return a.at - b.at; });
    var todo = tiers.filter(function (t) { return !t.earned; })
                    .sort(function (a, b) { return a._effort - b._effort; });
    return { won: won, todo: todo, all: tiers };
  }

  /* ── just-added garnish (Tyler 2026-08-02) ─────────────────────────
   * The freshly added line pulses tint + wears an "Added" chip for ~4s.
   * NOTE: an ASOS-style "It's in the bag" takeover of the reward bar's
   * message slot was BUILT AND REVERTED the same day — the mid-open
   * repaint made the segment strip visibly glitch and Tyler called it
   * off ("too complicated"). Don't rebuild it without solving the
   * repaint-during-slide-in problem first. */
  var _addedAt = 0, _addedVariant = null, _addedTimer = null;
  /* Post-add validation (backlog #4, 2026-08-03): when an upsell add CROSSES
     a reward rung, the just-added row also wears a one-shot "Popular choice ✓"
     chip for the same 4s window. Commitment & consistency — reassurance lands
     at the moment of maximum second-guessing, right after the choice. Gated
     to rung-crossing upsell adds so it stays rare; the permanent "Added" chip
     this slot used to hold was cut as clutter (Tyler 2026-08-02) — do not
     let this one become unconditional. "Popular" is earned, not claimed: rail
     items are engine-ranked by real view→add→purchase completion. */
  var _addedCrossed = false;
  /* Bundle-widget behaviors ported 2026-08-03 (Tyler: "same features as the
     bundle upsell carousel"). _wigglePending: an upward reward crossing while
     the drawer is open makes the checkout button wiggle once (the bundle's
     ATC celebration — positive reinforcement at the moment the price got
     better, cleared 600ms later, silent under reduced-motion).
     _railAfterAdd: after an upsell add, the rail restores its position so
     the NEXT candidate slides into the added card's slot, then glides its
     left edge into alignment — the bundle's advance-to-next-unpicked, 350ms
     later so the add registers first. */
  var _wigglePending = false;
  var _rewardPainted = false;
  var _railAfterAdd = null;
  /* Re-sync hook for the carousel's chrome (thumb width, arrow states),
     set fresh by each renderUpsells. render() runs while the drawer is
     still [hidden] — every width measures 0 there, so the progress thumb
     painted full-width (Tyler 2026-08-03: "just full green until i tap
     it"). openCart calls this after the drawer is actually visible. */
  var _carResync = null;
  /* Teardown handle for the desktop carousel's auto-scroll ticker. Held at
     module scope on purpose: the section is rebuilt on every cart change and
     on every toggle, so a timer owned only by the render that started it
     would keep gliding a DETACHED list — and the next render would start a
     second one on top. One holder, cleared before anything else in
     renderUpsells and again on close(). */
  var _upsellAuto = null;
  /* Sticky for the whole drawer session: once she has scrolled the carousel
     herself, it stays hers. Without this the ticker would come back to life on
     the very next repaint — and a repaint happens on every add and every
     quantity change, i.e. constantly. Reset in openCart, same as _railPeeked. */
  var _upsellAutoKilled = false;
  function stopUpsellAuto() {
    if (_upsellAuto) { _upsellAuto(); _upsellAuto = null; }
  }
  /* The expanded desktop stack grows into space the BODY owns, and the body is
     the upsell host's sibling — so the takeover has to be flagged on the panel
     itself. Driven from renderUpsells off what it actually painted (not off the
     stored preference), which is what keeps an emptied cart, a resize down to
     phone width, or a gift-note loan from stranding the class on. */
  function setUpsellTakeover(on) {
    if (panelEl) panelEl.classList.toggle('pp-cart--upsells-full', !!on);
  }
  /* Same problem, same fix, for the reward lines: fitTemplateLine measures a
     rendered width to pick the longest variant that stays on ONE row, and a
     drawer painted behind [hidden] measures 0, which makes the fit bail and
     leave the longest variant in place — the exact two-row wrap it exists to
     prevent. openCart re-runs it once the drawer has real width. */
  var _rewardRefit = null;
  /* Set by renderUpsells while the section is collapsible; lets the gift-note
     editor collapse the suggestions to free the screen (Tyler 2026-08-03 v14)
     through the same code path as the shopper's own toggle. Null when there
     is nothing to collapse (empty cart). */
  var _upsellSetCollapsed = null;

  /**
   * Bring an element fully into view inside the drawer's scrolling body.
   *
   * Tyler 2026-08-03 v14: "I just want the 'print a message on the note card'
   * to be made visible on screen when it's clicked, regardless of its position
   * in the cart's added products list." Tapping the third bouquet's note in a
   * six-bouquet cart opened an editor below the fold — the shopper tapped and
   * nothing appeared to happen.
   *
   * scrollIntoView is deliberately not used: it scrolls every scrollable
   * ancestor including the PAGE behind the drawer, which drags the storefront
   * around underneath a fixed panel. This moves exactly one scroller.
   */
  function revealInBody(node) {
    if (!node) return;
    /* Derive the scroller FROM THE NODE, not from `root`. Looking it up via
       root.querySelector meant this function depended on two references being
       right; closest() asks the only question that actually matters — which
       scroll container is this node inside — and is correct even if the
       drawer is ever re-rooted or rendered twice. */
    var body = node.closest && node.closest('.pp-cart__body');
    if (!body) return;
    var n = node.getBoundingClientRect(), b = body.getBoundingClientRect();
    /* A detached or not-yet-laid-out node measures 0×0, and a zero rect looks
       exactly like "scrolled far above the viewport" to the branches below —
       which scrolls to the TOP, the opposite of revealing anything. Bail
       instead and let the caller's next attempt measure a real node. */
    if (!n.height && !n.width) return;
    var pad = 10, delta;
    if (n.height >= b.height - pad * 2) delta = n.top - b.top - pad;
    else if (n.bottom > b.bottom - pad) delta = n.bottom - b.bottom + pad;
    else if (n.top < b.top + pad) delta = n.top - b.top - pad;
    else return;                                  // already fully visible
    /* DIRECT ASSIGNMENT, not scrollTo({behavior:'smooth'}). Measured on the
       live drawer: a smooth scrollTo on this container reliably settles at 0
       instead of the requested offset, while setting scrollTop lands exactly
       (260 vs 0 on the same element, same frame). The drawer's panel is a
       transformed, fixed-position ancestor and Chrome's smooth-scroll
       animation does not survive it — which is why the editor kept opening
       below the fold even though the arithmetic was right.
       Instant is also the better behaviour here: the shopper tapped a button
       and expects the editor to BE there, not to watch it arrive. */
    body.scrollTop = Math.max(0, body.scrollTop + delta);
  }
  /* One-shot rail PEEK per drawer open (Tyler 2026-08-03): the rail nudges
     ~30px out and settles back once, revealing the cut card — "there's more
     here" without autonomous motion. Reset when the drawer opens; skipped
     entirely under reduced-motion.
     (The note here used to say auto-scroll had been considered and rejected.
     Tyler asked for it on desktop 2026-08-04, so it now exists there and the
     peek stands down for that treatment — see startUpsellAuto. The WCAG 2.2.2
     obligation it worried about is met by hover/focus pause plus a manual
     scroll that stops the ticker for the session.) */
  var _railPeeked = false;
  function justAdded() { return _addedAt && (Date.now() - _addedAt < 4000); }
  function markAdded(variantId) {
    _addedAt = Date.now();
    if (variantId) _addedVariant = variantId;
    clearTimeout(_addedTimer);
    // One repaint when the moment expires, so the goal prompt returns and
    // the line flash class drops off without anyone touching the cart.
    _addedTimer = setTimeout(function () {
      _addedCrossed = false;
      if (open) render();
    }, 4200);
  }

  function renderReward(parent) {
    if (REWARDS.enabled === false) return;
    if (!cart) return;
    var L = ladderTiers();
    if (!L.all.length) return;

    var wrap = el('div', 'pp-cart__reward');
    var next = L.todo[0];
    var then = L.todo[1];
    // Held back so the ladder can be inserted between the goal and this hint —
    // the reading order is goal, progress, what's after it.
    var hintEl = null;

    /* Banked rewards are NOT listed here any more — they moved to the footer,
       beside the total. Two reasons, both Tyler's:
         · they were accumulating along the top edge and would eventually
           collide with the close button, and
         · a chip row sitting next to a chip-shaped X made the X read as
           another reward rather than a control.
       It also separates the two ideas properly: the top block is what's NEXT,
       the footer is what you've actually got — and what you've got is money,
       which belongs next to the number you're about to pay. */

    // ── the one goal in front of them ───────────────────────────────
    if (next) {
      var head = el('div', 'pp-cart__reward-head');
      head.appendChild(icon(tierIcon(next), 'pp-cart__reward-head-icon'));
      var msg = el('div', 'pp-cart__reward-head-msg');
      if (next.axis === 'value') {
        /* Near-miss distance framing (backlog #2, 2026-08-03): "You're $4.10
           away from free delivery" states the gap as almost-possession, which
           is what the goal-gradient effect actually reads — proximity, not
           instructions. "Add $X more" survives behind cfg.reward_nmiss:false
           so the variant is one config flip (pippy.cart_config). Variant is
           stamped on cart_open props (reward_copy) for segmentation. Honest
           by construction: the amount is nearestReward's real remaining. */
        if (CFG.reward_nmiss !== false) {
          fillTemplate(msg, S('reward_nmiss_value', "You're {amount} away from {reward}"), {
            amount: { text: money(next._remaining), strong: true },
            reward: { text: next.label, strong: true }
          });
        } else {
          fillTemplate(msg, S('reward_add_value', 'Add {amount} more for {reward}'), {
            amount: { text: money(next._remaining), strong: true },
            reward: { text: next.label, strong: true }
          });
        }
      } else {
        var n = Math.max(1, next._remaining);
        /* Endowed progress (backlog #5, 2026-08-03): when the shopper already
           holds ≥1 counting item, NAME the banked contribution — "1 of 3 —
           add 2 more…". Nunes & Drèze: granted progress only motivates when
           it's explained; the bar shows it, this says it. TRUE by
           construction: _current is the real eligible-item count feeding the
           same tier math. Zero-item carts keep the plain instruction (there
           is nothing to endow). cfg.reward_endow:false reverts. */
        /* `have` is NOT strong: head-msg strong renders green (the reward's
           colour), and a green count read as random decoration (Tyler
           2026-08-03). The reward keeps the only green in this line. */
        var rp = {
          have: { text: String(next._current) },
          need: { text: String(next.at) },
          count: { text: String(n) },
          item: { text: tierNoun(next, n !== 1) },
          reward: { text: next.label, strong: true }
        };
        if (CFG.reward_endow !== false && next._current >= 1) {
          msg._ppFit = [
            { tpl: S('reward_add_qty_endow',
                '{have} of {need} — add {count} more {item} for {reward}'), parts: rp },
            { tpl: S('reward_add_qty_endow_noitem',
                '{have} of {need} — add {count} more for {reward}'), parts: rp },
            { tpl: S('reward_add_qty_endow_tight',
                '{have}/{need} — {count} more for {reward}'), parts: rp }
          ];
        } else {
          msg._ppFit = [
            { tpl: S('reward_add_qty', 'Add {count} more {item} for {reward}'), parts: rp },
            { tpl: S('reward_add_qty_noitem', 'Add {count} more for {reward}'), parts: rp }
          ];
        }
        fitTemplateLine(msg, msg._ppFit);
      }
      head.appendChild(msg);
      wrap.appendChild(head);

      // One rung beyond, faint: a path, not a menu.
      if (then) {
        var hint = el('div', 'pp-cart__reward-then');
        /* REMAINING, not the threshold. The line above says "Add 2 more\u2026" and
           this one used to say "then 3 full-size bouquets" \u2014 one relative,
           one absolute, stacked. Tyler read the second as ignoring the two
           bouquets already in his cart, which is exactly what it looked like:
           a shopper has no way to tell that one number counts from here and
           the other counts from zero. Both count from here now. */
        var tn = Math.max(1, then._remaining);
        if (then.axis === 'value') {
          fillTemplate(hint,
            S('reward_then_value', 'then {amount} more \u2192 {reward}'),
            { amount: { text: money(then._remaining) },
              reward: { text: then.label } });
        } else {
          var tp = { count: { text: String(tn) },
                     item: { text: tierNoun(then, tn !== 1) },
                     reward: { text: then.label } };
          hint._ppFit = [
            { tpl: S('reward_then_qty', 'then {count} more {item} \u2192 {reward}'), parts: tp },
            { tpl: S('reward_then_qty_noitem', 'then {count} more \u2192 {reward}'), parts: tp }
          ];
          fitTemplateLine(hint, hint._ppFit);
        }
        hintEl = hint;
      }
    } else if (L.won.length) {
      /* NAME what was won. "All rewards unlocked — nice work." congratulated the
         shopper on something it declined to specify, and at six bouquets it was
         also the only thing left in the block — one bare sentence under a
         stray hairline, which is what Tyler saw. Listing the rewards makes the
         line carry information instead of tone, and it's the same set the
         footer prices out, so the two agree. */
      var doneRow = el('div', 'pp-cart__reward-sub is-earned');
      doneRow.appendChild(icon(ICON.check, 'pp-cart__reward-done-icon'));
      var wonLabels = L.won.map(function (t) { return t.label; });
      var tail = wonLabels.length > 1
        ? wonLabels.slice(0, -1).join(', ') + ' and ' + wonLabels[wonLabels.length - 1]
        : wonLabels[0];
      var base = String((CFG.reward_labels && CFG.reward_labels.all_unlocked)
        || 'All rewards unlocked').replace(/[.\s]+$/, '');
      doneRow.appendChild(el('span', null, base + ' — ' + tail));
      wrap.appendChild(doneRow);
    }

    /* ── THE LADDER, AS SEGMENTS — one per reward, and it only ever grows ──
     *
     * The old bar measured progress toward the CURRENT goal alone, so it could
     * jump BACKWARD the moment a reward was banked: at 3 bouquets the goal is
     * free delivery and the bar sits at 35.70/45.00 = 79%; add a fourth, free
     * delivery is won, the goal becomes 25%-off-at-6, and the bar drops to
     * 4/6 = 67%. The shopper does the thing they were asked to do and the meter
     * shrinks — which is precisely backwards as a reward loop, and it's what
     * Tyler was seeing.
     *
     * Now the track holds one segment per applicable reward. Filled segments are
     * banked, the current one fills as they add, future ones sit empty. Progress
     * is (earned + fraction toward the next) / total, which is MONOTONIC: at the
     * instant of crossing, the partial reaches 1 and is replaced by a whole
     * segment, so the fill never retreats.
     *
     * Segments rather than a continuous bar because the rungs are on different
     * axes — a quantity of bouquets and a dollar subtotal don't share a scale,
     * so any single continuous axis across them would be a fiction. Equal
     * segments claim only what's true: how many rewards there are, and how many
     * you have.
     *
     * Deliberately NOT casino: no flashing, no near-miss teasing, no sound, no
     * randomised reward. The only motion is the fill width easing, which is
     * already suppressed under prefers-reduced-motion. The gap between segments
     * does the satisfying work — crossing a threshold snaps a whole block to
     * solid, which reads as banked rather than as a needle creeping. That's the
     * goal-gradient effect done honestly: a fixed, visible, truthful next step,
     * which matters more for a 60+ audience who need to be able to READ the
     * thing, not just feel it.
     */
    if (L.all.length) {
      var rungs = L.all.length;
      var frac = next && next.at > 0
        ? Math.max(0, Math.min(1, (next._current || 0) / next.at)) : 1;
      var progress = Math.min(1, (L.won.length + (next ? frac : 0)) / rungs);

      /* NOT `track` — that's the module-level analytics emitter, and a `var`
         here would hoist and shadow it for the whole function. */
      var trackEl = el('div', 'pp-cart__reward-track');
      for (var si = 0; si < rungs; si++) {
        var seg = el('div', 'pp-cart__reward-seg');
        var segFill = el('div', 'pp-cart__reward-segfill');
        // How much of THIS segment the overall progress covers.
        var within = Math.max(0, Math.min(1, progress * rungs - si));
        /* A visible sliver on a barely-started segment, so an empty trough
           still reads as "a bar that has begun" rather than as broken. Only
           once they're actually on this segment — a future one stays empty. */
        if (within > 0 && within < 0.06) within = 0.06;
        segFill.style.width = (within * 100).toFixed(1) + '%';
        if (within >= 1) seg.className += ' is-done';
        seg.appendChild(segFill);
        trackEl.appendChild(seg);
      }
      wrap.appendChild(trackEl);
      if (hintEl) wrap.appendChild(hintEl);
    }

    // Celebrate a crossing exactly once per tier.
    L.all.forEach(function (t) {
      if (t.earned && !earnedKeys[t.key]) {
        earnedKeys[t.key] = true;
        wrap.classList.add('just-earned');
        /* Wiggle the checkout button too — but only on a crossing DURING the
           session, never on the first paint of a cart that arrived already
           over a threshold (that's an announcement, not a win). */
        if (_rewardPainted) _wigglePending = true;
        track_emit_reward(t);
      } else if (!t.earned) {
        earnedKeys[t.key] = false;   // allow re-celebration if they drop below
      }
    });
    _rewardPainted = true;

    parent.appendChild(wrap);
    /* Re-fit once the drawer has real width (see _rewardRefit). Scoped to the
       nodes this paint produced, and re-armed by every subsequent paint, so it
       can never re-measure a detached node from an earlier render. */
    _rewardRefit = function () {
      [wrap.querySelector('.pp-cart__reward-head-msg'),
       wrap.querySelector('.pp-cart__reward-then')].forEach(function (n) {
        if (n && n._ppFit) fitTemplateLine(n, n._ppFit);
      });
    };
    /* Run it NOW, not only from openCart. The inline fit during render always
       bails: the line is measured while `wrap` is still detached — everything
       above builds into it and this appendChild is the first moment it has a
       width — so clientWidth is 0 and the longest variant stands. That was
       invisible on open (the open hook re-fit it) and wrong on every
       re-render of an already-open drawer: changing a quantity left "4 of 6 —
       add 2 more full-size bouquets for 25% off" ellipsed mid-word instead of
       stepping down to a shorter complete sentence. Attached now, so this
       call measures for real; on the first hidden paint it bails as before
       and openCart still covers it. */
    _rewardRefit();
  }

  function track_emit_reward(t) {
    track.emit('cart_reward_reached', {
      value: cart.total_price / 100, currency: CFG.currency, qty: realItemCount()
    }, { tier: t.key, axis: t.axis, label: t.label, clarity_tag: 'reward_' + t.key });
  }

  // ─────────────────────────── line items ──────────────────────────
  /**
   * Group cart lines by variant so the same product shows ONCE with a combined
   * quantity, not as "2" and "3" on separate rows.
   *
   * Shopify only splits a variant across lines when the lines differ by line-item
   * properties or selling plan. Ours pick that up from the theme's add-to-cart
   * form (which other apps decorate with their own properties) versus our own
   * JSON adds, which send none. The shopper doesn't care about that distinction —
   * to them it's five of one bouquet — so we present it that way.
   *
   * The underlying lines are kept in `lines` so quantity changes and removal act
   * on the real cart without destroying any properties Shopify is tracking.
   */
  var NOTE_PROP = 'Gift message';

  /**
   * Tidy a gift message into what will actually be printed.
   *
   * The message goes onto a small physical greeting card, so runaway blank
   * lines aren't a styling quibble — they push the text off the card, and the
   * warehouse prints whatever we send. Left unchecked a shopper can also paste
   * past the limit, or leave trailing whitespace that silently shifts the
   * layout.
   *
   * Collapses runs of spaces, strips space around line breaks, allows at most
   * ONE blank line between paragraphs, removes control characters, and trims.
   * Truncation falls back to a word boundary so a name is never cut in half.
   *
   * Applied on blur, on save and to AI suggestions — never on every keystroke,
   * which would move the caret mid-sentence.
   */
  function normalizeNote(text, maxLen) {
    var s = String(text == null ? '' : text);
    s = s.replace(/\r\n?/g, '\n');
    /* Strip anything shaped like an HTML tag.
       The cart itself is safe — every string reaches the DOM via textContent —
       but this message travels onward into order notes, the Flow automation and
       Klaviyo templates, and those CAN render HTML. Sanitising at the point of
       entry means nothing downstream has to remember to.
       The pattern requires a letter or slash after "<", so affectionate "<3"
       survives while "<script>" and "<img src=x>" do not. */
    s = s.replace(/<\/?[a-zA-Z][^>]*>/g, '');
    // Strip control characters: pasting from Word or Notes brings these
    // in, and they print as blanks or boxes on the physical card.
    s = s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
    s = s.replace(/[ \t]+/g, ' ');
    s = s.replace(/[ \t]*\n[ \t]*/g, '\n');
    s = s.replace(/\n{3,}/g, '\n\n');
    s = s.trim();
    if (maxLen && s.length > maxLen) {
      s = s.slice(0, maxLen);
      // Prefer cutting at a word boundary, but not if that guts the message.
      var cut = s.replace(/\s+\S*$/, '');
      if (cut.length >= maxLen * 0.6) s = cut;
      s = s.trim();
    }
    return s;
  }

  function lineNote(item) {
    var p = item && item.properties;
    return (p && p[NOTE_PROP]) ? String(p[NOTE_PROP]) : '';
  }

  /**
   * Group cart lines for display, keyed by variant AND gift message.
   *
   * Two competing needs:
   *   · the same product added twice must show as one row with a combined
   *     quantity (Shopify splits lines when properties differ — e.g. Kaching
   *     stamps __kaching_bundles on lines it creates and ours carry none)
   *   · a bouquet carrying a gift message must stay its OWN row, because the
   *     message belongs to that specific bouquet and the shopper has to see
   *     which one it's on
   *
   * Keying on variant+message satisfies both: un-noted units merge, and each
   * distinct message keeps its own row.
   */
  function groupedLines() {
    if (!cart) return [];
    var order = [];
    var byKey = {};
    cart.items.forEach(function (item) {
      if (isMechanismLine(item)) return;
      // Group by VARIANT ONLY. Shopify splits a line whenever properties
      // differ, so attaching a note to one of three bouquets creates a second
      // line — but that's an implementation detail of Shopify's cart, not
      // something a shopper should ever have to understand. Showing the same
      // bouquet as two cards confused people (and this customer base skews
      // elderly). One product = one card; the notes live inside it.
      /* Group by variant AND by gift-ness. Shopify keeps the free BXGY unit
         on its own line (its properties differ), but grouping purely by
         variant merged it with a PAID unit of the same mini into a single
         card showing quantity 2 — the "1 free mini and 1 full price mini"
         Tyler found at checkout, presented in the drawer as one $0 row.
         Separate cards mean the drawer says exactly what checkout will
         charge, and the gift card can drop its stepper without also
         freezing a paid unit the shopper is entitled to change. */
      var isGift = isFreeGiftLine(item);
      var k = String(item.variant_id) + (isGift ? ':gift' : '');
      if (!byKey[k]) {
        byKey[k] = {
          key: k,
          isGift: isGift,
          variant_id: item.variant_id,
          product_id: item.product_id,
          product_title: item.product_title,
          variant_title: item.variant_title,
          url: item.url,
          featured_image: item.featured_image,
          quantity: 0,
          final_line_price: 0,
          original_line_price: 0,
          final_price: item.final_price,
          lines: [],
          notes: [],      // {line, text} per noted unit
          plainQty: 0     // units with no note yet
        };
        order.push(byKey[k]);
      }
      var g = byKey[k];
      var note = lineNote(item);
      g.lines.push(item);
      g.quantity += item.quantity;
      g.final_line_price += item.final_line_price;
      g.original_line_price += item.original_line_price;
      if (note) g.notes.push({ line: item, text: note, quantity: item.quantity });
      else g.plainQty += item.quantity;
    });
    /* FIRST-ADDED FIRST (Tyler 2026-08-03). /cart.js lists newest first, so
       rendering it verbatim put every fresh add at the TOP — the list
       reshuffled under the shopper and an upsell add looked like it hijacked
       the cart. Reversed, the cart reads as a stable ledger: what you chose
       first stays first, new things append at the bottom (where the eye
       already is, next to the rail they were added from). */
    order.reverse();
    return order;
  }

  /** Total bouquets carrying a message — drives how many $5 notes we charge. */
  function notedUnitCount() {
    if (!cart) return 0;
    return cart.items.reduce(function (n, i) {
      return n + (!isMechanismLine(i) && lineNote(i) ? i.quantity : 0);
    }, 0);
  }

  function renderLines(parent) {
    if (!cart || !cart.items.length) {
      var empty = el('div', 'pp-cart__empty');
      empty.appendChild(el('div', 'pp-cart__empty-title', S('empty_title', 'Your cart is empty')));
      empty.appendChild(el('div', 'pp-cart__empty-text',
        S('empty_text', 'Find a bouquet that lasts forever.')));
      var shopBtn = el('button', 'pp-cart__btn pp-cart__btn--secondary',
        S('empty_cta', 'Continue shopping'));
      shopBtn.addEventListener('click', close);
      empty.appendChild(shopBtn);
      parent.appendChild(empty);
      return;
    }

    groupedLines().forEach(function (item) {
      /* Each row is a self-contained CARD: media + details on top, and the
         gift note as a full-width footer INSIDE the same card. Previously the
         note was appended to the details column, so it started at the image's
         right edge and hung off the bottom of the row — it read as loose debris
         rather than as something belonging to that bouquet. */
      var line = el('div', 'pp-cart__line');
      line.setAttribute('data-line-key', String(item.key || item.variant_id));
      var isFreshAdd = justAdded() && _addedVariant &&
          String(item.variant_id) === String(_addedVariant);
      if (isFreshAdd) line.classList.add('is-just-added');
      var main = el('div', 'pp-cart__line-main');

      if (item.featured_image && item.featured_image.url) {
        var img = d.createElement('img');
        img.className = 'pp-cart__line-img';
        img.src = item.featured_image.url;
        img.alt = item.product_title || '';
        img.loading = 'lazy';
        main.appendChild(img);
      }

      var info = el('div', 'pp-cart__line-info');
      var top = el('div', 'pp-cart__line-top');
      // The wrapper takes the free space so the remove button is pinned to the
      // panel edge on every row, rather than sitting wherever the title ends.
      var titleWrap = el('div', 'pp-cart__line-titlewrap');
      // /cart.js line items carry their own market-aware url — link the title
      // to the product page. Falls back to a plain div if it's ever absent.
      var lineClean = cleanTitle(item.product_title);
      var lineTitle = el(item.url ? 'a' : 'div', 'pp-cart__line-title',
        lineClean.name);
      if (lineClean.size) {
        /* "(Full size)" in parens — the same treatment as the upsell card
           titles (Tyler 2026-08-03), one convention drawer-wide. */
        lineTitle.appendChild(el('span', 'pp-cart__line-size',
          '(' + lineClean.size + ')'));
      }
      if (item.url) lineTitle.href = item.url;
      titleWrap.appendChild(lineTitle);
      /* No "Added" chip (Tyler 2026-08-02): it stacked under the title on
         mobile and read as clutter. The is-just-added pulse on the row is
         feedback enough. The ONE exception (backlog #4): a rung-crossing
         upsell add earns a one-shot "Popular choice ✓" for the pulse window —
         conditional and rare, which is what keeps it out of clutter court. */
      if (isFreshAdd && _addedCrossed) {
        titleWrap.appendChild(el('span', 'pp-cart__line-affirm',
          S('line_affirm', 'Popular choice ✓')));
      }
      /* The gift says what it is on its own line, which is where a shopper
         looks to understand a $0.00 row. This is why it needs no chip in the
         footer: the fact is stated at the thing it's true of. */
      if (item.properties && item.properties[(CFG.free_gift || {}).prop || '_free_gift']) {
        titleWrap.appendChild(el('span', 'pp-cart__line-gift',
          (CFG.free_gift || {}).line_label || 'Free gift'));
      }
      if (item.variant_title && item.variant_title !== 'Default Title') {
        titleWrap.appendChild(el('div', 'pp-cart__line-variant', item.variant_title));
      }
      /* Inclusion pills (Envelope / Note card / Instructions) removed
         entirely (Tyler 2026-08-03 — "too much and too out of place").
         The CFG.line_subtitle emit stays in the liquid for a possible
         return; the old renders live at da399c1 and its parents. */
      top.appendChild(titleWrap);

      var remove = el('button', 'pp-cart__line-remove');
      remove.setAttribute('aria-label',
        A11Y('remove', 'Remove') + ' ' + (item.product_title || ''));
      remove.appendChild(icon(ICON.trash));
      remove.addEventListener('click', function () {
        var doRemove = function () {
          // Fade the card immediately. Removal takes a moment, and a control
          // that looks inert reads as broken — which is how this was reported.
          line.style.opacity = '0.45';
          remove.disabled = true;
          changeQty(item, 0);
        };
        var loss = removalLoss(item, 0);
        if (loss) confirmLoss(remove, item, loss, doRemove); else doRemove();
      });
      top.appendChild(remove);
      info.appendChild(top);

      var bottom = el('div', 'pp-cart__line-bottom');

      /* THE FREE GIFT HAS NO QUANTITY CONTROL (Tyler 2026-08-26).
         The Buy-X-Get-Y discount zeroes exactly ONE unit. With a stepper the
         shopper could raise the gift to 2, watch the drawer price both at
         $0 — the line price is genuinely $0 until the discount reallocates —
         and then reach checkout holding "1 free mini and 1 full price mini",
         paying for a gift they were told was free, plus the extra shipping
         weight. syncFreeGift() heals quantity>1 back to 1, but only after a
         round trip, so the wrong number is on screen in the meantime and the
         shopper has already been shown a price that will change.
         Removing the control removes the whole class of bug: the gift's
         quantity is owned by the reward ladder, not by the shopper. The bin
         still works — declining a gift is a legitimate choice. */
      if (item.isGift || isFreeGiftLine(item)) {
        /* A tag, not a stepper. NOTE: this must NOT early-return — the price
           slot, the note footer and parent.appendChild(line) all come after,
           so bailing here would drop the gift line out of the drawer. */
        bottom.appendChild(el('span', 'pp-cart__gift-tag',
          S('free_gift_tag', 'Free gift')));
      } else {
        var qtyWrap = el('div', 'pp-cart__qty');
        var minus = el('button', 'pp-cart__qty-btn', '−');
        var count = el('span', 'pp-cart__qty-count', String(item.quantity));
        var plus = el('button', 'pp-cart__qty-btn', '+');
        minus.setAttribute('aria-label', A11Y('decrease', 'Decrease quantity'));
        plus.setAttribute('aria-label', A11Y('increase', 'Increase quantity'));
        if (item.quantity <= 1) minus.disabled = true;
        minus.addEventListener('click', function () {
          var loss = removalLoss(item, item.quantity - 1);
          if (loss) {
            confirmLoss(minus, item, loss, function () { changeQty(item, item.quantity - 1); });
          } else {
            changeQty(item, item.quantity - 1);
          }
        });
        plus.addEventListener('click', function () { changeQty(item, item.quantity + 1); });
        qtyWrap.appendChild(minus); qtyWrap.appendChild(count); qtyWrap.appendChild(plus);
        bottom.appendChild(qtyWrap);
      }

      var priceWrap = el('div', 'pp-cart__line-price');
      // Show the struck-through original only when a discount actually applied.
      if (item.original_line_price > item.final_line_price) {
        priceWrap.appendChild(el('span', 'pp-cart__line-was', money(item.original_line_price)));
      }
      /* The free gift's price slot says FREE, not $0.00 — a zero price reads
         as an error, FREE reads as the offer (Tyler 2026-08-26). Same green
         as the delivery row's Free. Only the gift line: any other zero-priced
         line would be a bug we WANT to look wrong. */
      /* isFreeGiftLine, not a raw properties read: renderLines works on
         GROUPED items, which carry `lines` rather than `properties`, so the
         direct check never matched and the gift priced as "$0.00" — the exact
         zero-reads-as-an-error case this branch exists to avoid. */
      if (item.final_line_price === 0 && isFreeGiftLine(item)) {
        priceWrap.appendChild(el('strong', 'pp-cart__sum-free',
          S('line_free_label', 'FREE')));
      } else {
        priceWrap.appendChild(el('span', null, money(item.final_line_price)));
      }
      bottom.appendChild(priceWrap);

      info.appendChild(bottom);
      main.appendChild(info);
      line.appendChild(main);

      // Full-width footer of the SAME card, so the note reads as belonging to
      // this bouquet rather than floating beneath the row.
      if ((CFG.gift_note || {}).enabled && (CFG.gift_note || {}).variant_id
          && giftNoteEligible(item)) {
        line.appendChild(renderLineNote(item));
      }

      parent.appendChild(line);
    });
  }

  /**
   * The card's note footer: every message already attached to this product,
   * plus an invitation to note another unit while any remain un-noted.
   *
   * Buying 3 and noting 1 shows ONE card with one message listed and "2 more
   * without a note" — the Shopify line split stays completely invisible.
   */
  /* Only lines the note is TRUE of get the offer (Tyler 2026-08-27: Forever
     Friends showed "Add personal message \$5.00" for a card they don't
     include). Empty/missing list = old behaviour, so a stale config cannot
     silently kill the feature on bouquets. */
  function giftNoteEligible(item) {
    var ids = (CFG.gift_note || {}).eligible_product_ids;
    if (!ids || !ids.length) return true;
    for (var i = 0; i < ids.length; i++) {
      if (String(ids[i]) === String(item.product_id)) return true;
    }
    return false;
  }

  function renderLineNote(group) {
    var g = CFG.gift_note || {};
    var wrap = el('div', 'pp-cart__note' + (group.notes.length ? ' has-note' : ''));

    group.notes.forEach(function (n, idx) {
      var row = el('div', 'pp-cart__note-row');
      var head = el('div', 'pp-cart__note-head');
      head.appendChild(icon(ICON.card, 'pp-cart__note-icon'));
      head.appendChild(el('span', 'pp-cart__note-label',
        group.quantity > 1
          ? (S('note_on_label', 'Gift note')) + ' ' + (idx + 1)
          : (S('note_on_label', 'Gift note'))));
      if (n.quantity > 1) {
        head.appendChild(el('span', 'pp-cart__note-count',
          '· on ' + n.quantity + ' of them'));
      }
      /* Keep the charge visible after saving. The price showed on the "add"
         button and then vanished once the note existed, so a shopper who
         glanced back had no reminder of what the $5 on their total was for —
         a small thing that reads as a surprise charge at checkout. */
      if (g.price != null) {
        head.appendChild(el('span', 'pp-cart__note-price',
          money(g.price * (n.quantity || 1))));
      }
      row.appendChild(head);

      /* Notes can run to 300 characters across several paragraphs. Printing
         them in full pushed the checkout button off-screen — the message is
         reassurance that it saved, not something to re-read in the cart. Show
         a one-line preview and let them expand it. */
      var full = n.text;
      var oneLine = full.replace(/\s+/g, ' ').trim();
      var LIMIT = 58;
      var isLong = oneLine.length > LIMIT;
      var textEl = el('div', 'pp-cart__note-text' + (isLong ? ' is-clamped' : ''),
        isLong ? oneLine.slice(0, LIMIT).trim() + '…' : oneLine);
      row.appendChild(textEl);

      /* ONE action row: View note · Edit · Remove.
         `acts` is built before the toggle so the toggle can join it. These used
         to be siblings in a plain block row — fine while "View note" was a
         borderless text link that flowed like text, but once all three became
         bordered controls the toggle claimed a line of its own and the row
         stacked (Tyler, on both mobile and desktop). They are three peers doing
         the same kind of job; they belong on the same line. */
      var acts = el('div', 'pp-cart__note-acts');

      if (isLong) {
        var toggle = el('button', 'pp-cart__note-link', S('note_view_cta', 'View note'));
        toggle.type = 'button';
        var expanded = false;
        toggle.addEventListener('click', function () {
          expanded = !expanded;
          textEl.textContent = expanded ? full : oneLine.slice(0, LIMIT).trim() + '…';
          textEl.classList.toggle('is-clamped', !expanded);
          textEl.classList.toggle('is-full', expanded);
          toggle.textContent = expanded
            ? (S('note_hide_cta', 'Hide'))
            : (S('note_view_cta', 'View note'));
        });
        acts.appendChild(toggle);
      }

      var edit = el('button', 'pp-cart__note-link', S('note_edit_cta', 'Edit'));
      edit.type = 'button';
      edit.addEventListener('click', function () { openLineNoteEditor(group, wrap, n.line); });
      var del = el('button', 'pp-cart__note-link pp-cart__note-link--del', S('note_remove_cta', 'Remove'));
      del.type = 'button';
      del.addEventListener('click', function () {
        var line = n.line;
        // Snapshot FIRST — the optimistic paint below mutates the in-memory
        // line, which would otherwise leave the sync unable to identify it.
        // Removing: resolve against the line that HAS the note.
        var snap = snapshotTarget(group, line, true);
        optimisticNote(group, '', line);
        setLineNote(group, '', line, snap)
          .then(function () { render(); })
          .catch(function () {
            showCartError(CFG.err_generic || "Couldn't update your cart");
            return getCart().then(function (c) { cart = c; render(); });
          });
      });
      acts.appendChild(edit);
      acts.appendChild(del);
      row.appendChild(acts);
      wrap.appendChild(row);
    });

    /* Offered on EVERY bouquet now. This used to be withheld from the second
       bouquet onward, because the cart could only carry one message per order
       and advertising an option we'd then refuse is worse than not offering it.
       Wiio confirmed 2026-07-30 that the warehouse can process a card per
       bouquet, reading the per-line detail from the order snapshot — so there
       is nothing left to refuse. */
    if (group.plainQty > 0) {
      var add = el('button', 'pp-cart__note-add');
      add.type = 'button';

      /* A tiny CARD MOCKUP, not the product photo (Tyler 2026-08-02 — this
         deliberately supersedes the earlier one-quiet-line rule). The photo
         experiment failed because a full-colour thumbnail competed with the
         bouquet; this is different: a monochrome, hairline-bordered sketch of
         the printed card itself, with one handwritten line. It shows WHAT THE
         $4.99 BUYS — a physical printed card — which the row's words alone
         never managed. System cursive stack: zero font requests. */
      if (V2) {
        /* v2: a checkbox, not a picture of stationery (Tyler 2026-08-26).
           The row is an opt-in, and a tick box says that in one glyph where
           the drawn card said "here is an image of a card" and left the
           shopper to infer the rest. Presentational only — the whole row is
           already a <button>, so this must stay aria-hidden rather than
           become a second, competing control. */
        var box = el('span', 'pp-note-check');
        box.setAttribute('aria-hidden', 'true');
        add.appendChild(box);
      } else {
        var mock = el('span', 'pp-cart__note-cardmock');
        mock.setAttribute('aria-hidden', 'true');
        mock.appendChild(el('span', 'pp-cart__note-cardmock-line',
          S('note_card_sample', 'Dear Mum,')));
        mock.appendChild(el('span', 'pp-cart__note-cardmock-rule'));
        mock.appendChild(el('span', 'pp-cart__note-cardmock-rule pp-cart__note-cardmock-rule--short'));
        add.appendChild(mock);
      }

      /* Title only. The description that used to sit under it says what the
         thing IS — printed on the blank card that already comes with the
         bouquet — and that is an answer to a question nobody has asked yet.
         It still gets told, one tap later: openLineNoteEditor renders the
         same `g.description` as `.pp-cart__note-desc` above the textarea, at
         the moment they've shown interest and are deciding what to write. */
      /* "Add personal message" (Tyler 2026-08-26) — supersedes the longer
         2026-08-02 wording ("Print a message on the note card", chosen so
         "add" wouldn't read as buying a second object): the compactness of
         the cart won this trade, especially on phones. The editor's
         description still explains the printed-card mechanics one tap
         later, which is what keeps the short label honest. */
      add.appendChild(el('span', 'pp-cart__note-add-title',
        group.notes.length
          ? (S('note_add_another_cta', 'Add another message')) +
            ' (' + group.plainQty + ')'
          : (group.quantity > 1
              ? (S('note_add_one_cta', 'Add personal message'))
              : (S('note_add_cta', 'Add personal message')))));

      if (g.price != null) {
        add.appendChild(el('span', 'pp-cart__note-price', money(g.price)));
      }
      add.addEventListener('click', function () { openLineNoteEditor(group, wrap); });
      wrap.appendChild(add);

      /* The "Writing for…" chip row that briefly lived here was PULLED the
         same day it shipped (Tyler 2026-08-02): a permanent extra line per
         product card — two lines on mobile where the chips wrapped — for an
         offer the shopper hasn't shown interest in yet. The one-quiet-line
         rule stands after all. The chips' job (answer "who's it for" in one
         tap) moves INSIDE the tap — see the openLineNoteEditor opts fast
         lane, which is kept for that redesign. */
    }
    return wrap;
  }

  /**
   * Apply a new TOTAL quantity to a grouped product.
   *
   * A group may sit across several real cart lines (see groupedLines). We walk
   * them and allocate the new total, so removing 2 of 5 takes from one line
   * rather than blowing away properties Shopify is tracking on the others.
   * Returns the sequence of per-line updates to perform.
   */
  function allocateGroupQty(group, next) {
    var updates = [];
    var remaining = next;
    /* Reduce UN-NOTED units first. Someone who wrote a message for their mum
       and then lowered the quantity should not silently lose that message —
       the plain units are the ones they meant to drop. Removing everything
       (next === 0) clears both, which is what the bin means. */
    var ordered = (group.lines || []).slice().sort(function (a, b) {
      return (lineNote(b) ? 1 : 0) - (lineNote(a) ? 1 : 0);
    });
    ordered.forEach(function (l) {
      var give = Math.min(l.quantity, Math.max(0, remaining));
      remaining -= give;
      if (give !== l.quantity) updates.push({ id: l.key, quantity: give });
    });
    // Wanted more than the lines currently hold — top up the first line.
    if (remaining > 0 && group.lines && group.lines.length) {
      var first = group.lines[0];
      var target = first.quantity + remaining;
      var existing = updates.filter(function (u) { return u.id === first.key; })[0];
      if (existing) existing.quantity = target;
      else updates.push({ id: first.key, quantity: target });
    }
    return updates;
  }

  // ─────────────── loss-aversion removal guard ────────────────
  /**
   * When a removal would UN-EARN something — drop the eligible count below a
   * discount tier, or the subtotal under the free-delivery threshold — the tap
   * gets one honest beat of friction: a small popover naming exactly what goes
   * away, with keeping it as the primary action. Painless removals stay
   * one-tap; a guard that fires on every tap trains the shopper to ignore it.
   *
   * Perks are NAMED, not priced. What the discount is worth after Shopify
   * recomputes the changed cart is not knowable before the change, and the
   * footer already prices the current state — same discipline as the delivery
   * figure: no claims about money we can't verify.
   */
  function itemCountsToward(tier, item) {
    var scope = tier && tier.scope;
    if (!scope || scope.all) return !isMechanismLine(item);
    var ok = false;
    (scope.collections || []).forEach(function (h) {
      ((REWARDS.collection_products || {})[h] || []).forEach(function (pid) {
        if (String(pid) === String(item.product_id)) ok = true;
      });
    });
    if (item.handle && (scope.products || []).indexOf(item.handle) > -1) ok = true;
    return ok;
  }

  function removalLoss(item, nextQty) {
    if (!cart || isMechanismLine(item)) return null;
    var drop = item.quantity - Math.max(0, nextQty);
    if (drop <= 0) return null;
    var perks = [];
    (REWARDS.quantity_tiers || []).forEach(function (t) {
      if (!itemCountsToward(t, item)) return;
      var eligible = eligibleItemCount(t);
      if (eligible >= t.min_qty && eligible - drop < t.min_qty) {
        perks.push(t.percent
          ? S('percent_off', '{percent}% off').replace('{percent}', t.percent)
          : t.title || S('a_discount', 'a discount'));
      }
    });
    /* Value thresholds compare against the line's discounted unit price — the
       most conservative honest estimate of how far the subtotal falls. A
       compound case (quantity tier ALSO collapsing pushes it further) still
       guards via the tier perk above. */
    var unit = item.quantity > 0 ? Math.round(item.final_line_price / item.quantity) : 0;
    var after = cart.total_price - unit * drop;
    var thresh = REWARDS.shipping_threshold_minor;
    if (thresh != null && cart.total_price >= thresh && after < thresh) {
      perks.push((CFG.reward_labels && CFG.reward_labels.shipping) || 'free delivery');
    }
    (REWARDS.value_tiers || []).forEach(function (t) {
      if (cart.total_price >= t.min_subtotal_minor && after < t.min_subtotal_minor) {
        perks.push(t.percent
          ? S('percent_off', '{percent}% off').replace('{percent}', t.percent)
          : t.title || S('a_discount', 'a discount'));
      }
    });
    return perks.length ? { perks: perks } : null;
  }

  var lossPopEl = null;
  function dismissLossPop() {
    if (!lossPopEl) return;
    lossPopEl.parentNode && lossPopEl.parentNode.removeChild(lossPopEl);
    lossPopEl = null;
    d.removeEventListener('click', lossPopOutside, true);
  }
  function lossPopOutside(e) {
    if (lossPopEl && !lossPopEl.contains(e.target)) dismissLossPop();
  }
  function confirmLoss(btn, item, loss, proceed) {
    dismissLossPop();
    var host = panelEl || d.body;
    var pop = el('div', 'pp-cart__losspop');
    pop.setAttribute('role', 'alertdialog');
    pop.appendChild(el('p', 'pp-cart__losspop-head', S('loss_head', 'Removing this loses')));
    var chips = el('div', 'pp-cart__losspop-chips');
    loss.perks.forEach(function (perk) {
      chips.appendChild(el('b', 'pp-cart__losspop-chip', perk));
    });
    pop.appendChild(chips);
    var keep = el('button', 'pp-cart__losspop-keep', S('loss_keep', 'Keep my savings'));
    keep.type = 'button';
    keep.addEventListener('click', function () {
      dismissLossPop();
      // `kept` rides in props, NOT fields: unknown top-level keys are
      // silently discarded by the collector's column mapping.
      track.emit('cart_loss_guard', {
        product_id: item.product_id
      }, { kept: true, perks: loss.perks.join(' + '), clarity_tag: 'loss_guard_kept' });
    });
    pop.appendChild(keep);
    var rem = el('button', 'pp-cart__losspop-remove', S('loss_remove', 'Remove anyway'));
    rem.type = 'button';
    rem.addEventListener('click', function () {
      dismissLossPop();
      track.emit('cart_loss_guard', {
        product_id: item.product_id
      }, { kept: false, perks: loss.perks.join(' + '), clarity_tag: 'loss_guard_removed' });
      proceed();
    });
    pop.appendChild(rem);
    host.appendChild(pop);
    lossPopEl = pop;   // registered BEFORE positioning — dismiss paths key off this
    var pr = host.getBoundingClientRect();
    var br = btn.getBoundingClientRect();
    var w = pop.offsetWidth, h = pop.offsetHeight;
    var x = Math.min(Math.max(10, br.left - pr.left + br.width / 2 - w / 2), pr.width - w - 10);
    var above = br.top - pr.top - h - 10 >= 8;
    var y = above ? br.top - pr.top - h - 10 : br.bottom - pr.top + 10;
    pop.className += above ? ' is-above' : ' is-below';
    pop.style.left = x + 'px';
    pop.style.top = y + 'px';
    pop.style.setProperty('--pp-losspop-cx',
      Math.min(Math.max(16, br.left - pr.left + br.width / 2 - x), w - 16) + 'px');
    try { keep.focus(); } catch (e) {}
    setTimeout(function () { d.addEventListener('click', lossPopOutside, true); }, 0);
    track.emit('cart_loss_guard_shown', { product_id: item.product_id },
      { perks: loss.perks.join(' + '), clarity_tag: 'loss_guard_shown' });
  }

  function changeQty(item, next) {
    next = Math.max(0, next);
    var evt = next === 0 ? 'cart_line_remove'
            : next > item.quantity ? 'cart_line_add' : 'cart_line_qty';
    track.emit(evt, {
      product_id: item.product_id, variant_id: item.variant_id,
      qty: next, value: item.final_price / 100, currency: CFG.currency
    }, { from: item.quantity, to: next, title: item.product_title });

    /* SPEED PATH (Tyler 2026-08-03 "the cart must function super fast" +
       a live 429 from removing two items back-to-back):
       1. OPTIMISTIC ROW — the tapped line reacts instantly (collapses on
          remove, count updates on +/-) while the money region keeps the
          is-busy treatment until the authoritative /cart.js read lands. No
          provisional totals are ever printed — the truth rules stand.
       2. COALESCING — one in-flight mutation per line. More taps while it
          flies just move the target; when the flight lands, one follow-up
          request applies the LATEST target. Three fast clicks = 2 requests,
          not 9 — which is also the 429 fix.
       3. LEAN CHAIN — the mid-chain /cart.js re-read only happens when the
          gift-charge sync actually changed the cart. */
    optimisticLine(item, next);
    var ck = String(item.key || item.variant_id);
    _qtyTarget[ck] = next;
    if (_qtyInflight[ck]) return;
    _qtyInflight[ck] = true;
    setBusy(true);
    sendQty(item, ck);
  }

  var _qtyInflight = {};
  var _qtyTarget = {};

  function optimisticLine(item, next) {
    if (!root) return;
    var row = root.querySelector(
      '.pp-cart__line[data-line-key="' + String(item.key || item.variant_id) + '"]');
    if (!row) return;
    if (next === 0) {
      row.classList.add('is-removing');
    } else {
      var cnt = row.querySelector('.pp-cart__qty-count');
      if (cnt) cnt.textContent = String(next);
    }
  }

  function sendQty(item, ck) {
    var target = _qtyTarget[ck];
    // A grouped product may span several real lines; apply them in sequence.
    var updates = item.lines
      ? allocateGroupQty(item, target)
      : [{ id: item.key, quantity: target }];

    // adoptCart re-reads /cart.js rather than trusting these responses — see
    // its docstring for the measurement that forced that.
    updates.reduce(function (chain, u) {
      return chain.then(function () {
        return api('/cart/change.js', { id: u.id, quantity: u.quantity });
      });
    }, Promise.resolve())
      .then(adoptCart)
      .then(syncChargeOnly)
      .then(function (res) { return res && res.acted ? adoptCart() : cart; })
      /* The gift has to settle on EVERY quantity change, not just on add.
         Dropping from 4 bouquets to 3 is exactly when the discount stops
         applying, and a gift left behind at that moment is one the shopper
         silently pays for. */
      /* Same rule as the upsell add: a gift that cannot be added or removed
         must not turn a successful quantity change into an error toast. */
      .then(function (c) {
        return syncFreeGift().catch(function () { return c; });
      })
      .then(function () {
        if (_qtyTarget[ck] !== target && _qtyTarget[ck] !== undefined) {
          // She kept tapping while we flew — apply the latest target now.
          sendQty(item, ck);
          return;
        }
        delete _qtyInflight[ck];
        delete _qtyTarget[ck];
        render();
        scheduleNoteSummary();
        setBusy(false);
      })
      .catch(function (err) {
        delete _qtyInflight[ck];
        delete _qtyTarget[ck];
        showCartError(err && err.status === 429
          ? (S('err_busy', 'Too many requests — give it a moment'))
          : (CFG.err_generic || "Couldn't update your cart"));
        return getCart().then(function (c) { cart = c; render(); })
          .catch(function () {})
          .then(function () { setBusy(false); });
      });
  }

  /* Visible, self-clearing failure notice. A cart that silently ignores a click
     is the worst outcome — the shopper assumes the site is broken and leaves. */
  function showCartError(msg) {
    if (!root) return;
    var host = root.querySelector('.pp-cart__body');
    if (!host) return;
    var old = host.querySelector('.pp-cart__error');
    if (old) old.remove();
    var n = el('div', 'pp-cart__error', msg);
    n.setAttribute('role', 'status');
    host.insertBefore(n, host.firstChild);
    setTimeout(function () { if (n.parentNode) n.remove(); }, 5000);
  }

  function setBusy(on) {
    if (!root) return;
    root.classList.toggle('is-busy', !!on);
  }

  // ──────────────── per-bouquet gift notes ─────────────────────────
  /*
   * A message belongs to ONE bouquet, not to the order.
   *
   * Previously the message lived on `cart.note` — order level — so when someone
   * bought three bouquets the warehouse had no way to know which one the card
   * was for. They weren't guessing carelessly; the data to do better did not
   * exist. Attaching the message to the line makes it knowable, and Shopify
   * surfaces line properties on the order and packing slip natively.
   *
   * Adding a note to one of several identical bouquets SPLITS the line: the
   * original drops by one and a new single-unit line carries the message.
   * Shopify keeps them separate automatically because their properties differ.
   */
  /**
   * Attach (or clear) a message on ONE unit of a product.
   *
   * SPEED MATTERS MORE THAN ELEGANCE HERE. The first version chained six
   * sequential round trips — change → add → getCart → charge-sync → getCart →
   * note-summary → getCart — and every one of them was individually retryable
   * with backoff. That's how a save took ~30 seconds. Two things fix it:
   *
   *   1. /cart/change.js and /cart/add.js BOTH return the updated cart, so the
   *      getCart() calls between them were pure waste.
   *   2. the $5 charge is folded into the SAME add.js call, and the cart.note
   *      summary (which exists only for Flow compatibility) is fired in the
   *      BACKGROUND after the UI has already updated.
   *
   * Result: one or two blocking requests instead of six.
   *
   * @param targetLine optional specific line to act on; defaults to a plain
   *                   (un-noted) unit so a new note never overwrites an
   *                   existing one.
   */
  /**
   * Re-resolve a line against the CURRENT cart.
   *
   * A Shopify line key encodes its properties, so editing any note rewrites
   * that line's key — and the background cart.note mirror rewrites the cart
   * again a moment later. A key captured when the row was rendered can
   * therefore be stale by the time the shopper clicks, and change.js answers a
   * stale key with a 4xx. That's why removing a SECOND note failed while the
   * first worked: the first edit invalidated the keys the DOM was holding.
   *
   * Matching on variant + message text instead of the key makes the action
   * immune to how many times the cart has been rewritten since render.
   */
  function resolveLine(line) {
    if (!line || !cart) return null;
    var wantNote = lineNote(line);
    var exact = null, sameVariant = null;
    cart.items.forEach(function (i) {
      if (String(i.variant_id) !== String(line.variant_id)) return;
      if (!sameVariant) sameVariant = i;
      if (!exact && lineNote(i) === wantNote) exact = i;
    });
    return exact || sameVariant;
  }

  /**
   * Apply a note change to the in-memory cart and repaint IMMEDIATELY, before
   * the network is involved.
   *
   * Trimming request count got a save from ~30s to ~10s, but 10s of staring at
   * a spinner still loses shoppers. The cart API is simply not fast enough to
   * wait on, so we stop waiting: the shopper sees the result at once and the
   * server catches up behind them. If the sync fails we re-read and repaint,
   * so the display can't drift from the truth for long.
   *
   * Deliberately mirrors what setLineNote does server-side, including the
   * split, so the optimistic view matches the eventual real cart.
   */
  /**
   * The line an edit should act on — with the INTENT declared.
   *
   * The fallback chain used to be add-shaped everywhere: when the direct
   * lookup degraded it picked `!lineNote(l)`, a line with NO note. That is
   * right for attaching a message and exactly backwards for removing one.
   * A removal that landed on an un-noted line cleared a note that wasn't
   * there, left the real one untouched on the server, and produced the
   * reported flicker — the message vanishes on the optimistic paint, the
   * re-read brings it straight back, and a second click (by which point the
   * cart has been refreshed and the row re-bound) finally works.
   *
   * `resolveLine` alone can't prevent this: it falls back to `sameVariant`,
   * so it answers with SOME line of that variant rather than none, and the
   * caller can't tell a real match from a shrug.
   *
   * `wantNoted` makes the requirement explicit, and it is checked against the
   * direct hit too — a direct resolve that came back un-noted when we are
   * removing is precisely the mis-resolve this exists to catch.
   */
  function pickNoteLine(group, targetLine, wantNoted) {
    var lines = ((group && group.lines) || []).map(resolveLine)
      .filter(function (l) { return !!l; });
    var direct = targetLine && resolveLine(targetLine);
    if (direct && (!!lineNote(direct)) === !!wantNoted) return direct;
    var match = lines.filter(function (l) {
      return (!!lineNote(l)) === !!wantNoted;
    })[0];
    /* Prefer a line matching the intent; only then the direct hit, and only
       then anything at all. Removal never silently retargets an un-noted
       line while a noted one is sitting right there. */
    return match || direct || lines[0] || null;
  }

  function optimisticNote(group, text, targetLine) {
    if (!cart) return;
    var trimmed = (text || '').trim();
    var g = CFG.gift_note || {};
    // Clearing wants the line that HAS the note; setting wants a free one.
    var target = pickNoteLine(group, targetLine, !trimmed);
    if (!target) return;

    if (target.quantity > 1 && trimmed && !lineNote(target)) {
      // Peel one unit off, exactly as the server will.
      target.quantity -= 1;
      target.final_line_price = target.final_price * target.quantity;
      target.original_line_price = (target.original_price || target.final_price) * target.quantity;
      var clone = JSON.parse(JSON.stringify(target));
      clone.quantity = 1;
      clone.key = target.key + ':pending';
      clone.final_line_price = clone.final_price;
      clone.original_line_price = clone.original_price || clone.final_price;
      clone.properties = mergeProps(target.properties, trimmed);
      cart.items.push(clone);
    } else {
      target.properties = mergeProps(target.properties, trimmed);
    }

    // Keep the $5 line in step so the total doesn't visibly jump afterwards.
    if (g.variant_id) {
      var want = notedUnitCount();
      var charge = giftChargeLine();
      if (charge) {
        charge.quantity = want;
        charge.final_line_price = charge.final_price * want;
        charge.original_line_price = (charge.original_price || charge.final_price) * want;
        if (want === 0) {
          cart.items = cart.items.filter(function (i) { return i !== charge; });
        }
      } else if (want > 0 && g.price != null) {
        cart.items.push({
          key: 'pending-gift-note',
          variant_id: g.variant_id,
          product_id: null,
          product_title: S('note_product_title', 'Personal gift note'),
          variant_title: null,
          quantity: want,
          final_price: g.price,
          original_price: g.price,
          final_line_price: g.price * want,
          original_line_price: g.price * want,
          properties: {}
        });
      }
    }
    recalcCartTotals();
    render();
  }

  /* Recompute the headline figures after an optimistic edit. Approximate by
     design — the authoritative numbers arrive with the server's cart moments
     later — but close enough that nothing visibly lurches. */
  function recalcCartTotals() {
    if (!cart) return;
    var orig = 0, final = 0, count = 0;
    cart.items.forEach(function (i) {
      orig += i.original_line_price || i.final_line_price || 0;
      final += i.final_line_price || 0;
      count += i.quantity || 0;
    });
    cart.original_total_price = orig;
    cart.total_price = final;
    cart.item_count = count;
  }

  /**
   * Snapshot the line we're about to act on, BEFORE any optimistic edit.
   *
   * This exists because optimistic rendering broke removal. optimisticNote()
   * strips the message from the in-memory line, so by the time setLineNote ran
   * and asked resolveLine to find "the line whose note is X", X had already
   * become "". It then matched the first UN-NOTED line of that variant — a
   * different line entirely — and dutifully cleared a note that wasn't there,
   * leaving the real one untouched on the server. The note vanished, the
   * re-read brought it back.
   *
   * Capturing the server key and quantity up front removes the guesswork.
   */
  function snapshotTarget(group, targetLine, wantNoted) {
    var resolved = pickNoteLine(group, targetLine, wantNoted);
    if (!resolved) return null;
    return {
      key: resolved.key,
      quantity: resolved.quantity,
      properties: JSON.parse(JSON.stringify(resolved.properties || {})),
      hadNote: !!lineNote(resolved)
    };
  }

  /**
   * Has the note write we just made actually shown up in a cart READ?
   *
   * After a set, some line of the cart carries the new text. After a clear, no
   * line still carries the old one. Keyed on the TEXT, not the line key,
   * because clearing a property re-keys the line server-side.
   */
  function noteWriteLanded(snap, trimmed) {
    var items = (cart && cart.items) || [];
    if (trimmed) {
      return items.some(function (i) { return lineNote(i) === trimmed; });
    }
    var old = ((snap && snap.properties) || {})[NOTE_PROP] || '';
    if (!old) return true;
    return !items.some(function (i) { return lineNote(i) === old; });
  }

  /**
   * adoptCart(), but don't move on until the read agrees with the write.
   *
   * `adoptCart` discards the mutation response and issues its own GET
   * /cart.js, and that read can lag the write it follows. A stale read here is
   * not cosmetic: syncChargeOnly() decides FROM `cart` whether the $5 note line
   * still needs to move, so a cart that still shows the note makes it conclude
   * there is nothing to do — the charge is never removed and the shopper is
   * left paying for a note they deleted. The same stale read is what painted
   * the note back after it had visibly gone (Tyler 2026-08-04: "it comes back
   * and stays, then a second or 2 later the note then gets removed" — that
   * last step was a later refresh finally seeing the truth).
   *
   * Costs nothing in the normal case: the first read almost always agrees and
   * this returns immediately. Bounded at three extra reads so a genuinely
   * unreachable state can never hang the drawer — on giving up we proceed with
   * whatever we have, which is the pre-existing behaviour, not worse.
   */
  function adoptSettled(snap, trimmed, tries) {
    tries = (tries == null) ? 5 : tries;
    return adoptCart().then(function (c) {
      if (noteWriteLanded(snap, trimmed)) return c;
      if (tries <= 0) {
        /* The read never caught up inside the budget. Do NOT hand back a cart
           that still shows a note the shopper deleted — that is the reappear
           they reported. Re-assert the edit locally: it is what they asked
           for, the write already succeeded, and the next natural refresh
           reconciles. Only the note property is touched; the charge line is
           syncChargeOnly's job and has already run against a correct cart. */
        var old = ((snap && snap.properties) || {})[NOTE_PROP] || '';
        (cart.items || []).forEach(function (i) {
          if (!trimmed && old && lineNote(i) === old && i.properties) {
            delete i.properties[NOTE_PROP];
          }
        });
        return cart;
      }
      return new Promise(function (res) { setTimeout(res, 300); })
        .then(function () { return adoptSettled(snap, trimmed, tries - 1); });
    });
  }

  /**
   * `/cart/change.js` against a line key we captured earlier.
   *
   * A line's key is a hash of its PROPERTIES, so any write to that line mints
   * a new key and retires the old one — including our own previous save.
   * Shopify answers a retired key with `400 {"message": "no valid id or line
   * parameter"}`, which reads like a malformed request and is nothing of the
   * kind. Tagged here so setLineNote can re-resolve and retry instead of
   * showing the shopper a dead end.
   */
  function changeKeyed(body) {
    return api('/cart/change.js', body).catch(function (err) {
      if (err && (err.status === 400 || err.status === 404)) err.staleKey = true;
      throw err;
    });
  }

  /**
   * Write (or clear) the note, surviving a line key that went stale mid-edit.
   *
   * THE SNAPSHOT IS THE POINT AND ALSO THE HAZARD. snapshotTarget captures the
   * server key up front so an optimistic repaint can't make us act on the
   * wrong line — see its comment. But a key captured at render time is a bet
   * that nothing touches that line before Save, and two ordinary things break
   * that bet:
   *
   *   1. A SECOND CLICK ON SAVE. Nothing disabled the button while the first
   *      request was in flight, so an impatient double-tap sent the same
   *      snapshot twice — the first call re-keyed the line, the second got a
   *      400. This is the common one: the shopper sees "Couldn't save your
   *      note" for a note that saved perfectly a moment earlier.
   *   2. The cart moving underneath an open editor — a second tab, or any
   *      background write to that line.
   *
   * Both are now recoverable: on a stale key, re-read the cart, re-resolve the
   * line, and try once more. Retried ONLY when the tagged first call failed,
   * because the multi-step branches below have already mutated the cart by the
   * time their later requests run and replaying those would double-apply.
   */
  function setLineNote(group, text, targetLine, snap) {
    var trimmed = (text || '').trim();
    var target = snap || snapshotTarget(group, targetLine, !trimmed);
    if (!target) return Promise.resolve(cart);
    return setLineNoteOnce(group, text, targetLine, target)
      .catch(function (err) {
        if (!err || !err.staleKey) throw err;
        /* Re-resolve against the SERVER's current cart, not the optimistic
           one — the whole reason we're here is that our copy is behind. */
        return getCart().then(function (c) {
          cart = c;
          var fresh = snapshotTarget(group, targetLine, !trimmed);
          if (!fresh || fresh.key === target.key) throw err;
          /* Already what the shopper asked for — a duplicate click whose
             first request won. Treat it as done rather than writing it twice. */
          if ((lineNote(fresh) || '').trim() === trimmed) return cart;
          try { console.warn('[pp-cart] line key went stale, retrying'); } catch (e) {}
          return setLineNoteOnce(group, text, targetLine, fresh);
        });
      });
  }

  function setLineNoteOnce(group, text, targetLine, target) {
    var trimmed = (text || '').trim();
    var g = CFG.gift_note || {};

    /* The one-message-per-order refusal lived here. It existed because the
       warehouse printed the ORDER NOTE straight onto a card, which left a
       second message nowhere safe to go — concatenated, both printed on one
       card; labelled, the labels printed too.

       Lifted 2026-07-30. Shaylin at Wiio confirmed the warehouse can handle a
       card per bouquet and will read the quantity and content per line from the
       order snapshot, with the order note carrying only a pointer to it. The
       properties were always written per line for exactly this moment, so this
       is the deletion the old comment promised rather than a rebuild. */
    var hadNote = target.hadNote;
    // How the $5 line must move as a result of this edit.
    var chargeDelta = (trimmed ? 1 : 0) - (hadNote ? 1 : 0);

    var chain;
    if (target.quantity > 1 && trimmed && !hadNote) {
      // Peel one unit off and give the message to that one, adding the $5
      // charge in the same request rather than a follow-up round trip.
      var items = [{
        id: group.variant_id, quantity: 1, properties: buildNoteProps(trimmed)
      }];
      var chargeQty = giftChargeQty() + chargeDelta;
      chain = changeKeyed({ id: target.key, quantity: target.quantity - 1 })
        .then(function () {
          if (g.variant_id && chargeDelta > 0 && !giftChargeLine()) {
            items.push({ id: g.variant_id, quantity: chargeQty });
          }
          return api('/cart/add.js', { items: items });
        });
    } else if (!trimmed && hadNote) {
      /* REMOVAL: drop the line and re-add it clean, rather than editing the
         properties in place.

         Editing properties to clear a note went through five rounds and never
         held: an omitted key is a silent no-op when the object ends up empty,
         a blank value leaves a real empty property behind, and either way the
         result depends on how Shopify chooses to interpret a properties patch
         AND on a follow-up read landing after the write. Deleting a line is
         the least ambiguous operation the AJAX cart API has — quantity 0 means
         gone, with nothing to interpret — and re-adding gives a line that
         provably has no note because it was never given one.

         Everything else about the line is carried across: the full quantity,
         and every property except the note (Kaching's `__kaching_bundles`
         included, which is why this merges rather than dropping properties
         wholesale). The re-added line lands at the end of the cart, which the
         drawer already renders as first-added-first, so it reads as the same
         row rather than a new one. */
      var keep = {};
      var srcProps = target.properties || {};
      for (var pk in srcProps) {
        if (Object.prototype.hasOwnProperty.call(srcProps, pk) && pk !== NOTE_PROP) {
          keep[pk] = srcProps[pk];
        }
      }
      var reAdd = { id: group.variant_id, quantity: target.quantity };
      if (Object.keys(keep).length) reAdd.properties = keep;
      chain = changeKeyed({ id: target.key, quantity: 0 })
        .then(function () { return api('/cart/add.js', { items: [reAdd] }); });
    } else {
      // Rewrite properties in place. change.js replaces the whole properties
      // object, so MERGE — Kaching stores __kaching_bundles here and wiping it
      // would break their bundle tracking.
      chain = changeKeyed({
        id: target.key,
        quantity: target.quantity,
        properties: mergeProps(target.properties, trimmed)
      });   // target is a SNAPSHOT: server key + pre-edit properties
    }

    return chain
      /* Adopt the MUTATION RESPONSE, not a fresh GET. change.js returns the
         authoritative post-write cart with zero replication lag, and this
         value is used for exactly one decision — syncChargeOnly reading
         whether the note line still needs to move. Re-reading here is what
         let a lagging GET tell syncChargeOnly "nothing to do" and leave the
         shopper charged for a deleted note. (The documented reason not to
         trust a mutation response is its TOTALS under discounts; totals are
         refreshed by the settled read below, after both writes.) */
      .then(function (c) {
        var real = asCart(c);
        if (real) { cart = real; return cart; }
        /* `/cart/add.js` with an items array answers with the added items, not
           a cart, so the removal path lands here. syncChargeOnly MUST NOT run
           against the optimistic cart — it would see the note already gone,
           conclude there is nothing to do, and leave the charge line behind
           server-side. A settled read is the only safe input. */
        return adoptSettled(target, trimmed);
      })
      .then(syncChargeOnly)
      /* Only this read refreshes totals, and it is the one the shopper sees.
         It must be SETTLED: a lagging GET here is what painted the deleted
         note back for a second or two before a later refresh corrected it. */
      .then(function (res) {
        return res && res.acted ? adoptSettled(target, trimmed) : cart;
      })
      .then(function (c) {
        // Flow-compatibility mirror: not on the critical path, so don't make
        // the shopper wait for it.
        scheduleNoteSummary();
        return c;
      });
  }

  /* ───────────────────── free gift (engine-chosen) ─────────────────────
   * "Buy N full-size bouquets, get a mini free."
   *
   * The mini is NOT chosen by the shopper and NOT fixed by us. It's picked by
   * the same upsell engine that drives the rail, from whatever the cart
   * already holds — 23 of 25 anchors already rank at least one mini, and the
   * strongest pairings are the matching design (a Kakadu Embrace buyer takes
   * the mini Kakadu Embrace).
   *
   * Two problems solved by that one decision:
   *   · No picker. A chooser inside the drawer is friction at the exact moment
   *     the shopper has decided to buy, and it needs out-of-stock handling,
   *     a change-your-mind path and a mobile layout.
   *   · No inventory hole. The last time PetalPal ran this through Qikify the
   *     gift was a single preselected mini and it drained that one SKU fast.
   *     Different carts now earn different minis, so demand spreads by
   *     construction rather than by someone remembering to rotate it.
   *
   * Shopify makes it free, not us: a Buy-X-Get-Y automatic discount zeroes the
   * line. The cart's only job is to make sure the right line is present when
   * eligible and gone when not — because if the shopper drops below the
   * threshold Shopify stops discounting and they would be CHARGED for a gift
   * they never asked for. That sync is the whole risk here, and it's the same
   * shape as syncChargeOnly() keeping the $5 note line in step.
   */
  function giftCfg() { return CFG.free_gift || {}; }

  function giftLine() {
    var g = giftCfg();
    if (!cart || !g.enabled) return null;
    var found = null;
    cart.items.forEach(function (i) {
      if (i.properties && i.properties[g.prop || '_free_gift']) found = i;
    });
    return found;
  }

  /** Full-size units that count toward earning the gift. */
  function giftQualifyingQty() {
    var g = giftCfg();
    if (!cart) return 0;
    /* Own product list, not REWARDS.collection_products — that map only holds
       collections referenced by a quantity tier, and the gift earns off
       Signature Bouquets, which isn't one. Empty means we can't verify
       eligibility, and we don't promise what we can't verify. */
    var allowed = {};
    (g.earn_products || []).forEach(function (pid) { allowed[String(pid)] = true; });
    if (!Object.keys(allowed).length) return 0;
    return cart.items.reduce(function (n, i) {
      if (isMechanismLine(i)) return n;
      if (i.properties && i.properties[g.prop || '_free_gift']) return n;  // the gift itself never earns itself
      return n + (allowed[String(i.product_id)] ? i.quantity : 0);
    }, 0);
  }

  function giftEarned() {
    var g = giftCfg();
    var q = giftQualifyingQty();
    /* max_below_qty: the range where the gift's zeroing discount actually
       fires at checkout. Measured 2026-08-26: at the 6-rung the 20%
       automatic claims the buy-side items and the BXGY goes dead — an
       "earned" gift there would be silently CHARGED. Above the cap the
       sync therefore removes the mini and the offer converges on the
       rung's own rewards. */
    if (g.max_below_qty && q >= g.max_below_qty) return false;
    return !!(g.enabled && g.min_qty && q >= g.min_qty);
  }

  /**
   * Which mini to give. Engine first, configured fallback second.
   * Never returns something already in the cart — receiving a duplicate of
   * what you just bought doesn't read as a gift.
   */
  function chooseGift() {
    var g = giftCfg();
    var pool = g.pool || {};                 // handle -> {variant_id, product_id, title, image}
    var handles = Object.keys(pool);
    if (!handles.length) return null;

    var inCart = {};
    cart.items.forEach(function (i) { inCart[String(i.product_id)] = true; });

    var eligible = handles.filter(function (h) {
      return !inCart[String(pool[h].product_id)];
    });
    if (!eligible.length) eligible = handles;   // every mini already held — give one anyway

    // Rank by how often the engine recommends it for what's actually in the cart.
    var recs = (CFG.upsells || {}).recs || {};
    var score = {};
    cart.items.forEach(function (item) {
      if (isMechanismLine(item)) return;
      var list = recs[String(item.product_id)] || [];
      list.forEach(function (h, idx) {
        if (eligible.indexOf(h) === -1) return;
        score[h] = (score[h] || 0) + (list.length - idx);
      });
    });
    /* Ties are common — two minis can easily be recommended equally by the
       same basket — and leaving them to sort order means the gift could differ
       between two identical carts for no reason anyone could explain. Break on
       the handle so the choice is deterministic and reproducible; variety
       still comes from carts differing, which is where it should come from. */
    eligible.sort(function (a, b) {
      var d = (score[b] || 0) - (score[a] || 0);
      return d !== 0 ? d : (a < b ? -1 : a > b ? 1 : 0);
    });

    // A pinned handle wins outright — the lever for a seasonal gift like the
    // Christmas mini, without needing a code change to introduce it.
    if (g.pinned && pool[g.pinned] && eligible.indexOf(g.pinned) !== -1) {
      return pool[g.pinned];
    }
    return pool[eligible[0]] || null;
  }

  /** Add / remove the gift line so it always matches eligibility. */
  function syncFreeGift() {
    /* SERIALIZED: two chains (boot adopt + the drawer-open refresh) used to
       run this concurrently, both saw "earned, no line yet", and both
       added — a merged qty-2 gift line that checkout only zeroes ONE unit
       of, silently charging for the other (measured 2026-08-25 during the
       t1 rehearsal). Concurrent callers now share a single pass; the
       quantity>1 guard below still heals anything that slips through. */
    if (syncFreeGift._p) return syncFreeGift._p;
    syncFreeGift._p = _syncFreeGiftPass().then(
      function (r) { syncFreeGift._p = null; return r; },
      function (e) { syncFreeGift._p = null; throw e; });
    return syncFreeGift._p;
  }
  function _syncFreeGiftPass() {
    var g = giftCfg();
    if (!g.enabled) return Promise.resolve(cart);
    var line = giftLine();
    var earned = giftEarned();

    if (earned && !line) {
      var pick = chooseGift();
      if (!pick) return Promise.resolve(cart);
      var props = {};
      props[g.prop || '_free_gift'] = pick.handle || '1';
      return api('/cart/add.js', {
        items: [{ id: pick.variant_id, quantity: 1, properties: props }]
      }).then(adoptCart);
    }
    if (!earned && line) {
      // Removing is the SAFETY half. Left in place below the threshold the
      // discount stops applying and the shopper silently pays for it.
      return api('/cart/change.js', { id: line.key, quantity: 0 }).then(adoptCart);
    }
    if (earned && line && line.quantity > 1) {
      return api('/cart/change.js', { id: line.key, quantity: 1 }).then(adoptCart);
    }
    return Promise.resolve(cart);
  }

  function giftChargeLine() {
    var g = CFG.gift_note || {};
    var found = null;
    if (cart && g.variant_id) {
      cart.items.forEach(function (i) {
        if (String(i.variant_id) === String(g.variant_id)) found = i;
      });
    }
    return found;
  }
  function giftChargeQty() {
    var l = giftChargeLine();
    return l ? l.quantity : 0;
  }

  /** Bring the $5 line in step with the number of noted bouquets. One request,
   *  and skipped entirely when it's already correct. */
  function syncChargeOnly() {
    /* Resolves with {acted:true} ONLY when it touched the cart — the caller
       skips its follow-up /cart.js read otherwise (the common case; that
       read was a flat 300-500ms tax on every quantity change,
       Tyler 2026-08-03 "cart must function super fast"). */
    var g = CFG.gift_note || {};
    if (!g.variant_id) return Promise.resolve(null);
    var want = notedUnitCount();
    var line = giftChargeLine();
    var have = line ? line.quantity : 0;
    if (want === have) return Promise.resolve(null);
    if (line) {
      return api('/cart/change.js', { id: line.key, quantity: want })
        .then(function () { return { acted: true }; });
    }
    if (want > 0) {
      return api('/cart/add.js', { items: [{ id: g.variant_id, quantity: want }] })
        .then(function () { return { acted: true }; });
    }
    return Promise.resolve(null);
  }

  /* THE ORDER NOTE IS NOW AN INSTRUCTION, NOT CARD CONTENT.
     Verbatim from Shaylin at Wiio, 2026-07-30, and it must stay verbatim —
     it's the string their warehouse was told to look for:
       "Please check the order snapshot for the quantity of cards and
        customization details"

     Everything this function used to do is deleted, and deliberately.
     It used to put the MESSAGE ITSELF in the order note, because the warehouse
     printed that field straight onto the card. That constraint is what capped
     the cart at one message per order, and what made the multi-message form
     dangerous: any labelled summary reaching a field that gets printed puts
     "GIFT MESSAGES — one per bouquet: 1× Golden Harvest…" on a customer's card.

     Now the note points at the snapshot and the per-line 'Gift message'
     properties carry the content — which is where it was always written, and
     which is unambiguous about WHICH bouquet in a way an order-level blob
     never could be.

     NO MESSAGE CONTENT AT ORDER LEVEL ANY MORE, including the 'Gift message'
     cart attribute this used to set. We know the warehouse prints one
     order-level field; we do not know that it prints only that one. Content
     that isn't there can't be printed by mistake, and a wrong card that has
     already shipped is not recoverable. CS can read the per-line properties on
     the order itself.

     Debounced and fired after the UI has settled: it must never sit on the
     shopper's critical path. */
  var _summaryTimer = null;
  function noteSummaryText() {
    return notedUnitCount() > 0
      ? (S('note_order_instruction', 'Please check the order snapshot for ')
         + 'the quantity of cards and customization details')
      : '';
  }
  function scheduleNoteSummary() {
    clearTimeout(_summaryTimer);
    _summaryTimer = setTimeout(function () {
      var text = noteSummaryText();
      /* The attribute is CLEARED, not omitted. Carts built before today still
         carry real message content in it, and Shopify leaves an attribute
         untouched when its key is absent — so an empty string is the only way
         to actually get the content out of the order-level field. */
      api('/cart/update.js', { note: text, attributes: { 'Gift message': '' } })
        .then(adoptCart)
        .catch(function () { /* mirror only; the line properties are the truth */ });
    }, 250);
  }

  function buildNoteProps(text) {
    var p = {};
    p[NOTE_PROP] = text;
    return p;
  }

  /* Preserve every other property on the line — other apps (Kaching) store
     their own state here and wiping it would break their bundle tracking.

     REMOVAL IS AN EMPTY STRING, NOT AN OMITTED KEY. Shopify treats an empty
     properties object on /cart/change.js as "no properties supplied" and
     leaves the line untouched — so when the gift message was the ONLY property,
     dropping the key produced {} and the note silently survived. It vanished
     optimistically, then reappeared on the re-read. Setting the key to "" is
     Shopify's documented way to delete a line item property. */
  /**
   * The line's properties with the gift message set — or, when clearing it,
   * REMOVED rather than blanked.
   *
   * `change.js` replaces the whole properties object, so this merges: Kaching
   * stores `__kaching_bundles` in here and wiping it would break their bundle
   * tracking. Every other key is carried through untouched.
   *
   * Writing `NOTE_PROP: ''` on a clear — which this used to do — leaves a
   * real, empty line-item property behind, and that is wrong twice over.
   * Shopify keeps the key, so (a) the line stays "customised" and will not
   * merge back with an identical plain line, leaving two rows of the same
   * bouquet that never recombine, and (b) the order carries an empty
   * `Gift message` property into the snapshot Wiio reads per line — the very
   * field their warehouse was told to print a card from. A blank card that
   * has already shipped is not recoverable, so an absent property is the only
   * safe representation of "no message". Omitting the key deletes it.
   */
  function mergeProps(existing, text) {
    var trimmed = (text || '').trim();
    var out = {};
    if (existing) {
      for (var k in existing) {
        if (Object.prototype.hasOwnProperty.call(existing, k) && k !== NOTE_PROP) {
          out[k] = existing[k];
        }
      }
    }
    if (trimmed) {
      out[NOTE_PROP] = trimmed;
    } else if (!Object.keys(out).length) {
      /* REGRESSION GUARD (Tyler 2026-08-04, live). Omitting the key deletes it
         — but ONLY when something else is left to send. `change.js` treats an
         EMPTY properties object as "not specified" and leaves the line exactly
         as it was, so on the common cart (the note is the line's only
         property) the clear became a silent no-op and Remove stopped working
         altogether. A blank value is the only payload that forces the write.
         Kaching's `__kaching_bundles`, when present, keeps the object
         non-empty, so that path still gets a true key deletion. */
      out[NOTE_PROP] = '';
    }
    return out;
  }


  // ─────────────────────────── gift note ───────────────────────────

  /**
   * Persist the gift message and keep the paid gift-note line in sync.
   *
   * The line is looked up from the CURRENT cart on every call, not from a
   * closure captured at render time. The old version captured it once: after
   * the first save added the line, the stale `null` meant every later keystroke
   * batch added ANOTHER one, silently multiplying the $5 charge.
   */
  /**
   * Inline editor for ONE bouquet's note. Opens in place under that bouquet so
   * there is never any doubt about which one the message is going on.
   */
  function openLineNoteEditor(group, host, targetLine, opts) {
    var g = CFG.gift_note || {};
    if (host.querySelector('.pp-cart__note-edit')) return;
    host.textContent = '';

    /* Funnel top (2026-08-02): the editor opening is the moment of intent —
       without it the funnel view can't see who tapped and bailed before the
       AI ran. 'edit' = reopening an existing note; excluded from the
       new-note funnel in the dashboard query. */
    track.emit('cart_giftnote_open', null, {
      entry: (targetLine && (lineNote(targetLine) || '').trim()) ? 'edit' : 'new',
      clarity_tag: 'giftnote_open'
    });

    var box = el('div', 'pp-cart__note-edit');
    box.appendChild(el('div', 'pp-cart__note-for',
      (S('note_for_label', 'Gift note for')) + ' ' + group.product_title));
    if (g.description) box.appendChild(el('p', 'pp-cart__note-desc', g.description));

    var ta = d.createElement('textarea');
    ta.className = 'pp-cart__note-input';
    ta.maxLength = g.max_length || 300;
    ta.placeholder = g.placeholder || 'Write your message…';
    ta.value = (targetLine ? lineNote(targetLine) : '');
    /* Native, instant, and free — the browser's own spellchecker already works
       in the shopper's language. Setting `lang` matters: without it the browser
       checks a Dutch or German message against an English dictionary and
       underlines every correct word. autocapitalize/autocorrect help on the
       phone keyboards where most of this typing happens. */
    ta.setAttribute('spellcheck', 'true');
    ta.setAttribute('lang', CFG.locale || 'en');
    ta.setAttribute('autocapitalize', 'sentences');
    ta.setAttribute('autocorrect', 'on');
    box.appendChild(ta);

    var meta = el('div', 'pp-cart__note-meta');

    // Declared before the AI button so its click handler closes over real
    // references rather than relying on var-hoisting.
    var status = el('span', 'pp-cart__giftnote-status', '');
    var counter = el('span', 'pp-cart__giftnote-count', (ta.maxLength - ta.value.length) + ' left');
    var cancel = el('button', 'pp-cart__note-link', S('note_cancel_cta', 'Cancel'));
    cancel.type = 'button';
    var save = el('button', 'pp-cart__giftnote-save', S('note_save_cta', 'Save'));
    save.type = 'button';
    save.disabled = true;

    /* ONE button whose job changes with the state of the field:
     *   empty      -> "Help me write"  (guided compose)
     *   has text   -> "Fix spelling"   (one-tap correction, applied directly)
     *
     * "Polish it" was doing double duty and the label went stale the moment
     * someone started typing. Splitting by intent also matters for who's using
     * this: a shopper who has already written their message wants their OWN
     * words with the typos gone — not a panel of rewritten alternatives to
     * choose between. So correction applies straight to the field, with Undo.
     */
    var ai = el('button', 'pp-cart__giftnote-ai');
    ai.type = 'button';
    var aiIcon = icon(ICON.sparkle, 'pp-cart__giftnote-ai-icon');
    var aiLabel = el('span', null, '');
    ai.appendChild(aiIcon);
    ai.appendChild(aiLabel);

    function hasText() { return !!(ta.value || '').trim(); }
    function syncAiButton() {
      aiLabel.textContent = hasText()
        ? (S('note_ai_fix', 'Fix spelling'))
        : (S('note_ai_cta', 'Help me write'));
      ai.classList.toggle('is-fix', hasText());
    }

    ai.addEventListener('click', function () {
      if (hasText()) runSpellcheck(ta, status, ai, aiLabel, syncCounter);
      else openNoteAssist(ta, null);
    });

    function syncCounter() {
      counter.textContent = (ta.maxLength - ta.value.length) + ' left';
      save.disabled = ta.value.trim() === (targetLine ? lineNote(targetLine) : '').trim();
      syncAiButton();
    }

    /* Any edit clears a stale status. An error (or an "Undo") left sitting
       there after the shopper has moved on is worse than no message — it
       describes a state that no longer exists. */
    ta.addEventListener('input', function () {
      if (status.textContent) {
        status.textContent = '';
        status.className = 'pp-cart__giftnote-status';
      }
    });
    ta.addEventListener('input', syncCounter);

    /* Block a THIRD consecutive newline as it's typed. Preventing the input is
       better than reformatting afterwards: the shopper simply can't build a
       wall of blank lines, and the caret never jumps mid-sentence. */
    ta.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      if (/\n\s*\n\s*$/.test(ta.value.slice(0, ta.selectionStart))) e.preventDefault();
    });

    /* Tidy on blur, so what's on screen is what will be printed on the card. */
    ta.addEventListener('blur', function () {
      var cleaned = normalizeNote(ta.value, ta.maxLength);
      if (cleaned !== ta.value) { ta.value = cleaned; syncCounter(); }
    });
    cancel.addEventListener('click', function () { render(); });
    /* Nothing stopped a second Save while the first was still in flight, and
       the two shared one snapshot — so the second one always carried a key the
       first had just retired, and the shopper was told their note failed to
       save immediately after it saved. setLineNote recovers from a stale key
       now, but not sending the duplicate at all is the better fix: it is one
       fewer write, and the save cannot flicker through an error state. */
    var saving = false;
    save.addEventListener('click', function () {
      if (saving) return;
      saving = true;
      save.disabled = true;
      // Authoritative tidy: blur can be skipped entirely (typing, then clicking
      // straight onto Save), so never trust the raw field value.
      var text = normalizeNote(ta.value, ta.maxLength);
      ta.value = text;
      var hadNote = !!(targetLine && (lineNote(targetLine) || '').trim());
      // Snapshot before painting, for the same reason as removal. An EDIT
      // targets the noted line; a new note wants a free one.
      var snap = snapshotTarget(group, targetLine, hadNote);
      optimisticNote(group, text, targetLine);
      setLineNote(group, text, targetLine, snap)
        .then(function () {
          /* The commercial moment — a saved note is the $5 actually added.
             Was allowlisted from day one but never emitted, so the funnel
             had a hole exactly at its conversion step. */
          track.emit('cart_giftnote_add', { qty: 1 }, {
            kind: hadNote ? 'edit' : 'new',
            chars: text.length,
            clarity_tag: 'giftnote_saved'
          });
          /* Assist-written note saved with a declared occasion → capture the
             facts for the occasion-reminder pipeline. Fires once per save;
             consent is checked server-side at dispatch time. */
          if (ta._ppReminder) {
            captureReminderFacts(ta._ppReminder);
            ta._ppReminder = null;
          }
          render();
        })
        .catch(function (err) {
          /* Leave the real error in the console. This catch swallowed it
             entirely, which made a save failure impossible to diagnose from a
             screenshot or a "it says couldn't save" report — the same hole
             runSpellcheck's catch was fixed for. The shopper still gets the
             plain message; only the diagnosis changes. The status code matters
             most: a 400/404 is a stale line key (Shopify says "no valid id or
             line parameter" for a key its own write retired), a 422 is Shopify
             refusing the write, and the two have nothing to do with each
             other. Anything reaching here has ALREADY survived one re-resolve
             and retry in setLineNote, so it is a real failure, not a race. */
          try {
            console.warn('[pp-cart] note save failed:', err && err.status, err);
          } catch (e) {}
          showCartError(CFG.note_error || "Couldn't save your note — please try again");
          return getCart().then(function (c) { cart = c; render(); });
        })
        /* Runs on both paths. render() usually replaces this button outright,
           so re-enabling a detached node is a no-op — it matters on the error
           path, where the editor stays put and Save must work again. */
        .then(function () {
          saving = false;
          try { save.disabled = false; } catch (e) {}
        });
    });

    /* THE STATUS IS NOT A CONTROL, so it does not sit in the control row.
       Measured at 390px: the four controls total 320px of 325px available, so
       they fit — but "No changes needed" adds ~110px and forced the row to
       wrap, which dropped Save onto a line of its own (Tyler, with a
       screenshot). Truncating the message instead would be worse: it is the
       answer to the button the shopper just pressed.
       On its own line it gets the full width, the toolbar keeps its four
       controls together, and an empty status collapses to nothing (:empty). */
    box.appendChild(status);
    meta.appendChild(ai);
    meta.appendChild(counter);
    meta.appendChild(cancel);
    meta.appendChild(save);
    box.appendChild(meta);
    host.appendChild(box);
    // Set the initial label/counter state — without this the AI button renders
    // blank until the first keystroke.
    syncCounter();

    /* ASK-FIRST (Tyler 2026-08-02): a fresh note opens on "Who's it for?"
       instead of a blank textarea — the blank page is the reason notes
       don't get written. The textarea stays one "I'll write it myself" tap
       away, and Cancel/X closes the whole editor in one tap. Editing an
       existing note keeps the classic editor: there is nothing to ask.
       Deliberately NO ta.focus() on the ask-first path — it would pop the
       phone keyboard underneath the wizard. */
    if (!(ta.value || '').trim()) {
      openNoteAssist(ta, null, {
        recipient: (opts && opts.assistRecipient) || '',
        onCancel: function () { render(); }
      });
    } else {
      setTimeout(function () { ta.focus(); }, 40);
    }

    /* GIVE THE EDITOR THE SCREEN (Tyler 2026-08-03 v14).
       Collapse the suggestions first, then scroll — in that order, because
       collapsing removes up to ~180px from ABOVE the editor on a phone and
       any position measured before that reflow is stale by exactly that much.
       The collapse is temporary and unsaved; render() releases it.
       Deferred one frame so the browser has laid out the editor (and, on the
       ask-first path, the wizard that just replaced the textarea) before
       anything is measured. */
    noteOpen = true;
    if (_upsellSetCollapsed) _upsellSetCollapsed(true);
    /* On a v2 phone the carousel is docked above the footer, so an open note
       editor is pinned into whatever strip is left between the two. Push the
       carousel down below the cart lines — the same move a second product
       triggers — so the editor gets the panel's full height. render() (both
       Save and Cancel call it) clears noteOpen and re-docks it. */
    if (V2) v2PlaceHost();
    /* Re-QUERY the editor each attempt instead of trusting the `box` captured
       above. On the ask-first path openNoteAssist() has already run
       synchronously by this point and rebuilds the editor's contents, so the
       captured node can be detached — and a detached node measures 0×0, which
       read as "far above the viewport" and scrolled the body to the TOP,
       hiding the editor it was supposed to reveal (caught live 2026-08-03).
       Three attempts: one after layout, then two as the wizard settles and
       changes the editor's height. Each is a no-op once it's fully visible. */
    var reveal = function () {
      revealInBody(host.querySelector('.pp-cart__note-edit')
        || host.querySelector('.pp-cart__giftnote-pad')
        || d.querySelector('.pp-cart__note-edit'));
    };
    requestAnimationFrame(function () {
      reveal();
      setTimeout(reveal, 180);
      setTimeout(reveal, 450);
    });
  }


  /* Re-render ONLY the reward block, leaving the gift-note textarea untouched
     so the shopper doesn't lose focus or caret position mid-sentence. The new
     block is inserted back at the top of the body to keep sticky ordering. */

  // ──────────────────────────── upsells ────────────────────────────
  /**
   * Build the candidate list from the recommendation engine.
   *
   * Every product in the cart is an anchor, so a two-item cart draws on both
   * products' recommendations rather than only the first. A product suggested
   * by MORE than one anchor is genuinely complementary to this basket, not just
   * to one item in it — so its rank improves with each anchor that names it.
   *
   * Rank position matters too: the engine returns its list already ordered by
   * lift, so an item recommended first carries more weight than one recommended
   * fourth.
   */
  function engineCandidates(u, inCartProducts) {
    var recs = u.recs || {};
    var cat = u.catalogue || {};
    if (!cart) return [];

    var scored = {};
    cart.items.forEach(function (item, cartIdx) {
      if (isMechanismLine(item)) return;
      var list = recs[String(item.product_id)] || [];
      list.forEach(function (handle, idx) {
        var p = cat[handle];
        if (!p) return;                                   // sold out / unresolved
        if (inCartProducts[String(p.product_id)]) return;  // already have it
        if (!scored[handle]) {
          scored[handle] = {
            handle: handle,   // joins pippy.bundle_social for the chip layer
            variant_id: p.variant_id,
            product_id: p.product_id,
            title: p.title,
            native: p.native === true,
            url: p.url,
            price: p.price,
            image: p.image,
            rating: p.rating,
            reviews: p.reviews,
            _rank: 0,
            _anchors: 0,
            /* Which cart item earned this suggestion its best position. The
               learning loop scores (anchor → recommendation) pairs, so an
               event that only says "a teddy was shown" teaches nothing —
               it has to say what the teddy was shown NEXT TO. Where several
               anchors name the same product we attribute to the strongest,
               because splitting one impression across anchors would inflate
               the denominator for all of them. */
            _anchor: null,
            _anchorWeight: -1,
            /* Set when this recommendation is the 14cm twin of a bouquet
               already in the cart. Derived here from the handles rather than
               shipped in the metafield: the payload is a bare list of handles
               to stay under 64KB, and "mini-X is the mini of X" is knowable
               from the two strings alone, so paying to transmit it would be
               paying for something we can read off what's already here. */
            _miniTwin: false,
            _twinIdx: -1
          };
        }
        if (item.handle && handle === 'mini-' + item.handle) {
          scored[handle]._miniTwin = true;
          /* Which cart LINE this is the twin of. Items append at the bottom
             (first-added-first), so a higher index = more recently added —
             the boost pass uses it so the twin of the bouquet the shopper
             JUST added outranks twins of older lines (Tyler 2026-08-03:
             post-add, the first card was an older item's mini, not the
             corresponding one). */
          scored[handle]._twinIdx = cartIdx;
        }
        // Higher for an earlier position, and additive across anchors.
        var weight = list.length - idx;
        scored[handle]._rank += weight;
        scored[handle]._anchors += 1;
        if (weight > scored[handle]._anchorWeight) {
          scored[handle]._anchorWeight = weight;
          scored[handle]._anchor = String(item.product_id);
        }
      });
    });

    return Object.keys(scored)
      .map(function (h) { return scored[h]; })
      .sort(function (a, b) {
        if (b._anchors !== a._anchors) return b._anchors - a._anchors;
        return b._rank - a._rank;
      });
  }

  /**
   * Why this item, right now — stated so it survives checkout.
   *
   * Ordered by how much the shopper actually gains, because that's what makes
   * an upsell worth taking rather than an interruption:
   *   1. crosses a QUANTITY tier  — a percentage off the WHOLE order, usually
   *      worth more than the item costs, so it's the strongest honest offer
   *      we have
   *   2. crosses the FREE DELIVERY threshold — concrete saving, universally
   *      understood
   *   3. otherwise no reason at all. A vague "recommended for you" is noise;
   *      silence is better than filler.
   */
  /**
   * What the shopper GAINS from adding this specific item, in descending order
   * of how compelling it honestly is.
   *
   * The first version only spoke when an item immediately unlocked a tier, so
   * most rows said nothing at all — wasting the strongest motivator available:
   * the goal-gradient effect, where wanting-to-finish rises as a goal gets
   * closer. Showing PROGRESS is legitimate pressure; inventing urgency is not.
   *
   *   1. unlocks a reward now      -> name it, and quantify the saving in money
   *   2. would leave them 1 away   -> "then just 1 more for 15% off"
   *   3. moves them toward a goal  -> state the remaining distance
   *   4. nothing true to say       -> silence
   *
   * Every figure is derived from the live cart and the discount's real scope,
   * so it stays true through checkout.
   */
  /**
   * What the DISCOUNTED subtotal becomes if one more of `it` is added.
   *
   * A percentage discount means a $12.99 item does NOT move the subtotal by
   * $12.99. At 3 bouquets with 15% already applied, a fourth adds $11.04 — so
   * comparing the item's list price against the remaining gap made the cart
   * promise "Unlocks free delivery" on an item that left the shopper $0.80
   * short. It said the reward was unlocked, then didn't unlock it.
   *
   * Simulating the whole subtotal rather than adjusting the gap also handles
   * the case that has the opposite sign: adding an item can CROSS a quantity
   * tier, raising the discount rate, which moves the discounted subtotal
   * FURTHER from a value threshold. Arithmetic on the gap alone can't see that.
   */
  function projectedSubtotalIfAdded(it) {
    if (!cart) return 0;
    var listTotal = (cart.original_total_price || cart.total_price) + it.price;
    var qtyTiers = (REWARDS.quantity_tiers || []).filter(function (t) {
      return t.percent;
    });
    if (!qtyTiers.length) return listTotal;

    /* Every ladder is scored on ITS OWN collection.
       This used to assume one shared scope — true when signature and mini
       tiers both hung off pop-up-bouquets, and false the moment they were
       split into 3/6 and 4/8. It then priced every basket against whichever
       tier happened to be first in the list, and mispriced 6 of 8 mixed
       baskets: with 3 signatures and 7 minis in the cart an eighth mini
       genuinely costs 45c, because it drags all eight minis to 25% — and we
       were quoting $18.43 for it. Overstating an upsell by 40x is exactly the
       bug this function was written to prevent, one collection over. */
    var scopes = [];
    qtyTiers.forEach(function (t) {
      var key = (((t.scope || {}).collections || []).slice().sort().join('|'))
        || '__all__';
      var found = null;
      scopes.forEach(function (sc) { if (sc.key === key) found = sc; });
      if (!found) {
        found = { key: key, rep: t, tiers: [], units: [], qty: 0, pct: 0 };
        scopes.push(found);
      }
      found.tiers.push(t);
    });

    // Each line belongs to at most ONE scope, so nothing is discounted twice
    // if a product ever appears in two scoped collections. Units are kept
    // separately rather than summed into a scope total, because the discount
    // is rounded PER UNIT (see below) and a scope can hold units at different
    // prices.
    function claim(entry, unit, qty) {
      for (var si = 0; si < scopes.length; si++) {
        if (itemCountsTowardTier(entry, scopes[si].rep)) {
          scopes[si].units.push({ unit: unit, qty: qty });
          scopes[si].qty += qty;
          return { scope: scopes[si], amount: unit * qty };
        }
      }
      return { scope: null, amount: 0 };
    }

    var covered = 0;
    cart.items.forEach(function (i) {
      if (isMechanismLine(i)) return;
      var qty = i.quantity || 0;
      var line = (i.original_line_price != null
        ? i.original_line_price : i.final_line_price) || 0;
      covered += claim(i, qty ? line / qty : line, qty).amount;
    });
    var mine = claim(it, it.price, 1);
    covered += mine.amount;

    var discounted = 0;
    scopes.forEach(function (sc) {
      var pct = 0;
      sc.tiers.forEach(function (t) {
        if (sc.qty >= t.min_qty && t.percent > pct) pct = t.percent;
      });
      sc.pct = pct;
      /* PER UNIT, FLOORED — which is what Shopify actually does, not an
         implementation detail we're free to approximate. A percentage of the
         scope total, rounded once, came out three cents light on a three-
         bouquet cart ($35.67 against Shopify's $35.70), and those cents landed
         straight in the upsell price: the rail quoted $11.87 and then $11.86
         for a bouquet that bills at $11.90 both times.
         Checked against Tyler's live cart: 1399 x 15% = 209.85, floored to
         209, so a unit is $11.90 and three of them save $6.27 — the exact
         figure the cart footer prints. */
      sc.units.forEach(function (u) {
        discounted += u.qty * (u.unit - Math.floor(u.unit * pct / 100));
      });
    });

    lastSimScope = mine.scope;
    // Anything in no scope at all (the $5 note, a future candle) is undiscounted.
    return discounted + Math.max(0, listTotal - covered);
  }

  /* Which scope claimed the item in the most recent simulation, so
     effectiveAddCost can read the rate that applied to it without building
     the whole scope table a second time. Set by projectedSubtotalIfAdded on
     every call; only ever read immediately afterwards. */
  var lastSimScope = null;

  /**
   * What adding this item ACTUALLY costs the shopper right now.
   *
   * The rail was pricing in list terms while every cart line beside it showed
   * a discounted figure — so with 15% live we advertised a $13.99 bouquet that
   * really costs $11.89, overstating our own upsell by 15% at the exact moment
   * of decision.
   *
   * It is THIS LINE'S price after the discount — not the amount the cart
   * total moves.
   *
   * The subtotal delta was tried and pulled. It is a true number, and it is
   * the wrong one: with 2 bouquets in the cart, adding a third moves the total
   * by $7.69, because the two already there drop to 15% as well. Printed in
   * the price slot, struck-through sticker beside it, that reads as "this
   * bouquet costs $7.69" — and checkout then bills the line at $11.90. Tyler
   * hit it immediately and couldn't reconcile it ("idk where the $7.69 is
   * coming from"); a shopper gets no explanation at all, just a number that
   * changes on them.
   *
   * The saving on the bouquets they ALREADY chose is real and worth saying —
   * but it belongs to the reward chip ("Unlocks 15% off") and the footer's
   * "You save", which is where the shopper is looking for a cart-level fact.
   * A price slot may only ever answer "what will this line cost me".
   */
  function effectiveAddCost(it) {
    if (!cart || !it || it.price == null) return null;
    /* Run the simulation for its side effect — which scope ends up holding
       this item, and at what rate, once it's in the cart. */
    projectedSubtotalIfAdded(it);
    var sc = lastSimScope;
    if (!sc || !sc.pct) return null;      // no discount will reach it
    var price = it.price - Math.floor(it.price * sc.pct / 100);
    if (!isFinite(price) || price <= 0 || price >= it.price) return null;
    return price;
  }

  function rewardReasonFor(it, next) {
    if (!next || !cart) return { text: null, boost: 0 };
    var tier = next.tier;

    if (tier.axis === 'quantity') {
      var counts = itemCountsTowardTier(it, tier);
      if (!counts) return { text: null, boost: 0 };   // can't claim what it can't earn
      var after = (tier.current || 0) + 1;
      var stillNeeded = tier.at - after;

      if (stillNeeded <= 0) {
        /* No "· save $6.30" here any more. That figure is precisely the gap
           between the struck-through sticker and the effective price on the
           row above it — the same number, written twice, costing a line of
           text each time. The price says what it costs; this says why. */
        return {
          text: (S('reason_unlocks', 'Unlocks')) + ' ' + tier.label,
          boost: 100
        };
      }
      if (stillNeeded === 1) {
        /* "1 more for 15% off" — was "Then just 1 more for …", which
           overflowed the fused button's label segment (Tyler 2026-08-03). */
        return {
          text: (S('reason_then_one', '1 more for')) + ' ' + tier.label,
          boost: 70
        };
      }
      return {
        text: stillNeeded + ' ' + (S('reason_away', 'away from')) + ' ' + tier.label,
        boost: 40
      };
    }

    // Value axis (free delivery, subtotal discounts). Compare against what the
    // subtotal ACTUALLY becomes, not the item's list price — see
    // projectedSubtotalIfAdded.
    var projected = projectedSubtotalIfAdded(it);
    if (projected >= tier.at) {
      var over = projected - tier.at;
      /* Plain "Unlocks free delivery". A gap-framed variant ("Clears the
         $9.30 gap") shipped for a few hours on 2026-08-03 and Tyler killed
         it — "doesn't even make sense" as shopper language; the reward head
         already owns the dollar distance. */
      return {
        text: (S('reason_unlocks', 'Unlocks')) + ' ' + tier.label,
        // Prefer the item clearing the threshold with the least overshoot —
        // the cheapest honest route to the reward.
        boost: 90 - Math.min(89, over / 100)
      };
    }
    /* Deliberately silent when the item doesn't reach the threshold.
       "$19.02 USD from free delivery" asks the shopper to do arithmetic about a
       reward they aren't close to, on a row whose job is to sell one product —
       it reads as confusing rather than motivating. The reward bar at the top
       of the drawer already states the distance clearly and in one place. */
    return { text: null, boost: 0 };
  }

  /**
   * Money saved if this item takes the cart over a percentage tier.
   *
   * Applied to the ELIGIBLE subtotal only, not the whole cart — the discount is
   * scoped to a collection, so quoting a figure based on everything would
   * overstate it. Returns null when the number can't be computed confidently,
   * and the caller then states the percentage alone rather than guessing.
   */
  function tierSavingIfAdded(it, tier) {
    if (!tier || !cart) return null;

    /* The gift isn't a percentage — what the shopper saves is the mini's own
       retail price. Answering it here also stops it falling through to the
       percentage lookup below, which is what produced "save $7.79": the gift
       rung is min_qty 4 and so is BUY 4+ MINIS, so matching on quantity alone
       applied the minis' 15% to the signature subtotal. */
    if (tier.key === 'gift') {
      var pick = chooseGift();
      return pick && pick.price ? pick.price : null;
    }

    /* Match the SCOPE as well as the quantity. Two ladders now share
       thresholds — signature at 3/6, minis at 4/8 — so quantity alone is no
       longer a unique key and silently picked the wrong percentage. */
    var wantColl = ((tier.scope || {}).collections || [])[0] || null;
    var pct = null;
    (REWARDS.quantity_tiers || []).forEach(function (t) {
      if (t.min_qty !== tier.at || !t.percent) return;
      var tColl = ((t.scope || {}).collections || [])[0] || null;
      if (wantColl && tColl && wantColl !== tColl) return;
      pct = t.percent;
    });
    if (!pct) return null;

    var scope = tier.scope;
    var allowed = null;
    if (scope && !scope.all) {
      allowed = {};
      (scope.collections || []).forEach(function (h) {
        ((REWARDS.collection_products || {})[h] || []).forEach(function (pid) {
          allowed[String(pid)] = true;
        });
      });
      if (!Object.keys(allowed).length) return null;
    }

    var base = 0;
    cart.items.forEach(function (i) {
      if (isMechanismLine(i)) return;
      if (allowed && !allowed[String(i.product_id)]) return;
      base += (i.original_line_price != null ? i.original_line_price : i.final_line_price);
    });
    base += it.price;                       // the item they're being offered
    var saving = Math.round(base * (pct / 100));
    return saving > 0 ? saving : null;
  }

  /** Would this product count toward a scoped quantity tier? */
  function itemCountsTowardTier(it, tier) {
    var scope = tier && tier.scope;
    if (!scope || scope.all) return true;
    var allowed = {};
    (scope.collections || []).forEach(function (h) {
      ((REWARDS.collection_products || {})[h] || []).forEach(function (pid) {
        allowed[String(pid)] = true;
      });
    });
    // No scope data resolved — don't promise something we can't verify.
    if (!Object.keys(allowed).length) return false;
    return !!allowed[String(it.product_id)];
  }

  /**
   * The recipient's first name, when we genuinely know it.
   *
   * Preference order matters. A name the shopper TYPED into the assist flow is
   * declared fact; a name scraped out of their message is an inference. We use
   * the inference only when it's a confident one — an opening salutation, which
   * is how the overwhelming majority of gift notes begin.
   *
   * PRIVACY: this shows a shopper their OWN recipient name back to them, in
   * their own session. That is their data and never leaves the browser. It is
   * deliberately NOT the same as surfacing message content inside Pippy, which
   * stays aggregate-only per GIFT_NOTE_INTELLIGENCE_HANDOFF.md.
   *
   * Getting this wrong is worse than not doing it — "Get Dear a teddy" would be
   * embarrassing — so anything uncertain returns null.
   */
  /* Terms of address that aren't names. "Something else for Mum?" is a fine
     fallback heading, but "for Oma?" reads as though we mistook a relationship
     for a person — and the salutation matcher already accepts "Lieve" and
     "Liebe", so an English-only list let Dutch and German relationship words
     straight through. Covers the languages our markets actually write in. */
  var GENERIC_ADDRESS = [
    // EN — includes bare relationship titles, since "Happy birthday, Auntie!"
    // otherwise resolves to the "name" Auntie.
    'all', 'everyone', 'friend', 'friends', 'family', 'team', 'mum', 'mummy',
    'mom', 'mommy', 'dad', 'daddy', 'nan', 'nana', 'gran', 'granny', 'grandma',
    'grandad', 'grandpa', 'sis', 'bro', 'love', 'darling', 'folks', 'guys',
    'auntie', 'aunty', 'aunt', 'uncle', 'cousin', 'sister', 'brother',
    // NL
    'oma', 'opa', 'mam', 'mama', 'pap', 'papa', 'moeder', 'vader',
    'allemaal', 'iedereen', 'familie',
    // DE
    'mutti', 'vati', 'alle',
    // FR
    'maman', 'papi', 'mamie', 'tous',
    // SE
    'mamma', 'pappa', 'mormor', 'farmor', 'morfar', 'farfar', 'alla'
  ];

  /**
   * How many times this browser has opened the cart, ever.
   *
   * A crude but honest proxy for familiarity — it's this device, not this
   * person, and it makes no claim beyond "we've seen you before". Stored
   * locally; never sent anywhere.
   */
  function visitCount() {
    try {
      var n = parseInt(w.localStorage.getItem('pp_cart_visits') || '0', 10) || 0;
      return n;
    } catch (e) { return 0; }
  }
  function bumpVisitCount() {
    try {
      w.localStorage.setItem('pp_cart_visits', String(visitCount() + 1));
    } catch (e) { /* private mode */ }
  }

  /* Whether the shopper has minimised the upsell section. Persisted rather
     than held in memory because the drawer re-renders on every cart change —
     see the toggle in renderUpsells. Survives the session too: someone who
     doesn't want suggestions this visit almost certainly doesn't want them
     re-expanded on the next one, and the header keeps the way back visible. */
  /* Key VERSIONED 2026-08-26 (Tyler: "expanded by default"): while the v19
     footer guard was crushing the phone band into a scrolling sliver,
     shoppers (Tyler included) tapped the chevron at the broken thing and
     the old key remembered that collapse forever after. Retiring the key
     resets every device to the true default (expanded) exactly once; a
     deliberate collapse still persists under the new key. */
  var UPSELLS_COLLAPSED_KEY = 'pp_upsells_collapsed2';
  function upsellsCollapsed() {
    try {
      return w.localStorage.getItem(UPSELLS_COLLAPSED_KEY) === '1';
    } catch (e) { return false; }
  }
  function setUpsellsCollapsed(v) {
    try {
      w.localStorage.setItem(UPSELLS_COLLAPSED_KEY, v ? '1' : '0');
    } catch (e) { /* private mode */ }
  }

  /* DESKTOP has its own key and its own meaning for the same chevron (Tyler
     2026-08-04). Above 560px the section is no longer hidden/shown — it
     switches between a single-row auto-scrolling carousel (the DEFAULT, which
     is why this key defaults to false) and a full stacked view that takes the
     drawer body over up to the reward band. Deliberately NOT the key above:
     mobile's default is "shown", desktop's is "carousel", and one key can't
     carry two defaults without the drawer opening wrong on one of them. */
  var UPSELLS_DESK_KEY = 'pp_upsells_desk_expanded';
  function deskUpsellsExpanded() {
    try {
      return w.localStorage.getItem(UPSELLS_DESK_KEY) === '1';
    } catch (e) { return false; }
  }
  function setDeskUpsellsExpanded(v) {
    try {
      w.localStorage.setItem(UPSELLS_DESK_KEY, v ? '1' : '0');
    } catch (e) { /* private mode */ }
  }

  /**
   * The upsell heading, chosen from what we actually know about this shopper.
   *
   * Priority is by SPECIFICITY, not by novelty:
   *   1. the recipient's name — most relevant, this is a gift and they told us
   *      who it's for
   *   2. the buyer's name — warm, and only available when they're signed in,
   *      so it's their own data shown back to them
   *   3. returning visitor — familiarity without claiming to know them
   *   4. market default — AU/GB say "Often bought together", the US says
   *      "Frequently"; small, but writing in someone else's dialect is exactly
   *      the kind of thing that reads as foreign
   *
   * Every step degrades silently. An unpersonalised heading is fine; a wrong
   * one ("Something else for Birthday?") is not.
   */
  function upsellHeading(u, who) {
    var t = CFG.upsell_titles || {};
    var market = (t.by_market || {})[CFG.country] || {};
    var shopper = CFG.shopper || {};

    if (who) {
      return (market.recipient || t.recipient || 'Something else for {name}?')
        .replace('{name}', who);
    }
    /* Greet by name or not at all (Tyler 2026-08-02). The old anonymous
       "Welcome back —" branch (orders>0 or 3+ visits) claimed recognition of
       someone it couldn't actually name, which reads as tracking, not
       warmth. A shopper we can address — today that's a logged-in customer;
       identified abandoned-cart returners can join when a Klaviyo-identify
       hook exists — gets the social-proof greeting; everyone else gets the
       market default. */
    var buyer = (shopper.first_name || '').trim();
    if (buyer && /^[\p{L}][\p{L}'-]{1,20}$/u.test(buyer)) {
      return (market.buyer || t.buyer || 'Hey {name}, customers often add these')
        .replace('{name}', capitalise(buyer));
    }
    return market.default || t.default || u.title || 'Complete your gift';
  }

  /* Words a gift note commonly opens with that are emphatically not names.
     Only pattern C (direct address, no greeting) can produce these, but that
     pattern accepts anything before the first comma — so without this list
     "Wishing you all the best," would address the shopper's aunt as "Wishing". */
  var NON_NAME_OPENERS = [
    'wishing', 'hoping', 'hope', 'thinking', 'sending', 'just', 'so', 'we',
    'i', 'you', 'thank', 'thanks', 'with', 'all', 'best', 'enjoy', 'have',
    'may', 'here', 'congratulations', 'congrats', 'happy', 'merry', 'well',
    'good', 'lots', 'much', 'from', 'for', 'to', 'and', 'but', 'this', 'that',
    'what', 'wow', 'yay', 'finally', 'welcome', 'get', 'love', 'dear', 'hi',
    'hey', 'hello', 'lieve', 'liebe', 'beste', 'hej'
  ];

  function recipientName() {
    try {
      // Read through `w` consistently — guarding on w.sessionStorage and then
      // calling the bare global works in a browser but is a latent trap.
      var declared = (w.sessionStorage
        && w.sessionStorage.getItem('pp_note_recipient')) || '';
      declared = declared.trim();
      if (declared && /^[\p{L}][\p{L}'-]{1,20}$/u.test(declared)) {
        return capitalise(declared);
      }
      var note = '';
      (cart ? cart.items : []).forEach(function (i) {
        if (!note) note = lineNote(i);
      });
      if (!note) return null;
      note = String(note);

      /* Two opening shapes, because only handling the first missed a very
         common one:
           A  "Dear Anne,"  "Hi Sara!"  "Lieve Els,"
           B  "Happy 80th birthday, Auntie Geraldine!"  "Congratulations Tom!"
         B is arguably MORE common on a gift card than A — the greeting is the
         occasion, and the name follows it. */
      var m = note.match(
        /^\s*(?:dear|hi|hey|hello|to|lieve|liebe[rs]?|beste)\s+([\p{L}][\p{L}' -]{1,30}?)\s*[,!.\n]/iu
      ) || note.match(
        /* The occasion is enumerated rather than matched as free filler.
           A wildcard between the greeting and the name looked tidier but let
           "Happy birthday!" resolve to the name "Birthday" — the lazy filler
           matched nothing and the occasion word became the capture. Spelling
           out the occasions costs a longer pattern and removes a whole class
           of embarrassing output.
           Punctuation after the greeting is optional, because people punctuate
           gift cards however they like: both "Happy birthday, Anne!" and
           "Happy birthday Tom," are common. */
        /^\s*(?:happy\s+(?:\d{1,3}(?:st|nd|rd|th)\s+)?(?:birthday|anniversary|christmas|easter|new year|holidays|retirement|mother'?s day|father'?s day|valentine'?s day)|congratulations|congrats|merry christmas|get well(?: soon)?|gefeliciteerd|herzlichen gl(?:ü|u)ckwunsch)\b[,!]?\s+([\p{L}][\p{L}' -]{1,30}?)\s*[,!.\n]/iu
      ) || note.match(
        /* C — addressed directly, with no greeting at all:
             "Aunt Cheryl, what a wonderful chapter ahead!"
             "Cheryl, hope you love these"
           This is common and the previous patterns missed it entirely. It's
           also the riskiest shape, because plenty of notes open with an
           ordinary word followed by a comma ("Wishing you all the best,"), so
           the capture is checked against NON_NAME_OPENERS below. */
        /^\s*([\p{L}][\p{L}' -]{1,30}?)\s*[,!]/u
      );
      if (!m) return null;

      /* Strip a relationship title: "Auntie Geraldine" is Geraldine, and a
         bare "Auntie" is not a name at all. Without this we'd have addressed
         the shopper's aunt as "Auntie". */
      var phrase = m[1].trim().replace(/\s+/g, ' ');
      var TITLES = /^(auntie|aunty|aunt|uncle|grandma|grandad|grandpa|granny|nana|nan|cousin|sis|sister|bro|brother|mr|mrs|ms|miss|dr|tante|oom|tia|tio)\s+/i;
      if (TITLES.test(phrase)) phrase = phrase.replace(TITLES, '').trim();

      // Only a single given name reads naturally in an offer.
      var name = phrase.split(' ')[0];
      if (!name || !/^[\p{L}][\p{L}'-]{1,20}$/u.test(name)) return null;
      var low = name.toLowerCase();
      if (GENERIC_ADDRESS.indexOf(low) !== -1) return null;
      /* Pattern C accepts anything before the first comma, so a note opening
         "Wishing you all the best," would otherwise yield the name "Wishing".
         These are sentence-openers, not people. */
      if (NON_NAME_OPENERS.indexOf(low) !== -1) return null;
      return capitalise(name);
    } catch (e) { return null; }
  }

  function capitalise(s) {
    s = String(s || '');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /* Upsell card style is DEVICE-ASSIGNED (Tyler 2026-08-03, supersedes the
     same-day 50/50 A/B): the swipeable carousel serves narrow/touch screens
     (limited real estate, native gesture), the scannable rows serve desktop.
     The `cards` prop still stamps every event, so the readout is a per-device
     baseline instead of an experiment. cfg.upsells.cards ('rows'|'carousel')
     stays as the one-flip override; localStorage pp_force_upsell_cards forces
     an arm for QA (the OLD pp_ab_upsell_cards key is deliberately ignored —
     it holds sticky 50/50 assignments that would pin returning shoppers to a
     random arm). */
  /* "Mini Spotted Lilies Pop-Up Bouquet" → { name: 'Spotted Lilies',
     size: 'Mini size' } (Tyler 2026-08-03: the suffix is noise; size labels
     carry the distinction — same convention as the bundle widget). Pattern
     ONLY: a title that doesn't match (gift card, translated catalogue title,
     future products) passes through untouched with no size label, so this
     can never mangle something it doesn't understand. */
  /* ── Social proof for upsell cards (backlog #3, 2026-08-03) ──────────
     Fed by pippy.bundle_social — the nightly first-party feed the bundle
     lane built (occasions ≥30 tagged orders, recipient counts ≥40, live
     bundle counter; thresholds enforced AT THE FEED). One chip per card,
     concrete numbers only, the bundle widget's exact priority:
       live counter → numbered recipient → occasion breadth → nothing.
     Descriptive norms (Cialdini): undecided buyers follow what other
     women chose; numbers beat claims. A card with no data gets NO chip —
     silence is the honest fallback. */
  function socialFor(it) {
    var s = (CFG.social || {})[it.handle];
    if (!s) return null;
    if ((s.bundles_today || 0) >= 8) {
      return { kind: 'counter',
        text: S('chip_counter', 'In {n} bundles today')
          .replace('{n}', String(s.bundles_today)) };
    }
    if (s.recip_label && s.recip_n) {
      var who = s.recip_label === 'mums' ? S('recip_mums', 'Mums') : s.recip_label;
      /* No visible number (Tyler 2026-08-03: "the number thing is weird").
         The claim stays data-gated — recip_n >= 40 at the feed decides
         whether this exists — the digits just aren't printed. */
      return { kind: 'recip',
        text: S('chip_recip', 'Loved for {who}').replace('{who}', who) };
    }
    /* "Popular for X" from the feed's popular_for — the segment this design
       OVER-INDEXES in (lift >= 1.15, diversity-first round-robin at the
       feed, so neighbouring cards never repeat). Reading occasions[0] here
       briefly made every card say "Popular for celebrations" (Tyler
       2026-08-03) — raw most-common is celebrations for the whole
       catalogue; popular_for is the field built to fix exactly that. */
    if (s.popular_for) {
      return { kind: 'pop',
        text: S('chip_recip', 'Loved for {who}')
          .replace('{who}', popLabel(s.popular_for)) };
    }
    return null;
  }

  /* Shopper-facing name for a feed segment key — shared by the desktop soc
     chip (socialFor above) and the mobile Popular-for line. */
  function popLabel(k) {
    var POP = {
      mums: S('recip_mums', 'Mums'),
      sisters: S('pop_sisters', 'sisters'),
      daughters: S('pop_daughters', 'daughters'),
      friends: S('pop_friends', 'friends'),
      celebration: S('occ_pop_celebration', 'celebrations'),
      milestone: S('occ_pop_milestone', 'milestones'),
      get_well: S('occ_pop_get_well', 'get-wells'),
      thinking_of_you: S('occ_pop_thinking', 'just-because gifts')
    };
    return POP[k] || k;
  }

  /* All of a product's segments as display labels, MOST POPULAR FIRST
     (Tyler 2026-08-03 v7): popular_for is the over-index segment the feed
     computed to lead with; the data-gated recipient claim next; then the
     feed's occasions, which arrive popularity-sorted. Deduped on label. */
  function popCats(it) {
    var s = (CFG.social || {})[it.handle];
    if (!s) return [];
    var out = [], seen = {};
    function push(k) {
      if (!k) return;
      var label = popLabel(k);
      if (seen[label]) return;
      seen[label] = true;
      out.push(label);
    }
    push(s.popular_for);
    if (s.recip_label && s.recip_n) push(s.recip_label);
    (s.occasions || []).forEach(push);
    return out;
  }

  /* Manual ease-out scroll tween, ported from the bundle widget: native
     behavior:'smooth' silently no-ops on scroll-snap containers, so we
     animate scrollLeft ourselves, releasing the snap for the duration.
     Reduced-motion gets the instant jump. */
  function glide(elm, target, dur, onDone) {
    dur = dur || 320;
    if (elm._ppGlide) clearInterval(elm._ppGlide);
    var start = elm.scrollLeft, delta = target - start;
    var noMotion = w.matchMedia &&
      w.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (Math.abs(delta) < 2 || noMotion) {
      elm.scrollLeft = target; elm.style.scrollSnapType = '';
      if (onDone) onDone();
      return;
    }
    elm.style.scrollSnapType = 'none';
    var t0 = Date.now();
    elm._ppGlide = setInterval(function () {
      var pr = Math.min(1, (Date.now() - t0) / dur);
      var e = 1 - Math.pow(1 - pr, 3);
      elm.scrollLeft = start + delta * e;
      if (pr >= 1) {
        clearInterval(elm._ppGlide);
        elm._ppGlide = null;
        elm.style.scrollSnapType = '';
        if (onDone) onDone();
      }
    }, 16);
  }

  function cleanTitle(title) {
    var t = String(title || '');
    var m = /^(Mini\s+)?(.*\S)\s+Pop[\s-]?Up\s+Bouquet\s*$/i.exec(t);
    if (!m) return { name: t, size: null };
    return {
      name: m[2],
      size: m[1] ? S('size_mini', 'Mini size') : S('size_full', 'Full size')
    };
  }

  function upsellCardStyle() {
    /* ROWS EVERYWHERE (Tyler 2026-08-03, supersedes the same-day device
       split — "stop this A/B testing… keep the horizontal cards for now").
       The carousel code stays dormant; a future carousel gets designed on a
       draft theme first. cfg.upsells.cards and the QA key can still force
       it for that work. */
    var forced = (CFG.upsells || {}).cards;
    if (forced === 'rows' || forced === 'carousel') return forced;
    try {
      var qa = w.localStorage.getItem('pp_force_upsell_cards');
      if (qa === 'rows' || qa === 'carousel') return qa;
    } catch (e) { /* storage blocked */ }
    return 'rows';
  }

  /* railMode: square-card carousel; docked: rendering into the dark dock
     host (false only for the empty-cart gallery, which stays in the body —
     a dock with no cart above it is a footer ad, not a suggestion). */
  /* ───────────────────── v2: gift-intelligence sections ─────────────────
     The desktop rail groups the SAME candidate list the carousel already
     produces into headed sections, using data the store already computes —
     the co-add engine, `popular_for` and `occasions` from the nightly
     bundle_social feed, and the `mini-` handle convention. Nothing here is
     hand-curated, and nothing needs a new metafield.

     Every candidate lands in exactly one section (first rule wins), so the
     rail can never show the same product twice, and empty sections are
     dropped rather than rendering a heading over nothing. */
  function v2Sections(cands) {
    /* The section is named after what the shopper is actually holding —
       the most recently added bouquet, which is the one the suggestions
       were anchored on. */
    var anchorName = null;
    if (cart && cart.items.length) {
      for (var i = cart.items.length - 1; i >= 0; i--) {
        if (isMechanismLine(cart.items[i])) continue;
        anchorName = cleanTitle(cart.items[i].product_title ||
                                cart.items[i].title).name;
        break;
      }
    }

    var goes = [], minis = [], byAud = {}, byOcc = {}, audOrder = [], occOrder = [];
    cands.forEach(function (c) {
      var h = String(c.handle || '');
      /* THE MATCHING TWIN leads section one — it is the single strongest
         suggestion the drawer has, and it is about the bouquet already in the
         cart. It is tested FIRST because its handle also starts with `mini-`,
         and letting the mini rule catch it buried it in the Minis section. */
      if (c._miniTwin) { goes.push(c); return; }
      /* EVERY OTHER MINI goes to the Minis section, even when the engine
         picked it. The engine ranks purely on co-purchase lift, which on this
         catalogue fills "Goes with your X" with minis — four out of five in
         the state Tyler hit, on a cart that had just been GIVEN a mini free.
         They are still offered, under their own heading, where a shopper
         browsing for a small add-on will look for them. */
      if (h.indexOf('mini-') === 0) { minis.push(c); return; }
      /* Engine picks belong with the anchor they were computed against;
         only gallery top-ups are free to be regrouped by who or what they
         suit. */
      if (!c._fromGallery) { goes.push(c); return; }
      var s = (CFG.social || {})[h] || {};
      /* `popular_for` holds EITHER an audience (mums/sisters/daughters) or an
         occasion (milestone/get_well/…) — the feed writes whichever segment
         over-indexed. They need different sentences: "Mums love these" reads
         right, "Get-wells love these" does not. Anything not a known
         audience is treated as an occasion, so a new segment key degrades to
         the safer wording rather than producing nonsense. */
      var AUD = { mums: 1, sisters: 1, daughters: 1, friends: 1 };
      if (s.popular_for && AUD[s.popular_for]) {
        if (!byAud[s.popular_for]) { byAud[s.popular_for] = []; audOrder.push(s.popular_for); }
        byAud[s.popular_for].push(c);
        return;
      }
      var occ = s.popular_for || (s.occasions || [])[0];
      if (occ) {
        if (!byOcc[occ]) { byOcc[occ] = []; occOrder.push(occ); }
        byOcc[occ].push(c);
        return;
      }
      goes.push(c);
    });

    /* No section runs longer than this. "Goes with your X" was swallowing
       most of the rail (Tyler 2026-08-26), which buries the headed sections
       underneath it — the whole point of the rail is a shortlist per reason,
       not one long list with a label on top. */
    var SEC_MAX = 5;
    var out = [];
    if (goes.length) {
      out.push({
        title: anchorName
          ? S('sec_goes_with', 'Goes with your {name}').replace('{name}', anchorName)
          : (S('upsells_title', 'Frequently bought together')),
        items: goes
      });
    }
    audOrder.forEach(function (k) {
      out.push({ title: S('sec_audience', '{who} love these')
        .replace('{who}', popLabel(k)), items: byAud[k] });
    });
    occOrder.forEach(function (k) {
      out.push({ title: S('sec_occasion', '{occ} favourites')
        .replace('{occ}', popLabel(k)), items: byOcc[k] });
    });
    if (minis.length) {
      /* The price note is the honest version of the old discount nudge:
         it states what a mini costs rather than promising a rung. */
      var lo = null;
      minis.forEach(function (m) {
        if (m.price != null && (lo === null || m.price < lo)) lo = m.price;
      });
      out.push({
        title: S('sec_minis', 'Mini bouquets'),
        note: lo != null ? S('sec_each', '{price} each').replace('{price}', money(lo)) : null,
        items: minis
      });
    }
    /* Four is the ceiling the design was approved at (the mock ships
       Goes-with / audience / occasion / Minis) — past that the rail stops
       being a shortlist and becomes a catalogue.

       But a section that doesn't make the cut must not take its PRODUCTS with
       it. capN pulls up to 12 candidates for this rail, and gallery top-ups
       spread across four audience keys and four occasion keys, so five to
       eight sections is the normal case — slicing alone silently binned the
       cards the code had just paid to fetch and rank (the Minis section
       first, since it is appended last). Overflow items fold into the final
       kept section instead. */
    out.forEach(function (sec) { sec.items = sec.items.slice(0, SEC_MAX); });
    if (out.length > 4) {
      var keep = out.slice(0, 4);
      var last = keep[keep.length - 1];
      out.slice(4).forEach(function (sec) {
        last.items = last.items.concat(sec.items);
      });
      return keep;
    }
    return out;
  }

  /* Re-arrange a card the existing `row()` builder produced into the v2
     layout. Deliberately a DOM re-shuffle rather than a second card builder:
     every listener, tracking hook and add handler `row()` attached survives,
     which is what keeps this a design change and not a rewrite of the one
     surface whose whole job is AOV. */
  function v2Card(node, isRail, it) {
    var info = node.querySelector('.pp-cart__upsell-info');
    if (!info) return;
    var meta  = node.querySelector('.pp-cart__upsell-meta');
    var pop   = node.querySelector('.pp-cart__upsell-pop');
    var nudge = node.querySelector('.pp-cart__upsell-nudge');
    var price = node.querySelector('.pp-cart__upsell-price');

    /* "Loved for mums" (a sentence fragment) becomes "Popular for [♥ Mums]"
       (a label plus a tag) — the same fact, but shaped so it reads as
       metadata about the product instead of a second product title. */
    /* REWARD NUDGES ARE OUT (Tyler 2026-08-26: "remove the 'Make it a
       matching set' '1 more for 15% off' lines as they take too much
       space" — then, on review, the matching-set line was reinstated as the
       one exception). So: keep the gift-framed reason, drop the rung ones.
       The reward ladder at the top of the drawer already states the rung,
       and repeating it on every card was the redundancy that made the
       carousel too tall. */
    if (nudge && !(it && it._reasonGift)) {
      if (nudge.parentNode) nudge.parentNode.removeChild(nudge);
      nudge = null;
    }

    var chipWrap = null;
    if (pop) {
      /* From the DATA, never by unpicking the rendered string. The old
         approach stripped a literal English "Loved for " prefix, which in any
         other locale leaves the whole translated sentence inside a pill sized
         for one word. popCats() returns the same segments the pop line was
         built from, already localised. */
      var lbl = (it ? (popCats(it) || [])[0] : null) ||
                (pop.textContent || '').replace(/^\s*Loved for\s*/i, '').trim();
      if (lbl) {
        lbl = lbl.charAt(0).toUpperCase() + lbl.slice(1);
        chipWrap = el('span', 'pp-cart__popwrap');
        chipWrap.appendChild(el('span', 'pp-cart__poplbl', S('pop_lead', 'Popular for')));
        var chip = el('span', 'pp-cart__popchip');
        chip.appendChild(icon(ICON.heart));
        chip.appendChild(el('span', null, lbl));
        chipWrap.appendChild(chip);
      }
      if (pop.parentNode) pop.parentNode.removeChild(pop);
    }

    if (isRail) {
      /* Desktop: title+rating, then the gift nudge as a subtitle, then the
         pill, then a hairline sealing price → Add. */
      if (nudge && meta) info.insertBefore(nudge, meta);
      if (chipWrap && meta) info.insertBefore(chipWrap, meta);
      if (price && meta) meta.insertBefore(price, meta.firstChild);
      return;
    }
    /* Phone: the Add row carries exactly ONE signal. A gift nudge outranks
       the audience pill — it is specific to this pairing, the pill is a
       general fact — and showing both wrapped the row and grew the card,
       which is the opposite of what the compact carousel is for. */
    if (meta && !nudge && chipWrap) meta.insertBefore(chipWrap, meta.firstChild);
  }

  function renderUpsells(parent, dockMode, docked) {
    // Cleared FIRST: every early return below must also drop the previous
    // render's hook, or openCart would resync a detached carousel — and the
    // auto-scroll ticker would keep driving a list that is no longer on screen.
    _carResync = null;
    stopUpsellAuto();
    setUpsellTakeover(false);
    var u = CFG.upsells || {};
    /* Either source is sufficient. The old guard required the hand-picked
       `items` list, so with the engine live and no manual list configured the
       rail bailed out before consulting the recommendations at all. */
    var hasEngine = u.recs && Object.keys(u.recs).length;
    var hasManual = u.items && u.items.length;
    var hasGallery = u.gallery && u.gallery.length;
    if (!u.enabled || !cart || (!hasEngine && !hasManual && !hasGallery)) return;

    /* An EMPTY cart used to end at "Continue shopping" — a dead end inside
       the drawer (Tyler 2026-08-02). The engine has no anchor to recommend
       from, so the gallery list (the flagship collection, in its admin sort)
       gives the shopper something to start with instead. */
    var emptyCart = !cart.items.length;
    if (emptyCart && !hasGallery) return;

    /* v2's sectioned desktop rail shows several headed groups, so it needs
       more than the 6 candidates a single carousel row was capped at. The
       number is read in TWO places — the gallery top-up target and the final
       slice — and raising only one starves the other, so it is computed once
       here, above both. */
    var railV2 = V2 && !emptyCart && v2Rail();
    var capN = railV2 ? 12 : (u.max_visible || 6);

    var inCart = {}, inCartProducts = {};
    cart.items.forEach(function (i) {
      inCart[String(i.variant_id)] = true;
      inCartProducts[String(i.product_id)] = true;
    });

    var next = nearestReward(buildTiers());
    var candidates = emptyCart
      ? (u.gallery || []).slice()
      : engineCandidates(u, inCartProducts);
    // Fall back to the hand-picked list when the engine has nothing for this
    // cart — a new product with no co-add history yet, or a market still
    // accumulating data.
    if (!candidates.length && !emptyCart) candidates = (u.items || []).slice();
    /* LAST-RESORT GALLERY (Tyler 2026-08-03 v16). The hand-picked `items` list
       is empty in production, so when the engine has no row for anything in
       the cart the drawer rendered NO upsell section at all — a tall blank
       band above the footer and zero suggestions, which is the worst possible
       state for the one surface whose whole job is AOV.
       It is not a rare edge case either. An anchor is missing whenever the
       product was unavailable at compute time, so it happens to every product
       Tyler RESTOCKS until the next nightly refresh (found exactly that way:
       Mini Cheerful Gerberas came back in stock, went in a cart alone, and the
       suggestions vanished), and to every genuinely new product.
       The gallery is the same flagship list the empty cart already falls back
       to, so this needs no new data — only permission to use it here. In-cart
       products are filtered out immediately below, as for any other source. */
    if (!candidates.length && !emptyCart) candidates = (u.gallery || []).slice();
    candidates = candidates.filter(function (it) { return !inCart[String(it.variant_id)]; });

    /* TOP UP A THIN ENGINE RESULT FROM THE GALLERY (Tyler 2026-08-04: "the
       upsell box only shows 3 products when spotted lilies are added. there
       should be many upsell products showing, not just 3").

       The engine stores at most `per_anchor` handles per anchor, and it only
       has as many as the co-purchase data actually earned. Spotted Lilies
       (8418647703809) has exactly THREE — `mini-spotted-lilies`,
       `mini-cheerful-gerberas`, `daffodil-bloom` — so a cart holding only
       that bouquet could never show more, no matter how much room the UI had.
       The gallery already existed as a fallback but only for a TOTALLY empty
       result, so "thin" fell through the gap between "some" and "none".

       Topping up rather than raising the engine's cap is the fix that works
       today: the cap is bounded by Shopify's 20-unique-handle `all_products`
       limit, and a thin anchor is thin because the DATA is thin — no cap
       change conjures pairings that were never scored.

       Ranked strictly below anything the engine chose (the −0.5 nudge in the
       boost pass): a real co-purchase pairing outranks "this is one of our
       flagship bouquets" at equal reward reason. Copied, not referenced —
       the boost pass writes `_reason`/`_boost` onto these objects and they
       live in CFG across renders. */
    if (!emptyCart) {
      var wantN = Math.max(1, capN);
      if (candidates.length < wantN) {
        var seenCand = {};
        candidates.forEach(function (c) { seenCand[String(c.variant_id)] = true; });
        (u.gallery || []).forEach(function (g) {
          if (candidates.length >= wantN) return;
          if (seenCand[String(g.variant_id)]) return;
          if (inCart[String(g.variant_id)]) return;
          if (inCartProducts[String(g.product_id)]) return;
          seenCand[String(g.variant_id)] = true;
          var copy = {};
          for (var gk in g) {
            if (Object.prototype.hasOwnProperty.call(g, gk)) copy[gk] = g[gk];
          }
          copy._fromGallery = true;
          candidates.push(copy);
        });
      }
    }

    /* GUARANTEED FOREVER FRIEND (Tyler 2026-08-27: "properly showing forever
       friends when different full size and mini size bouquets have been
       added"). The engine only pins a Friend on three anchors, so most
       bouquet carts never offered one. When the cart holds any Pop-Up
       Bouquet — /cart.js `product_type` covers full and mini alike — exactly
       ONE Friend joins the candidates. An engine-surfaced Friend keeps its
       slot (measured beats guaranteed), and a Friend already in the cart
       stands down entirely: the slot's job is first attach, not a second.
       Choice is deterministic (the chooseGift precedent): a Friend whose
       flower is named in a cart line's handle wins (sunflower-smiles →
       Sunny); ties and no-matches fall to serialized order, which is the
       collection's admin sort — the merchandising lever stays in Shopify.
       Injected BEFORE the boost pass so it earns its true reward reason
       (a Friend really can cross the free-shipping line). */
    if (!emptyCart && (u.friends || []).length) {
      var FF_SUFFIX = '-forever-friend';
      var STEMS = { sunflowers: 'sunflower', tulips: 'tulip',
                    daffodils: 'daffodil', daisies: 'daisy',
                    lavender: 'lavender', hearts: 'heart', roses: 'rose',
                    gerberas: 'gerbera', lilies: 'lilies' };
      var hasBouquet = false, holdsFriend = false;
      cart.items.forEach(function (i) {
        if (isMechanismLine(i)) return;
        if (i.product_type === 'Pop-Up Bouquet') hasBouquet = true;
        if (i.product_type === 'Crochet Forever Friend') holdsFriend = true;
      });
      var railHasFriend = candidates.some(function (c) {
        var h = String(c.handle || '');
        return h.length > FF_SUFFIX.length &&
               h.indexOf(FF_SUFFIX) === h.length - FF_SUFFIX.length;
      });
      if (hasBouquet && !holdsFriend && !railHasFriend) {
        var ffPick = null, ffBest = -1;
        u.friends.forEach(function (f, idx) {
          if (inCart[String(f.variant_id)]) return;
          if (inCartProducts[String(f.product_id)]) return;
          var hit = 0;
          (f.match || []).forEach(function (tag) {
            var stem = STEMS[tag];
            if (!stem) return;
            cart.items.forEach(function (i) {
              if (String(i.handle || '').indexOf(stem) !== -1) hit = 2;
            });
          });
          /* The per-index epsilon breaks ties toward the front of the
             collection without ever beating a real flower match. */
          var score = hit - idx / 100;
          if (score > ffBest) { ffBest = score; ffPick = f; }
        });
        if (ffPick) {
          var ffRow = {};
          for (var ffk in ffPick) {
            if (Object.prototype.hasOwnProperty.call(ffPick, ffk)) ffRow[ffk] = ffPick[ffk];
          }
          ffRow._friendSlot = true;
          candidates.push(ffRow);
        }
      }
    }

    /* Reward-proximity ranking + reasons.
     *
     * "$8.99, ⊕" is a weak ask. "Add this — that's 3 items, 15% off
     * everything" is a specific, verifiable one, and it's where the AOV comes
     * from: an item that tips the cart over a tier is worth more to both sides
     * than a generically similar product.
     *
     * Every reason must be TRUE for that exact item at that exact cart state,
     * which is why they're computed here from the live cart rather than stored
     * with the recommendation. A reason that doesn't survive checkout is worse
     * than no reason at all. */
    candidates.forEach(function (it) {
      if (emptyCart) {
        // No cart, no reward proximity — a "reason" against nothing would
        // just be noise. The product speaks for itself here.
        it._reason = null;
        it._boost = 0;
        return;
      }
      var r = rewardReasonFor(it, next);
      /* THE SIBLING MINI outranks every reason except an actual unlock.
         What it beats is the "3 away from 15% off minis" line the reward
         logic would otherwise produce for a first mini — technically true and
         actively discouraging, since it answers a question about bulk to
         someone who hasn't yet grasped that a small version exists at all.
         The useful sentence is what the product IS. It yields to boost 100
         (a discount landing right now), because money beats description.
         Boost 85 also floats it above the merely-similar, which is the whole
         point: this is the one recommendation we know is relevant. */
      if (it._miniTwin && r.boost < 100) {
        /* "…of this bouquet" only worked when there was exactly one bouquet to
           point at. With three signatures in the cart the row appears under a
           list the shopper can't fully see — the drawer scrolls — so "this"
           refers to nothing they can identify. "The 14cm mini version" makes
           the same claim without needing an antecedent. */
        it._reason = S('reason_mini_of', 'Make it a matching set');
        /* GIFT-framed, not reward-framed — the one nudge v2 keeps.
           Flagged here rather than string-matched later, because the
           string is translated and comparing it would silently drop the
           line in every non-English market. */
        it._reasonGift = true;
        /* 84, deliberately UNDER the strong threshold (85): fused-on-button
           is the money-now treatment ("Unlocks 15% off"); as a fused label
           this line made the Add button two lines tall and it crowded the
           pills row (Tyler 2026-08-03). Soft text keeps the rank benefit
           without the bulk. */
        /* The fractional recency nudge breaks ties BETWEEN twins only: the
           twin of the newest cart line ranks first (that's the mini the
           shopper expects to see right after an add). Bounded well under
           85, so it can never cross a threshold band. */
        it._boost = 84 + ((it._twinIdx || 0) + 1) / 1000;
        return;
      }
      it._reason = r.text || it.reason || null;
      it._boost = r.boost;
      /* Identity fallback (Tyler 2026-08-03): when no reward reason exists
         (all rewards banked, or nothing proximate), a design tagged
         'australian natives' says what it IS. A fact, not a claim — no
         data needed to stand behind it, unlike "Aussie favs", which would
         assert popularity the numbered pills already prove better. Never
         outranks a reward reason: money beats identity. */
      if (!it._reason && it.native) {
        it._reason = S('reason_native', 'Australian natives');
        it._boost = 0;
      }
    });
    /* Gallery top-ups rank strictly below anything the engine chose. Applied
       AFTER the boost pass so they still earn their true reward reason (a
       topped-up bouquet really does unlock the next tier) — this only breaks
       the tie, it doesn't demote them out of their band. */
    candidates.forEach(function (it) {
      if (it._fromGallery) it._boost = (it._boost || 0) - 0.5;
    });
    /* The Friend's visibility floor: 83 sits under the matching twin (84)
       and under money-now (100), above generic reward-progress lines — so
       the one guaranteed Friend is always inside the visible band without
       ever outranking the two suggestions that beat it honestly. A real
       unlock the Friend itself triggers still wins the max(). */
    candidates.forEach(function (it) {
      if (it._friendSlot) it._boost = Math.max(it._boost || 0, 83);
    });
    candidates.sort(function (a, b) { return (b._boost || 0) - (a._boost || 0); });

    /* FULL-SIZE FIRST (Tyler 2026-08-26). The engine ranks on co-purchase
       lift alone, which on this catalogue surfaces a lot of minis — and a
       mini is the worst thing to advertise in the AU arm that just GAVE the
       shopper one free. It also pulls against the ladder: the next rung is 6
       full-size bouquets, so a mini adds basket value without moving them
       toward it.
       The matching twin is the exception and keeps its place at the top —
       "make it a matching set" is about the bouquet they already chose, not
       about buying a smaller one. Everything else full-size ranks above the
       remaining minis, which still get their own section further down.
       Reordering happens BEFORE the cap so full-size actually survive it. */
    if (V2 && !emptyCart) {
      var twinFirst = null, fullSize = [], smalls = [];
      candidates.forEach(function (it) {
        if (it._miniTwin && !twinFirst) { twinFirst = it; return; }
        if (String(it.handle || '').indexOf('mini-') === 0) smalls.push(it);
        else fullSize.push(it);
      });
      candidates = (twinFirst ? [twinFirst] : []).concat(fullSize, smalls);
    }

    candidates = candidates.slice(0, capN);
    if (!candidates.length) return;

    // Name the recipient when we genuinely know it — see recipientName().
    var who = recipientName();

    /* ── WHICH TREATMENT (decided per render, before anything is built) ──
     *
     * PHONES (≤560px) keep the full-width snap carousel shipped 2026-08-03.
     *
     * DESKTOP (>560px) changed 2026-08-04: it used to be stacked rows, two
     * visible behind a "Show N more". It is now the SAME two ideas the phone
     * has, but wired to the chevron instead of a second control —
     *   minimised (the default) → a single row of carousel cards that
     *                             auto-scrolls, so the dock costs one row;
     *   expanded               → every candidate as a stacked row, the section
     *                             growing up to the reward band's bottom edge.
     * That retires "Show N more": one control, two honest states, and the
     * expanded state shows everything rather than two-at-a-time.
     *
     * These are computed HERE, above `wrap`, because the wrap's own class list
     * depends on them.
     *
     * TWO INDEPENDENT AXES, and conflating them was the 2026-08-04 mistake:
     *   `rail`     — is the LIST a horizontal scroller? (dock arm + minimised
     *                desktop). Drives the carousel chrome, snap, arrival-based
     *                view counting, post-add scroll continuity.
     *   `dockMode` — do the CARDS use the 158px column grammar? (dock A/B arm
     *                ONLY). Desktop keeps the ORIGINAL horizontal row card —
     *                photo flush left, title and price on one line, reason
     *                fused on the square Add — just laid side by side rather
     *                than stacked (Tyler: "keep the original horizontal style
     *                upsell cards and just turn them into a single row
     *                carousel"). Turning the rail on had silently reflowed
     *                them, because one flag was doing both jobs. */
    var phoneCar = !dockMode && !emptyCart && w.matchMedia &&
      w.matchMedia('(max-width: 560px)').matches;
    var deskCar = !dockMode && !emptyCart && !phoneCar;
    /* v2 never takes the expanded-stack arm. `deskFull` drives
       setUpsellTakeover, which sets `display:none` on the cart body — right
       for a full-width takeover, catastrophic for a side rail whose whole
       point is that the cart stays visible beside it. A shopper who left
       `pp_upsells_desk_expanded` set before arming v2 would otherwise open
       the drawer to a hidden cart. */
    var deskFull = deskCar && !V2 && deskUpsellsExpanded();
    var rail = dockMode || (deskCar && !deskFull);

    /* ONE ROW AT A TIME, paged by arrows (Tyler 2026-08-03 mobile fix).
     *
     * The stacked-rows list (two rows + "Show more") was a fixed, non-scrolling
     * zone between body and footer — on a phone it swallowed the drawer and the
     * actual cart lines were crushed to a sliver. A single dense row keeps the
     * dock a third of that height on every viewport, and the ◀ ▶ arrows make
     * the rest of the candidates reachable without a gesture nobody discovers.
     * The row keeps its deliberate not-a-cart-line grammar (smaller thumb, no
     * stepper, Add instead of a quantity control).
     *
     * The row rail is a REAL scroll-snap carousel (Tyler 2026-08-03 v2:
     * "needs to be swipeable on mobile and desktop") — every candidate
     * renders as a full-width snap page, so touch swipes natively, a mouse
     * can drag it, and the arrows just glide the same scroller. */

    var wrap = el('div', 'pp-cart__upsells' + (docked ? ' is-dock' : '') +
      (rail ? ' is-rail' : '') + (dockMode ? ' is-cards' : '') +
      (deskFull ? ' is-deskfull' : ''));
    var head = el('div', 'pp-cart__upsells-head');
    /* Address the recipient by name when the shopper has told us who it's for.
       "Something else for Anne?" is a different proposition from "Pairs
       beautifully with" — it reframes the upsell as finishing the gift rather
       than spending more. Falls back silently when we don't know. */
    head.appendChild(el('div', 'pp-cart__upsells-title', emptyCart
      ? S('empty_gallery_title', 'Customers love these')
      : upsellHeading(u, who)));

    /* MINIMISE (Tyler 2026-08-03 v14): "so users can minimise the section and
       see their in-cart products more clearly".

       A disclosure chevron in the header, not an ✕ — the section must be
       re-openable, and a close button reads as dismiss-forever. The heading
       stays visible when collapsed so the control that reopens it is exactly
       where the shopper collapsed it.

       NOT offered on an empty cart: there the upsells ARE the content, and a
       control whose only outcome is an empty drawer isn't a choice worth
       giving. There are also no cart lines to see more clearly, which is the
       entire point of the feature.

       The state PERSISTS in localStorage, and that is the whole engineering
       problem here rather than a nicety: this drawer re-renders on every cart
       change, so a collapse held only in a local variable would spring back
       open the next time the shopper changed a quantity — the section would
       feel broken precisely when it's being used most. */
    /* `noteOpen` forces collapsed while a gift-note editor is up (Tyler
       2026-08-03 v14) — see openLineNoteEditor. It is deliberately OR'd in
       rather than written to storage: the shopper didn't ask to hide
       suggestions, the note editor borrowed the space, so their own setting
       must survive the loan. render() clears the flag. */
    /* DESKTOP (2026-08-04) gives the same chevron a second meaning — see the
       treatment block above. `is-collapsed` (the full hide) still exists there,
       but ONLY as the gift-note editor's space loan; the shopper's own click
       swaps carousel ⇄ stack instead, because on desktop the carousel already
       IS the compact state and hiding the section outright would spend the
       control on an empty band. */
    var collapsible = !emptyCart;
    /* v2 HIDES the chevron on phones and has no room for it in the rail, so
       honouring a stored collapse would be a trap: the only control that can
       undo it is display:none, its key has no other writer, and the shopper
       would get a heading band with zero cards on every open, permanently.
       v2 also refuses the gift-note "space loan" — the note editor borrows the
       dock's height on the old layout, but the v2 rail is absolutely
       positioned OUTSIDE the panel, so collapsing it frees nothing and just
       blanks a styled 340px column mid-edit. */
    var collapsed = V2
      ? false
      : (collapsible && !deskCar && (upsellsCollapsed() || noteOpen));
    if (deskCar && noteOpen && !V2) collapsed = true;   // loan only, never persisted
    _upsellSetCollapsed = null;
    if (collapsible) {
      var tgl = el('button', 'pp-cart__upsells-toggle');
      tgl.type = 'button';
      tgl.setAttribute('aria-controls', 'pp-cart-upsell-list');
      tgl.appendChild(icon('M6 9l6 6 6-6'));      // chevron; CSS rotates it
      var applyCollapsed = function () {
        wrap.classList.toggle('is-collapsed', collapsed);
        var isOpen = deskCar ? deskFull : !collapsed;
        tgl.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        var lbl = deskCar
          ? (deskFull
              ? S('upsells_collapse', 'Show fewer suggestions')
              : S('upsells_expand', 'Show all suggestions'))
          : (collapsed
              ? S('upsells_show', 'Show suggestions')
              : S('upsells_hide', 'Hide suggestions'));
        tgl.setAttribute('aria-label', lbl);
        tgl.title = lbl;
      };
      tgl.addEventListener('click', function () {
        if (deskCar) {
          /* The two desktop states are different DOM, not one list under a
             class — carousel cards and stacked rows share no layout. So the
             toggle REBUILDS the section rather than flipping a class. That
             also re-arms the auto-scroll ticker and reassigns _carResync,
             both of which belong to whichever treatment is now on screen. */
          setDeskUpsellsExpanded(!deskFull);
          parent.textContent = '';
          renderUpsells(parent, dockMode, docked);
          if (_carResync) _carResync();
          return;
        }
        collapsed = !collapsed;
        setUpsellsCollapsed(collapsed);
        applyCollapsed();
        /* Re-measure on EXPAND. The carousel and rail thumb size themselves
           from clientWidth, and while collapsed every width reads 0 — the
           same measure-while-hidden trap that produced the solid-green
           progress bar when the drawer rendered behind [hidden]. Reusing the
           hook that already exists for it. */
        if (!collapsed && _carResync) _carResync();
      });
      /* Let the note editor drive the same control the shopper drives, so
         the chevron and aria-expanded never disagree with what's on screen. */
      _upsellSetCollapsed = function (v) {
        /* v2 NEVER collapses. The gift-note editor borrows the dock's height
           on the old layout; v2's rail is absolutely positioned outside the
           panel, so collapsing it frees the editor nothing and simply blanks
           the whole 340px column for the length of the edit (Tyler, live test
           2026-08-26). Setting the initial `collapsed` false was not enough —
           this setter is what openLineNoteEditor calls. */
        if (V2) return;
        if (collapsed === v) return;
        collapsed = v;
        /* Desktop: the loan must actually free the body, so a shopper sitting
           in the expanded stack has to leave it. Rebuild rather than reclass —
           the caller has already set `noteOpen`, so the fresh render collapses
           itself AND drops the takeover, without ever writing the shopper's
           own choice away. render() clears noteOpen and it comes back. */
        if (deskCar && v && deskFull) {
          parent.textContent = '';
          renderUpsells(parent, dockMode, docked);
          return;
        }
        applyCollapsed();
        // _carResync re-arms the ticker for rail treatments; nothing to arm
        // for a stack. Collapsing must silence it either way.
        if (!collapsed) { if (_carResync) _carResync(); }
        else stopUpsellAuto();
      };
      head.appendChild(tgl);
      applyCollapsed();
    }
    /* Truth of what's on screen, not of the stored preference: a shopper who
       left the stack expanded and then opened a gift note is collapsed right
       now, and the body must come back for them. */
    setUpsellTakeover(deskFull && !collapsed);
    wrap.appendChild(head);

    /* An EMPTY cart is stacked rows on EVERY width (Tyler 2026-08-03 v13).
       The carousel exists to stop the upsell zone swallowing a drawer that
       still has cart lines to show — on an empty cart there are no lines to
       protect, so the constraint it solves doesn't exist, and a rail would
       ask the shopper to page through a catalogue one card at a time when the
       whole drawer is free. The empty state is the one moment the upsells ARE
       the content. (That's why `deskCar`/`phoneCar` both require !emptyCart.) */
    var list = el('div', 'pp-cart__upsell-list');
    list.id = 'pp-cart-upsell-list';   // aria-controls target for the minimise toggle
    var railThumb = null;
    var carScroll = null, arrowPrev = null, arrowNext = null;
    if (rail) {
      /* NO EDGE FADES (Tyler 2026-08-04: "remove the faded sides of the upsell
         cards"). They were the bundle widget's "content continues" hint, but
         this rail now has explicit arrows AND a cut-off card at the edge —
         two affordances that say it better, without washing out the product
         photography they sit on top of. The wrapper stays as the positioning
         context; the thumb below still reports position. */
      var carwrap = el('div', 'pp-cart__upsell-carwrap');
      carwrap.appendChild(list);
      wrap.appendChild(carwrap);

      /* ARROWS (Tyler 2026-08-04: "desktop needs left/right arrow buttons to
         swipe the upsell cards"). A mouse has no swipe — on a phone the rail
         IS the control, which is why the phone arm deliberately has none, but
         on desktop dragging a scroller is a gesture most people never try.

         They FLANK THE PROGRESS THUMB rather than overlaying the rail edges.
         Edge-overlay is the more familiar carousel pattern, but the cards are
         428px in a 472px list, so a left arrow would sit on the product photo
         and a right arrow directly on top of the Add button — putting a
         decorative control over the one thing the section exists to get
         clicked. The thumb row is empty, centred, and already reads as
         carousel chrome. */
      var ctl = el('div', 'pp-cart__upsell-carctl');
      arrowPrev = el('button', 'pp-cart__upsell-arrow is-prev');
      arrowPrev.type = 'button';
      arrowPrev.setAttribute('aria-label', A11Y('upsell_prev', 'Previous suggestions'));
      arrowPrev.appendChild(icon('M15 18l-6-6 6-6'));
      carScroll = el('div', 'pp-cart__upsell-cscroll');
      railThumb = el('div', 'pp-cart__upsell-cthumb');
      carScroll.appendChild(railThumb);
      arrowNext = el('button', 'pp-cart__upsell-arrow is-next');
      arrowNext.type = 'button';
      arrowNext.setAttribute('aria-label', A11Y('upsell_next', 'More suggestions'));
      arrowNext.appendChild(icon('M9 18l6-6-6-6'));
      ctl.appendChild(arrowPrev);
      ctl.appendChild(carScroll);
      ctl.appendChild(arrowNext);
      wrap.appendChild(ctl);
    } else if (phoneCar) {
      /* Phone snap carousel: the list itself is the control — native touch
         swipe, next card peeking in from the right, the bundle rail's slim
         progress thumb below. No arrows (Tyler 2026-08-03 v3). */
      var nav = el('div', 'pp-cart__upsell-carnav');
      nav.appendChild(list);
      wrap.appendChild(nav);
      carScroll = el('div', 'pp-cart__upsell-cscroll');
      railThumb = el('div', 'pp-cart__upsell-cthumb');
      carScroll.appendChild(railThumb);
      wrap.appendChild(carScroll);
    } else {
      /* Stacked rows: the expanded desktop view, and the empty-cart gallery. */
      wrap.appendChild(list);
    }
    /* One snap page = one card = the list's full width; the 8px flex gap
       (base .pp-cart__upsell-list rule) is part of the stride. */
    function carStride() {
      var card = list.firstChild;
      return card ? card.offsetWidth + 8 : (list.clientWidth || 1);
    }
    function carCur() {
      var s = carStride();
      return s > 0 ? Math.round(list.scrollLeft / s) : 0;
    }
    /* One line, guaranteed (Tyler v7: "must fit in the product card and not
       turn into two lines or push anything out the way"): try the richest
       text first, step down until it measures inside the card. Unmeasured
       (drawer hidden) keeps the richest guess — the resync/settle passes
       re-run this once widths are real. CSS nowrap+ellipsis is the final
       backstop, so a wrap is impossible even mid-step. */
    function fitPopLine(elm) {
      var cats = elm._ppCats || [];
      if (!cats.length || elm._ppFitted) return;
      function line(txt) {
        return S('chip_recip', 'Loved for {who}').replace('{who}', txt);
      }
      /* ONE CATEGORY, always (Tyler 2026-08-04: "make sure the texts...
         dont include too may gift categories/giftees (mum, sister, aunt etc)
         that the text runs out of the upsell card or the text turns into two
         or more rows of text").

         The ladder used to OPEN on the richest form — "Loved for get-wells,
         celebrations + more" — and only step down under measurement pressure.
         Giving this voice its own full-width line removed that pressure, so
         the rich form would now win everywhere, which is the opposite of what
         was asked. Naming one segment is also the stronger claim: it is the
         one the feed says this design over-indexes in, and a list of four
         reads as "popular with everyone", i.e. nothing.

         The remaining rungs are pure fit insurance: any OTHER category from
         the product's own occasions, shortest first. All equally true, so a
         shorter true claim beats a truncated truer one — that is what fixed
         "Loved for just-bec…" on Mini Spotted Lilies, whose top segment
         labels as the very long "just-because gifts". */
      var tries = [line(cats[0])];
      cats.slice(1)
        .sort(function (a, b) { return a.length - b.length; })
        .forEach(function (c) { tries.push(line(c)); });
      for (var i = 0; i < tries.length; i++) {
        elm.textContent = tries[i];
        if (elm.clientWidth <= 0) return;   // unmeasured — refit on resync
        if (elm.scrollWidth <= elm.clientWidth + 1) {
          elm._ppFitted = true;             // measured fit — don't re-thrash
          return;
        }
      }
      /* NOTHING fit. Land on the SHORTEST candidate, not wherever the loop
         happened to stop — the tries are ordered richest-first, and since the
         last-resort block appends other categories ascending by length, the
         final entry is the LONGEST of them. Falling out of the loop therefore
         used to leave the worst possible choice on screen (caught on the
         harness at a 400px drawer: the ladder degraded correctly and then
         put "Loved for just-because gifts" back). CSS ellipsis still
         backstops, but it now truncates the least text possible. */
      var shortest = tries[0];
      for (var t = 1; t < tries.length; t++) {
        if (tries[t].length < shortest.length) shortest = tries[t];
      }
      elm.textContent = shortest;
      elm._ppFitted = true;                 // shortest form + CSS ellipsis
    }
    function fitPopLines() {
      // Both voices measure themselves: the phone's dedicated Popular-for line
      // and the desktop soc line beside the Add button (2026-08-04).
      var els = list.querySelectorAll('.pp-cart__upsell-pop, .pp-cart__upsell-soc');
      for (var i = 0; i < els.length; i++) fitPopLine(els[i]);
    }
    function carSync() {
      if (!phoneCar) return;
      syncRailThumb();
      fitPopLines();
      /* Views are emitted on ARRIVAL, not render — the carousel paints every
         candidate up front and emitting them all would inflate view counts
         (same funnel-pollution logic as the dock arm). seenUpsells dedupes. */
      var it = candidates[carCur()];
      if (it) emitView(it, carCur());
    }
    if (phoneCar) {
      var carT = null;
      list.addEventListener('scroll', function () {
        syncRailThumb(); // cheap style write — live, not throttled
        if (carT) return;
        carT = setTimeout(function () { carT = null; carSync(); }, 140);
      });
    }
    /* "Show N more" is RETIRED (Tyler 2026-08-04). It only ever existed on
       desktop stacked rows, to stop six suggestions pushing the cart lines
       off-screen — and the header chevron now does that job better: minimised
       is a single carousel row (cheaper than two stacked rows), expanded shows
       every candidate at once (more than "Show more" ever revealed in one
       click). Two controls for one axis was the redundancy. The CSS and the
       more_cta/less_cta strings are left in place; they cost nothing and the
       translations are already built. */
    function syncRailThumb() {
      if (!railThumb) return;
      var max = list.scrollWidth - list.clientWidth;
      var frac = list.scrollWidth > 0 ? list.clientWidth / list.scrollWidth : 1;
      /* UNMEASURED (drawer still [hidden] → every width reads 0): frac
         would come out 1 and the thumb painted FULL — the "solid green
         bar on load" Tyler kept seeing. The geometry is known without
         measuring: n near-full-width cards ≈ 1/n visible. Slightly under
         the true fraction (the peek), corrected by the next real sync. */
      if (list.scrollWidth <= 0 || list.clientWidth <= 0) {
        var n = list.children.length;
        frac = n > 0 ? 1 / n : 1;
        max = 0;
      }
      var left = max > 0 ? (list.scrollLeft / max) * (1 - frac) : 0;
      railThumb.style.width = Math.max(18, frac * 100) + '%';
      railThumb.style.left = (left * 100) + '%';
      syncArrows(max);
    }

    /* Arrows reflect where the rail actually is: disabled at each end rather
       than wrapping. Auto-scroll loops home because it is ambient and a dead
       end would just stop it; an arrow is aimed, and a "next" that silently
       jumps back to the first card reads as a glitch. */
    function syncArrows(max) {
      if (!arrowPrev) return;
      if (max == null) max = list.scrollWidth - list.clientWidth;
      var atStart = list.scrollLeft <= 2;
      var atEnd = max <= 2 || list.scrollLeft >= max - 2;
      arrowPrev.disabled = atStart;
      arrowNext.disabled = atEnd;
      // Nothing to page through at all: the pair is chrome without a job.
      var idle = max <= 2;
      arrowPrev.hidden = idle;
      arrowNext.hidden = idle;
    }

    /* One card per press, measured off the DOM rather than a stride constant —
       the row card is a percentage width, so its pixel size moves with the
       drawer. Falls back to a viewport-width page if the cards can't be
       measured (drawer still hidden). */
    function arrowStep(dir) {
      var max = list.scrollWidth - list.clientWidth;
      if (max <= 0) return;
      var cards = list.querySelectorAll('.pp-cart__upsell');
      var target = null;
      if (dir > 0) {
        for (var i = 0; i < cards.length; i++) {
          if (cards[i].offsetLeft > list.scrollLeft + 2) { target = cards[i].offsetLeft; break; }
        }
        if (target == null) target = max;
      } else {
        for (var j = cards.length - 1; j >= 0; j--) {
          if (cards[j].offsetLeft < list.scrollLeft - 2) { target = cards[j].offsetLeft; break; }
        }
        if (target == null) target = 0;
      }
      glide(list, Math.max(0, Math.min(max, target)), 340, syncRailThumb);
    }
    if (arrowPrev) {
      /* Using an arrow is the shopper taking the wheel, exactly like dragging
         the rail — so it retires the auto-scroll for this drawer session. The
         listener on `list` doesn't see these (the buttons are siblings), so
         the ticker is stopped explicitly. */
      arrowPrev.addEventListener('click', function () {
        _upsellAutoKilled = true; stopUpsellAuto(); arrowStep(-1);
      });
      arrowNext.addEventListener('click', function () {
        _upsellAutoKilled = true; stopUpsellAuto(); arrowStep(1);
      });
    }

    function emitView(it, idx) {
      if (seenUpsells[it.variant_id]) return;
      seenUpsells[it.variant_id] = true;
      var socView = socialFor(it);
      track.emit('cart_upsell_view',
        { variant_id: it.variant_id, product_id: it.product_id },
        /* `cards` reports the treatment that was actually on screen. Since
           2026-08-04 minimised DESKTOP reports 'carousel' too — it used to be
           the dock A/B's arm label only, so don't read it as the arm when
           comparing against pre-2026-08-04 data. */
        { title: it.title, reason: it._reason, position: idx,
          cards: rail ? 'carousel' : 'rows',
          soc: socView ? socView.kind : null,
          anchor: it._anchor, anchors: it._anchors,
          clarity_tag: 'upsell_shown' });
    }

    /* Rail views are emitted on VISIBILITY, not render: the rail paints all
       candidates at once, and emitting them all would inflate view counts
       ~3x against the rows treatment — the funnels would be polluted. A card
       counts as viewed when its left edge enters the scrollport. This is also
       what makes the desktop auto-scroll honest: auto-advanced cards emit on
       arrival through the same scroll listener, no separate path. */
    var railPending = [];
    function railCheckViews() {
      /* v2's rail scrolls VERTICALLY, so the horizontal maths below would
         mark every card viewed on first paint (offsetLeft is 0 for all of
         them) and inflate the funnel exactly the way this gate exists to
         prevent. Same rule, rotated: a card counts once its top edge enters
         the scrollport. */
      if (railV2) {
        var vEdge = list.scrollTop + list.clientHeight;
        railPending.forEach(function (p) {
          if (!p.done && p.node.offsetTop < vEdge - 8) {
            p.done = true;
            emitView(p.it, p.idx);
          }
        });
        return;
      }
      var edge = list.scrollLeft + list.clientWidth;
      railPending.forEach(function (p) {
        if (!p.done && p.node.offsetLeft < edge - 8) {
          p.done = true;
          emitView(p.it, p.idx);
        }
      });
    }

    function paint() {
      list.textContent = '';
      railPending = [];
      /* Snapshot BEFORE the restore block below nulls it — the peek guard
         must know this repaint is a post-add one (review find, 2026-08-03). */
      var wasAfterAdd = !!_railAfterAdd;
      /* EVERY treatment now shows every candidate — the rail because you can
         scroll it, the expanded desktop stack because that is the whole point
         of expanding, the empty gallery because the upsells are the content.
         (candidates was already capped at u.max_visible ≈ 6 upstream.) */
      var shown = candidates;
      if (railV2) {
        /* Sectioned rail: the same cards, under headings computed from
           the gift data. `idx` stays the candidate's ORIGINAL index so
           the view/add funnels line up with the carousel's numbering. */
        var pos = 0;
        v2Sections(shown).forEach(function (sec) {
          var h = el('div', 'pp-cart__upsell-sec');
          h.appendChild(el('span', null, sec.title));
          if (sec.note) h.appendChild(el('span', 'pp-cart__upsell-secnote', sec.note));
          list.appendChild(h);
          sec.items.forEach(function (it) {
            list.appendChild(row(it, shown.indexOf(it)));
            pos++;
          });
        });
        if (!pos) shown.forEach(function (it, idx) { list.appendChild(row(it, idx)); });
        /* paint() runs while the tree is still DETACHED (parent.appendChild
           happens at the end of renderUpsells), so every offsetTop reads 0 and
           the visibility gate can never fire. The horizontal rail was covered
           by the auto-scroll ticker's scroll events; v2 turns that off, so
           without this the rail stops reporting views after any cart change
           and under-reports against the arm it is measured against. */
        setTimeout(function () { try { railCheckViews(); } catch (e) {} }, 0);
      } else {
        shown.forEach(function (it, idx) { list.appendChild(row(it, idx)); });
      }
      if (rail) {
        if (_railAfterAdd) {
          /* Post-add continuity + advance (bundle behavior): restore the old
             scroll position — the next candidate now occupies the added
             card's slot — then, once the add has visibly registered, glide
             that card's left edge into alignment. */
          var stash = _railAfterAdd;
          _railAfterAdd = null;
          list.scrollLeft = stash.left;
          setTimeout(function () {
            var cards = list.querySelectorAll('.pp-cart__upsell');
            for (var ci = 0; ci < cards.length; ci++) {
              if (cards[ci].offsetLeft >= stash.cardLeft - 8) {
                glide(list, Math.max(0, cards[ci].offsetLeft - 2), 300, syncRailThumb);
                return;
              }
            }
          }, 350);
        }
        railCheckViews();
        syncRailThumb();
        /* The peek nudge is the "this scrolls" affordance for treatments with
           no other motion. DESKTOP has the auto-scroll ticker doing that job
           from 5s, and running both would be two unexplained twitches in the
           first six seconds — so desktop skips the peek. */
        if (!_railPeeked && !deskCar) {
          _railPeeked = true;
          if (!wasAfterAdd && list.scrollLeft === 0 &&
              !(w.matchMedia &&
                w.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
            setTimeout(function () {
              // Still at rest and still overflowing? (She may have swiped.)
              if (list.scrollLeft > 0 ||
                  list.scrollWidth <= list.clientWidth) return;
              glide(list, 30, 260, function () {
                setTimeout(function () {
                  glide(list, 0, 300, syncRailThumb);
                }, 160);
              });
            }, 600);
          }
        }
      }
      if (phoneCar) {
        // A full green bar under a single, unscrollable card reads as
        // broken chrome — the thumb only earns its row when there's travel.
        // Inline display, not [hidden]: the class's display would override
        // the UA's [hidden] rule (same trap as div:empty in this file).
        if (carScroll) {
          carScroll.style.display = candidates.length < 2 ? 'none' : '';
        }
        carSync();
        /* Settle pass: whichever way this paint reached the screen (open
           hook, post-add repaint, theme quirks), re-measure once layout
           has definitely happened — the count-based estimate above then
           gets replaced by the real fraction. */
        setTimeout(carSync, 350);
      }
      if (rail && carScroll) {
        // A full green bar and two dead arrows under a single, unscrollable
        // card read as broken chrome — the control row only earns its space
        // when there is somewhere to go. Hide the whole row, not just the
        // thumb, or the arrows sit alone in an otherwise empty strip.
        var ctlRow = carScroll.parentNode;
        if (ctlRow) ctlRow.style.display = candidates.length < 2 ? 'none' : '';
      }
      /* Re-arm after every repaint (an add rebuilds the whole list, and the
         old ticker was pointing at detached nodes). Safe while the drawer is
         still hidden: tick() re-arms rather than jumping when it measures
         nothing to scroll, and openCart's _carResync restarts the clock once
         widths are real. */
      /* Never on v2's vertical rail: the ticker glides scrollLeft, which
         on a column scroller is a no-op that still burns a timer. */
      if (deskCar && !railV2) startUpsellAuto();
    }
    if (rail) {
      var railT = null;
      list.addEventListener('scroll', function () {
        syncRailThumb(); // cheap style write — live, not throttled
        if (railT) return;
        railT = setTimeout(function () { railT = null; railCheckViews(); }, 180);
      });
    }

    function row(it, idx) {
      var r = el('div', 'pp-cart__upsell');
      if (rail) {
        // Visibility-gated: paint() appends before railCheckViews runs, so
        // offsetLeft is real by the time the check reads it.
        railPending.push({ node: r, it: it, idx: idx, done: false });
      } else if (!phoneCar) {
        // Stacked rows are all on screen at once — emit on render.
        emitView(it, idx);
      }
      // (Phone-carousel views are emitted by carSync on arrival, not here.)
      var cleaned = cleanTitle(it.title);
      var _r = parseFloat(it.rating);
      var _rn = parseInt(it.reviews, 10);
      var rated = _r > 0 && _rn >= (CFG.min_reviews_shown || 10);

      if (it.image) {
        var im = d.createElement('img');
        im.className = 'pp-cart__upsell-img';
        im.src = it.image; im.alt = cleaned.name || ''; im.loading = 'lazy';
        /* The image links wherever the title links (Tyler 2026-08-27) — an
           image that looks tappable and does nothing reads as broken. Same
           degrade rule as the title: an <a> only when a URL exists. */
        var imNode = im;
        if (it.url) {
          var imA = d.createElement('a');
          imA.href = it.url;
          imA.setAttribute('aria-label', cleaned.name || 'View product');
          imA.appendChild(im);
          imNode = imA;
        }
        if (dockMode) {
          /* Card mode: wrapper kept for the zoomed cover crop — every
             product shot shares the bouquet-at-midsection framing, so one
             object-position lands on the flowers in all of them. */
          var imw = el('div', 'pp-cart__upsell-imgwrap');
          imw.appendChild(imNode);
          r.appendChild(imw);
        } else {
          r.appendChild(imNode);
        }
      }

      var info = el('div', 'pp-cart__upsell-info');
      /* Title and rating share a row. The rating had its own line, which cost
         a full row to carry five characters — and a row here is expensive
         because the whole card is four of them. Trailing the title also
         degrades cleanly: an unrated product just has a title, with no gap
         where a rating would have been. */
      /* The title links to its product page — an upsell is an unfamiliar
         product, and "what is this?" needs an answer that isn't just a
         160px image. An <a> only when we actually have a URL, so a stale
         payload degrades to the old plain title rather than a dead link. */
      /* Display name = the product title minus the "Pop-Up Bouquet" suffix,
         Mini prefix KEPT (Tyler 2026-08-03 v8: no "(Full size)/(Mini size)"
         parenthetical on upsell titles — but stripping the Mini prefix too,
         as cleanTitle does, would make a mini indistinguishable from its
         full-size twin). Unmatched titles pass through untouched. */
      var dispName = String(it.title || '')
        .replace(/\s+Pop[\s-]?Up\s+Bouquet\s*$/i, '');
      var titleEl = el(it.url ? 'a' : 'div', 'pp-cart__upsell-title', dispName);
      if (it.url) {
        titleEl.href = it.url;
        titleEl.addEventListener('click', function () {
          // sendBeacon-backed, so the emit survives the navigation.
          track.emit('cart_upsell_title_click',
            { variant_id: it.variant_id, product_id: it.product_id },
            { title: it.title, position: idx, anchor: it._anchor,
              clarity_tag: 'upsell_title_click' });
        });
      }
      /* TITLE LINE: one guaranteed-single line \u2014 the title ellipsizes
         before the line can ever wrap. Order (Tyler 2026-08-03 v2):
         \u2605 4.9 \u00b7 Daffodil Bloom \u2014 the rating LEADS as plain
         text (no pill, no review count), both arms, one convention.
         (The "(Full size)/(Mini size)" parenthetical was removed 2026-08-03
         v8 \u2014 dispName above carries the Mini prefix instead. cleaned.size
         still feeds the Add button's accessible name.) */
      var tline = el('div', 'pp-cart__upsell-tline');
      if (rated) {
        var rate = el('span', 'pp-cart__upsell-rate');
        rate.appendChild(icon(ICON.star, 'pp-cart__upsell-star'));
        rate.appendChild(el('span', null, _r.toFixed(1)));
        rate.setAttribute('aria-label',
          _r.toFixed(1) + ' out of 5 from ' + _rn + ' reviews');
        tline.appendChild(rate);
      }
      tline.appendChild(titleEl);
      info.appendChild(tline);
      /* ONE price \u2014 the figure checkout will actually charge (the
         effective price when a live discount lowers it).
         ROWS: right-aligned ON THE TITLE LINE, directly above the + (Tyler
         2026-08-03) \u2014 title reads left, money reads right, the action
         sits under the money. A row is the full drawer width, so three facts
         fit comfortably.
         CARDS (dock arm only): into the meta row instead. A 158px card cannot
         hold \u2605 4.9 + a product name + "$38.66 AUD" on one line \u2014 the
         name ellipsized to nothing and the line overflowed the card (found
         on the harness 2026-08-04, the first time the card grammar was ever
         rendered). That CSS already reserves the meta row for it ("price +
         struck price sit INLINE on one line").
         The desktop carousel is ROWS in a rail, so it keeps the title line. */
      var _eff = effectiveAddCost(it);
      var priceEl = el('span', 'pp-cart__upsell-price',
        money(_eff != null && _eff <= it.price - 50 ? _eff : it.price));
      if (!dockMode) tline.appendChild(priceEl);
      /* THREE STACKED LINES, one layout for phone AND desktop (Tyler
         2026-08-04). The row card used to cram the "Loved for" voice and the
         reason into the SAME row as the Add button — the reason fused into
         the button as a bordered white segment, which made the control wide
         enough to squash the voice beside it ("they make the button too big
         and squash the 'loved for' text").
             ★ 4.9  Tulip Kisses            $20.95 AUD
             Loved for milestones
             1 more for 15% off                   [ Add ]
         Each line owns its width now, so nothing competes: the voice gets the
         card, the nudge gets the card minus the button, and Add goes back to
         being a plain button. Costs one row of height and buys back the
         horizontal room the fused control was eating.

         Was phone-only; now every row treatment, which is also what makes the
         two viewports agree (the previous split had desktop reading its voice
         from socialFor and phones from popCats — two answers, one product). */
      if (!dockMode) {
        var pcats = popCats(it);
        if (pcats.length) {
          var popEl = el('div', 'pp-cart__upsell-pop');
          popEl._ppCats = pcats;
          fitPopLine(popEl);
          info.appendChild(popEl);
        }
      }
      var meta = el('div', 'pp-cart__upsell-meta');
      if (dockMode) meta.appendChild(priceEl);   // see the price note above
      /* ONE persuasion voice (Tyler 2026-08-03): the reason (money) overrides
         the social fact; social speaks only on reason-less cards, as quiet
         green text. In ROWS the reason doesn't sit in this meta line at all
         \u2014 it rides the + button as a fused white label (below). Card mode
         keeps reason-or-social as the centred green voice line. */
      var socChip = socialFor(it);
      if (dockMode) {
        var voice = it._reason || (socChip ? socChip.text : null);
        if (voice) {
          meta.appendChild(el('span', 'pp-cart__upsell-reason is-card', voice));
        }
      } else if (it._reason) {
        /* THE NUDGE, on its own line under the voice (Tyler 2026-08-04). Same
           words that used to be welded into the Add button; the difference is
           that it now costs the button nothing.

           Green, deliberately: `--pp-green-deep` is the same family as the
           button's `--pp-green` outline, so the nudge reads as belonging to
           the action it describes — Tyler asked for "a green tone similar to
           the 'add' button green outline color... as a kind of nudge". Not
           --pp-green itself, which is a 2.6:1 border colour and would be
           unreadable as small text; --pp-green-deep is the token this file
           already uses everywhere this voice speaks. */
        meta.appendChild(el('span', 'pp-cart__upsell-nudge', it._reason));
      }
      /* Rows action = a PLAIN tint button (Tyler 2026-08-04). It used to be a
         fused two-part control \u2014 a white segment carrying the reason welded to
         the tint square \u2014 which made the button as wide as its longest reason
         and left the voice beside it fighting for what was left. The reason
         now has its own line above (see the nudge), so the button is only ever
         as wide as the word "Add".
         The accessible name still carries the product AND the reason: they are
         separate elements visually, but a screen-reader user tabbing onto the
         button should not have to go hunting for why it's worth pressing. */
      var add = el('button', 'pp-cart__upsell-add' + (dockMode ? '' : ' is-sq'));
      add.type = 'button';
      add.setAttribute('aria-label', (u.cta || 'Add') + ' ' + (cleaned.name || '') +
        (cleaned.size ? ' (' + cleaned.size + ')' : '') +
        (it._reason ? ' \u2014 ' + it._reason : ''));
      /* Worded "Add" in BOTH arms (Tyler 2026-08-03 v4 — the bare + read
         as glyph, not action). */
      var addLabel = el('span', 'pp-cart__upsell-add-label', u.cta || 'Add');
      add.appendChild(addLabel);
      add.addEventListener('click', function () {
        add.disabled = true;
        r.classList.add('is-adding');
        /* "✓" while the request runs (Tyler 2026-08-03): instant confirmation
           in the same footprint — same width as "Add" and the "+", so nothing
           reflows mid-tap — and it covers the network wait. The repaint that
           follows removes the card (it's in the cart now), so the ✓ is also
           the card's exit note. Restored on error. */
        addLabel.textContent = '\u2713';
        track.emit('cart_upsell_add', {
          variant_id: it.variant_id, product_id: it.product_id, qty: 1,
          value: it.price / 100, currency: CFG.currency
        }, { title: it.title, reason: it._reason, position: idx,
             cards: rail ? 'carousel' : 'rows',
             anchor: it._anchor, anchors: it._anchors,
             clarity_tag: 'upsell_added' });
        /* Provenance rides ON THE ORDER, not just in analytics events: a
           hidden line property (underscore = never shown to the shopper)
           that survives checkout and lets Pippy attribute this line's
           revenue to the upsell engine forever — no cookies, no consent
           gaps. Format: "<anchor_product_id>|<reason_key>|<position>";
           gallery rows (empty-cart) have no anchor and say "gallery". */
        var prov = (emptyCart ? 'gallery' : String(it._anchor || '-')) + '|' +
          (emptyCart ? '-'
            : it._boost >= 100 ? 'unlock'
            : it._miniTwin ? 'mini'
            : it._reason ? 'soft' : 'rec') + '|' + idx;
        // add.js returns the line item, not the cart — adoptCart re-reads.
        var wonBefore = ladderTiers().won.length;
        if (rail) {
          // Captured NOW, from the outgoing rail: the repaint that follows
          // rebuilds the list and would otherwise reset it to the start.
          _railAfterAdd = { left: list.scrollLeft, cardLeft: r.offsetLeft };
        }
        api('/cart/add.js', { items: [{ id: it.variant_id, quantity: 1,
          properties: { _pp_upsell: prov } }] })
          .then(function (c) { markAdded(it.variant_id); return c; })
          .then(adoptCart)
          /* THE GIFT IS A BONUS — its failure is not the shopper's failure.
             syncFreeGift used to sit inside this chain's catch, so if the
             matching mini was out of stock, or its add caught a 429, the
             drawer told the shopper "Couldn't add that — please try again"
             about a bouquet that had ALREADY gone into the cart (Tyler
             2026-08-26, adding a 3rd bouquet from the rail). Swallow it: the
             add succeeded, the ladder simply hasn't got its gift yet, and
             the next render re-attempts the sync. */
          .then(function (c) {
            return syncFreeGift().catch(function () { return c; });
          })
          .then(function () {
            // Crossing check AFTER adoptCart so ladderTiers sees the new cart.
            _addedCrossed = ladderTiers().won.length > wonBefore;
            if (_addedCrossed) {
              track.emit('cart_affirm', {
                product_id: it.product_id, variant_id: it.variant_id
              }, { clarity_tag: 'post_add_affirm' });
            }
          })
          .then(render)
          .catch(function () {
            add.disabled = false;
            addLabel.textContent = u.cta || 'Add';
            r.classList.remove('is-adding');
            /* A failed add never repaints, so the stash would replay a
               scroll-restore on the next unrelated render (review find). */
            if (rail) _railAfterAdd = null;
            showCartError(CFG.err_generic || "Couldn't add that — please try again");
          });
      });
      /* Into the PRICE row, not the row's own right edge. Centred against the
         whole card it sat level with the gap between title and price, which is
         where the rating also ended up — two unrelated things aligned with each
         other and with nothing else. On the price line it reads as the price's
         action. */
      meta.appendChild(add);
      info.appendChild(meta);
      r.appendChild(info);
      /* v2 re-shuffles the card it just built (see v2Card). Done here,
         at the single exit point, so every caller — first paint, the
         post-add repaint, the gallery — gets the same treatment. */
      if (V2) v2Card(r, railV2, it);
      return r;
    }

    /* ── DESKTOP AUTO-SCROLL (Tyler 2026-08-04) ──────────────────────────
     *
     * The minimised desktop carousel advances itself: 5s after the drawer is
     * actually visible, then every 4.5s, looping back to the first card at
     * the end. Cadence chosen so the shopper reads their own cart first and
     * the row never moves twice while they're reaching for a card.
     *
     * The rules that keep it from being obnoxious:
     *   • hover / keyboard focus PAUSES, and resumes on leave — the row must
     *     never slide out from under a cursor that's aiming at an Add button;
     *   • any manual scroll STOPS it for good. Once she's driving, the
     *     carousel doesn't take the wheel back;
     *   • prefers-reduced-motion never starts it at all;
     *   • nothing to scroll (one card, or the rail fits) → nothing to do.
     *
     * Manual input is detected from pointerdown/wheel/touchstart, NOT from
     * the scroll event: glide() scrolls the list itself, so a scroll listener
     * would have the ticker immediately cancel itself on its own first move.
     *
     * Armed from _carResync rather than from paint(), because paint() can run
     * while the drawer is still [hidden], where every width reads 0 and the
     * "is there anything to scroll?" test would answer wrongly. */
    function startUpsellAuto() {
    /* Hard stop for v2's vertical rail. The `!railV2` guard at the call
       site is not enough — openCart's _carResync path re-arms the ticker
       independently, and glide() on a column scroller moves scrollLeft,
       which does nothing except keep a timer alive. */
    if (typeof V2 !== 'undefined' && V2 && v2Rail()) return;
      stopUpsellAuto();
      if (!deskCar || collapsed || _upsellAutoKilled) return;
      if (w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (candidates.length < 2) return;

      var FIRST_MS = 5000, EVERY_MS = 4500;
      var timer = null, paused = false, dead = false;

      function clear() { if (timer) { clearTimeout(timer); timer = null; } }
      function arm(ms) { clear(); if (!dead) timer = setTimeout(tick, ms); }
      function tick() {
        timer = null;
        if (dead) return;
        /* Drawer closed (a repaint from a PDP add, say): let the timer die
           rather than idle forever. openCart's _carResync starts a fresh one,
           which is also the behaviour we want — the 5s clock should count
           from when she can actually see the row, not from a background
           repaint she never witnessed. */
        if (!open) return;
        if (paused) { arm(EVERY_MS); return; }
        var max = list.scrollWidth - list.clientWidth;
        if (max <= 1) { arm(EVERY_MS); return; }   // nothing to scroll (yet)
        var target = 0;                            // loop home by default
        if (list.scrollLeft < max - 2) {
          var cards = list.querySelectorAll('.pp-cart__upsell');
          for (var i = 0; i < cards.length; i++) {
            if (cards[i].offsetLeft > list.scrollLeft + 2) {
              target = Math.min(max, cards[i].offsetLeft);
              break;
            }
          }
        }
        glide(list, target, 420, syncRailThumb);
        arm(EVERY_MS);
      }
      /* Two different stops. `teardown` is bookkeeping — this render is being
         replaced. `stopForGood` is the SHOPPER taking over, and only that one
         sets the session-sticky flag; if teardown set it, the first repaint
         after any add would silently retire the feature. */
      function teardown() { dead = true; clear(); }
      function stopForGood() { _upsellAutoKilled = true; teardown(); }

      var onEnter = function () { paused = true; };
      var onLeave = function () { paused = false; };
      wrap.addEventListener('mouseenter', onEnter);
      wrap.addEventListener('mouseleave', onLeave);
      wrap.addEventListener('focusin', onEnter);
      wrap.addEventListener('focusout', onLeave);
      list.addEventListener('pointerdown', stopForGood);
      list.addEventListener('wheel', stopForGood, { passive: true });
      list.addEventListener('touchstart', stopForGood, { passive: true });

      arm(FIRST_MS);
      _upsellAuto = function () {
        teardown();
        wrap.removeEventListener('mouseenter', onEnter);
        wrap.removeEventListener('mouseleave', onLeave);
        wrap.removeEventListener('focusin', onEnter);
        wrap.removeEventListener('focusout', onLeave);
        list.removeEventListener('pointerdown', stopForGood);
        list.removeEventListener('wheel', stopForGood);
        list.removeEventListener('touchstart', stopForGood);
      };
    }

    paint();
    parent.appendChild(wrap);
    _carResync = function () {
      /* fitPopLines here too: the soc line's ladder measures clientWidth, and
         render() paints while the drawer is still [hidden] where every width
         reads 0 — fitPopLine bails in that case and leaves the richest guess,
         so without a re-fit once widths are real the longest form survives and
         ellipsizes. Same measure-while-hidden trap as the rail thumb. */
      if (rail) {
        syncRailThumb(); railCheckViews(); fitPopLines(); startUpsellAuto();
        setTimeout(fitPopLines, 350);   // settle pass, as the phone arm does
      } else if (phoneCar) { carSync(); }
    };
  }

  // ──────────────────────────── footer ─────────────────────────────
  /**
   * "Arriving Tue 5 - Fri 8 Aug", from the delivery window the order-status
   * page already learned per country.
   *
   * Business days, not calendar days: quoting a Saturday as an arrival date
   * for a carrier that doesn't deliver on Saturdays is how a gift misses a
   * birthday. Orders placed after cutoff start counting tomorrow.
   *
   * Two registers, because confidence differs and the copy should say so:
   *   measured  -> "Arriving Thu, Aug 6 - Mon, Aug 10"   (a date we can stand behind)
   *   default   -> "Typically arrives in 5-10 business days"  (a duration, not a promise)
   *
   * The first version went silent on the default, which was too cautious: only
   * AU, GB and NL have enough delivered orders to have learned a window, so the
   * US cart showed nothing at all. A shopper asking "will this arrive for the
   * birthday" is better served by an honest range than by silence. What must
   * never happen is quoting a specific DATE off a number nobody measured, and
   * that distinction is what `basis` is for.
   */
  /* Phase 1 geo personalization: the PDP's destination picker (pp-bundle.js)
     persists the shopper's chosen destination as localStorage pp_dest. The
     cart must quote the SAME window for that destination — the PDP saying
     one date range and the cart another is exactly the promise drift the
     comment above warns about. Lookup mirrors pp-bundle.js destWindow():
     measured state row first, then that country's own window; no data →
     null and the line behaves exactly as before. */
  function destEdd() {
    var g = REWARDS.geo || {};
    var dest = null;
    try { dest = JSON.parse(localStorage.getItem('pp_dest') || 'null'); } catch (e) {}
    if (!dest || !dest.cc) return null;
    var regs = (g.edd_regions || {})[dest.cc];
    if (dest.region && regs && regs[dest.region]) {
      return { min_days: regs[dest.region][0],
               max_days: regs[dest.region][1], basis: 'region' };
    }
    return (g.country_edd || {})[dest.cc] || null;
  }
  function arrivalText(eddOverride) {
    /* LATENT BUG, FIXED 2026-08-26. `cutoff` is read below but was never
       declared anywhere in this file — the function has been dead since
       Tyler pulled the ETA line on 2026-08-03, so the ReferenceError was
       never reached. v2's delivery band calls it, so it had to be real
       before it could be wired up. 14:00 is the figure the comment below
       has always described. */
    var cutoff = 14;
    var e = eddOverride || destEdd() || REWARDS.edd;
    if (!e || !e.min_days) return null;
    if (e.basis === 'default') {
      var unit = S('edd_days_label', 'business days');
      var span = (e.max_days && e.max_days !== e.min_days
        ? e.min_days + '\u2013' + e.max_days : String(e.min_days)) + ' ' + unit;
      return { cutoff: false,
        text: (S('edd_typical_label', 'Typically arrives in ')) + span,
        forms: [(S('edd_typical_label', 'Typically arrives in ')) + span, span] };
    }
    var now = new Date();
    var start = new Date(now.getTime());

    function addBusiness(from, days) {
      var d0 = new Date(from.getTime()), added = 0;
      while (added < days) {
        d0.setDate(d0.getDate() + 1);
        var wd = d0.getDay();
        if (wd !== 0 && wd !== 6) added++;
      }
      return d0;
    }
    /* min_days/max_days measure CARRIER TRANSIT — ParcelPanel's
       fulfillment_date to delivery_date — so the clock starts the day we hand
       the parcel over, not the day the order lands. Counting transit from the
       order date silently dropped the handling day and quoted arrival a full
       business day earlier than Shopify's own checkout estimate, which adds
       fulfilment time on top of the same transit figure. Fulfilment is set to
       "Next business day" in Shopify (Settings -> Shipping and delivery), so
       one business day is the handling step.

       The browser-hour cutoff that used to sit here is GONE (2026-08-04) — it
       was doing the handling day's job by accident, so once an explicit
       handling day was added the two stacked and afternoon orders quoted a day
       late. It could never match Shopify anyway: Shopify cuts off at noon in
       the SHIPPING ORIGIN's timezone, this read the shopper's browser clock.
       Mirrors pp-bundle.js. */
    var ship = addBusiness(start, 1);
    var lo = addBusiness(ship, e.min_days);
    var hi = addBusiness(ship, e.max_days);
    /* Both ends get the SAME full format. Dropping the month from the first
       date to save a few characters made Intl reorder the parts — "6 Thu -
       Mon, Aug 10" — because weekday+day without a month is an unusual
       combination that locales order differently. Slightly longer and
       consistently readable beats shorter and scrambled. */
    var fmt = function (dt) {
      try {
        return new Intl.DateTimeFormat(CFG.locale || 'en', {
          weekday: 'short', day: 'numeric', month: 'short'
        }).format(dt);
      } catch (err) { return dt.toDateString(); }
    };
    /* A weekday-less twin of the range, for the phone delivery line when the
       full one will not fit beside a long place name. Dropping the WEEKDAY is
       safe where dropping the month was not (see above): day+month is an
       ordinary combination every locale orders sensibly. */
    var fmtShort = function (dt) {
      try {
        return new Intl.DateTimeFormat(CFG.locale || 'en', {
          day: 'numeric', month: 'short'
        }).format(dt);
      } catch (err) { return dt.toDateString(); }
    };
    var word = S('edd_label', 'Arriving');
    var range = (lo.getTime() === hi.getTime())
      ? fmt(lo) : fmt(lo) + ' \u2013 ' + fmt(hi);
    var rangeShort = (lo.getTime() === hi.getTime())
      ? fmtShort(lo) : fmtShort(lo) + ' \u2013 ' + fmtShort(hi);
    /* Honest urgency, and only honest urgency: the 14:00 cutoff is real (it
       feeds `start` above) and the dates shown already assume ordering before
       it, so "order within Nh for arrival X" is one true sentence, not a
       pressure widget. Static figure computed at render \u2014 a ticking countdown
       is the pattern this file deliberately bans (see the toast notes). Only
       within 8h of cutoff: "order within 13h" is clutter, not information. */
    if (now.getHours() < cutoff) {
      var hoursLeft = Math.max(1, Math.ceil(cutoff - now.getHours() - now.getMinutes() / 60));
      if (hoursLeft <= 8) {
        var cut = S('edd_order_within', 'Order within {hours}h for arrival between {range}')
          .replace('{hours}', String(hoursLeft)).replace('{range}', range);
        return {
          cutoff: true,
          text: cut,
          forms: [cut, word + ' ' + range, word + ' ' + rangeShort, rangeShort]
        };
      }
    }
    /* The weekdays go before the word does: "Arriving Sep 7 – Sep 14" is still
       a sentence, a bare date range is just a number the shopper has to
       interpret. Only the very last rung gives that up. */
    return { cutoff: false, text: word + ' ' + range,
      forms: [word + ' ' + range, word + ' ' + rangeShort, rangeShort] };
  }

  /* A time WINDOW, not a one-shot flag. The first version consumed a boolean
     on the first paint — but the fetchLiveRate repaint REPLACES the footer's
     nodes moments after open, killing the animation mid-flight, and the paint
     itself lands while the panel is still sliding in (420ms). So: openCart
     stamps a window; every footer paint inside it sweeps its own fresh node,
     delayed until the slide-in has finished. A repaint restarts the sweep
     instead of orphaning it. */
  var savingsFlashOpenAt = 0;
  var savingsFlashUntil = 0;

  /* ───────────────── v2: the delivery band + the receipt ─────────────────
     Two ideas, one block. The band answers "where is this going and when
     does it land" with a NAMED place and a DATE, which is the difference
     between a promise and an estimate. The receipt answers "what am I
     paying" line by line, so checkout only ever confirms a figure the
     shopper has already accepted — surprise cost at checkout being the
     single most-cited reason carts get abandoned. */

  /* The highest quantity rung this cart has actually EARNED, as a percent.
     Read from the same tier data the ladder uses and gated on the same
     eligibility count, so the pill can never advertise a rate checkout
     won't apply. Returns 0 when nothing is earned — the row then hides
     rather than printing "0% OFF". */
  /* A percent is only printed when it PROVABLY explains the money on the same
     row. `saved` is the whole cart's discount — every line, every mechanism —
     while a quantity tier is scoped to one collection, so on an ordinary
     PetalPal cart the two disagree: 3 bouquets + a mini + the gift note earns
     the 15% rung, but 15% of the printed Subtotal is not the printed Discount,
     because the mini and the note sit outside the discount base. A free gift
     in the cart skews it the other way — its zeroed line is inside `saved` and
     contributes no percent at all.

     So: reconstruct what the winning tier would actually take off, using the
     same per-unit floor Shopify applies (and that this file already relies on
     in projectedSubtotalIfAdded), and show the pill only if that equals the
     figure being printed. Anything else — mixed scopes, a code, a gift, a
     non-tier promotion — renders the amount with no pill. Never a rate the
     row beside it contradicts. */
  function v2EarnedPct(saved) {
    if (!cart || !saved) return 0;

    /* FIRST, ASK THE CART. Shopify reports what it actually applied in
       `discount_applications` (and per line in `discount_allocations`), and a
       percentage discount carries its own rate. That is authoritative for
       both automatic discounts and CODES — which matters here because the AU
       offer experiment delivers its rung as a code (BUNDLE10-AU), so
       reconstructing it from the tier table alone missed it and the pill
       silently vanished on exactly the market running the test (Tyler
       2026-08-26: "where is the 10% OFF discount pill").
       Only ever one percentage: if two were applied, naming one of them
       beside a combined total would be the mismatch this guard exists to
       prevent. */
    var apps = cart.discount_applications || cart.cart_level_discount_applications;
    if (apps && apps.length) {
      var pcts = [];
      apps.forEach(function (a) {
        if (a && a.value_type === 'percentage' && Number(a.value) > 0) {
          pcts.push(Number(a.value));
        }
      });
      if (pcts.length === 1) return pcts[0];
      if (pcts.length > 1) return 0;
    }

    var best = null;
    (REWARDS.quantity_tiers || []).forEach(function (t) {
      if (!t.percent) return;
      if (eligibleItemCount(t) < t.min_qty) return;
      if (!best || t.percent > best.percent) best = t;
    });
    if (!best) return 0;

    var expected = 0;
    cart.items.forEach(function (i) {
      if (isMechanismLine(i)) return;
      if (!itemCountsTowardTier(i, best)) return;
      var qty = i.quantity || 0;
      var line = (i.original_line_price != null
        ? i.original_line_price : i.final_line_price) || 0;
      var unit = qty ? line / qty : line;
      expected += qty * Math.floor(unit * best.percent / 100);
    });
    /* 2c of slack absorbs presentment rounding; anything wider means some
       other discount is in play and the rate would be a false claim. */
    return Math.abs(expected - saved) <= 2 ? best.percent : 0;
  }

  /* MIRRORS pp-bundle.js's REGION_NAMES — deliberately duplicated rather than
     threaded through the metafield, because pp-cart.liquid's geo block ships
     only `edd_regions` and `country_edd` (no names), and adding a field to
     that Liquid is the change class that broke add-to-cart store-wide on
     2026-08-11. If a region is added on the PDP side, add it here too; an
     unknown code degrades to the code itself, never to a crash. */
  var V2_REGION_NAMES = {
    AU: { NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland',
          SA: 'South Australia', WA: 'Western Australia', TAS: 'Tasmania',
          ACT: 'Canberra / ACT', NT: 'Northern Territory' },
    CA: { AB: 'Alberta', BC: 'British Columbia', ON: 'Ontario',
          QC: 'Quebec', SK: 'Saskatchewan' },
    US: { CA: 'California', FL: 'Florida', GA: 'Georgia', IL: 'Illinois',
          NY: 'New York', PA: 'Pennsylvania', TX: 'Texas' },
    GB: { ENG: 'England', SCT: 'Scotland' },
    IE: { D: 'Dublin' }
  };
  function v2CountryName(c2) {
    try {
      return new Intl.DisplayNames([CFG.locale || 'en'], { type: 'region' }).of(c2) || c2;
    } catch (e) { return c2; }
  }
  function v2RegionName(cc, r) {
    if (!r) return v2CountryName(cc);
    return (V2_REGION_NAMES[cc] || {})[r] || r;
  }

  /* Detected geo, in memory only. Mirrors pp-bundle.js's applyDetected: a
     DEFAULT, never an override — an explicit pp_dest always wins, and this is
     never written to storage (persisting a guess would make the shopper's own
     choice unrecoverable). Populated from window.ppGeo at boot. */
  var V2_GEO = null;

  /* Explicit choice > detected location > the market's country.
     The label prefers the CITY, exactly as the PDP ship line does — "Delivery
     to Adelaide" is a place the shopper recognises; "Delivery to Australia"
     is the thing they already knew. The region behind it is what prices the
     window, so the label can be a city while the date stays measured. */
  function v2Dest(cc, regions) {
    var stored = null;
    try { stored = JSON.parse(localStorage.getItem('pp_dest') || 'null'); } catch (e) {}
    /* A stored choice is honoured even for ANOTHER country — the picker can
       ship anywhere the rewards sync knows a window for, exactly like the
       PDP's. Only the region table is market-specific, so a foreign pick
       simply carries no region. */
    if (stored && stored.cc) {
      return { cc: stored.cc, region: stored.cc === cc ? (stored.region || '') : '',
               src: 'chosen',
               label: stored.label ||
                      (stored.cc === cc ? v2RegionName(cc, stored.region || '')
                                        : v2CountryName(stored.cc)) };
    }
    var g = V2_GEO;
    if (g && g.country === cc) {
      var rc = (g.region && regions[g.region]) ? g.region : '';
      return { cc: cc, region: rc, src: 'detected',
               label: g.city || (rc ? v2RegionName(cc, rc) : '') ||
                      g.region_name || v2CountryName(cc) };
    }
    return { cc: cc, region: '', src: 'default', label: v2CountryName(cc) };
  }

  function v2GeoBand() {
    var g = REWARDS.geo || {};
    var cc = g.cc || CFG.country;
    if (!cc) return null;

    var regions = (g.edd_regions || {})[cc] || {};
    var keys = Object.keys(regions);
    var dest = v2Dest(cc, regions);

    var band = el('div', 'pp-cart__geo');
    band.appendChild(icon(ICON.truck, 'pp-cart__geo-ic'));

    var txt = el('span', 'pp-cart__geo-txt');
    /* Its own element so v2FitGeo() can drop it on a narrow phone carrying a
       long place name. It is the one genuinely redundant part of the line —
       the truck icon beside it already says "delivery". */
    txt.appendChild(el('span', 'pp-cart__geo-pre', S('geo_deliver_to', 'Delivery to') + ' '));

    /* A VISIBLE label with a transparent <select> stretched over it — the
       same construction as the PDP's ship line. A styled <select> is sized by
       its LONGEST option, so "Australia" sat in a box wide enough for "New
       South Wales" and the caret drifted a dropdown's width away from the
       word. Sizing to the label kills that gap and makes the two surfaces
       look like one feature. */
    var pick = el('span', 'pp-cart__geo-pick');
    pick.appendChild(el('span', 'pp-cart__geo-lbl', dest.label));

    var sel = d.createElement('select');
    sel.className = 'pp-cart__geo-sel';
    /* The overlay-invisibility is FUNCTIONAL, so it is set inline as well as
       in the stylesheet. During the ~30min CDN fingerprint lag after a deploy
       a shopper can receive new JS with old CSS, and a class-only select then
       renders VISIBLY beside its own label — "Australia Australia", which is
       exactly what Tyler hit on the PDP on 2026-08-26. */
    sel.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;'
      + 'opacity:0;cursor:pointer;border:0;-webkit-appearance:none;appearance:none;';
    sel.setAttribute('aria-label', A11Y('geo_dest', 'Delivery destination'));

    /* Same option model as the PDP picker so the two behave identically:
       values are "cc:region", the shopper's own market leads with its states,
       and every other country we have a measured window for follows. */
    function opt(val, label) {
      var o = d.createElement('option');
      o.value = val; o.textContent = label;
      return o;
    }
    var names = V2_REGION_NAMES[cc] || {};
    var og = d.createElement('optgroup');
    og.label = v2CountryName(cc);
    og.appendChild(opt(cc + ':', v2CountryName(cc)));
    keys.map(function (rc) { return { rc: rc, n: names[rc] || rc }; })
      .sort(function (a, b) { return a.n < b.n ? -1 : 1; })
      .forEach(function (x) { og.appendChild(opt(cc + ':' + x.rc, x.n)); });
    sel.appendChild(og);

    var others = [];
    var ck = g.country_edd || {};
    for (var k2 in ck) { if (ck[k2] && k2 !== cc) others.push(k2); }
    if (others.length) {
      var og2 = d.createElement('optgroup');
      og2.label = S('dest_other', 'Other countries');
      others.map(function (c2) { return { c: c2, n: v2CountryName(c2) }; })
        .sort(function (a, b) { return a.n < b.n ? -1 : 1; })
        .forEach(function (x) { og2.appendChild(opt(x.c + ':', x.n)); });
      sel.appendChild(og2);
    }
    sel.value = (dest.cc || cc) + ':' + (dest.region || '');

    sel.addEventListener('change', function () {
      /* Written in the PDP picker's EXACT shape — {cc, region, label, src} —
         because pp-bundle.js reads this same key and paints `label` verbatim
         on two ungated surfaces. A raw region code here would leak a machine
         token onto the PDP and survive disarming the preview, since nothing
         ever rewrites this value. */
      var parts = String(sel.value || '').split(':');
      var cc2 = parts[0] || cc, rc2 = parts[1] || '';
      var selOpt = sel.options[sel.selectedIndex];
      try {
        localStorage.setItem('pp_dest', JSON.stringify({
          cc: cc2,
          region: rc2,
          /* The option's own text — a human name by construction, which is
             what pp-bundle.js paints verbatim on the PDP. */
          label: (selOpt && selOpt.textContent) ||
                 (rc2 ? v2RegionName(cc2, rc2) : v2CountryName(cc2)),
          src: 'chosen'
        }));
      } catch (e) { /* storage blocked — the choice just won't persist */ }
      track.emit('cart_geo_dest_change', { cc: cc2, region: rc2 || null });
      render();
    });
    pick.appendChild(sel);
    txt.appendChild(pick);
    band.appendChild(txt);

    /* The window is resolved for the destination this band NAMES, not from
       whatever destEdd() finds in storage — a pp_dest saved for another
       country, or a region with no measured row, both used to let the place
       and the date describe different things. No measured row → no date,
       rather than a promise nobody measured. */
    var rw = (dest.cc === cc) ? regions[dest.region] : null;
    var eddSel = (dest.region && rw && rw.length === 2)
      ? { min_days: rw[0], max_days: rw[1], basis: 'region' }
      : ((g.country_edd || {})[dest.cc || cc] || null);
    var arr = arrivalText(eddSel);
    if (arr && arr.text) {
      var etaEl = el('span', 'pp-cart__geo-eta', arr.text);
      /* Progressively shorter wordings of the SAME arrival, longest first,
         for v2FitGeo() to fall back through on a narrow phone. Pipe-joined:
         no wording contains one. */
      if (arr.forms && arr.forms.length > 1) {
        etaEl.setAttribute('data-forms', arr.forms.join('|'));
      }
      band.appendChild(etaEl);
    }
    return band;
  }

  /* Fit the delivery line to the phone's width (Tyler 2026-08-27).
     It has to stay on ONE legible line, and BOTH halves vary: the label is a
     city, a country or a long state ("Northern Territory"), and the arrival
     half is a date RANGE. No single font-size is right for every combination
     — at 12.5px "Adelaide" fits a 390px phone and "South Australia" silently
     ellipsised the date away. So measure the row's real content width and
     scale the type to it, with a floor; below the floor the date drops its
     weekday names rather than the type dropping below readable.
     Phone only: the desktop panel has never been short of room here. */
  function v2FitGeo(band, tries) {
    if (!band || !v2Phone()) return;
    /* A band with no layout yet — the drawer is still opening, or the footer
       has not been attached — measures 0 wide, and `0 <= 0` reads as "it
       fits". Nothing would ever shrink. Wait for a frame instead, a handful
       of times, then give up rather than spin. */
    if (band.clientWidth < 40) {
      if ((tries || 0) > 5) return;
      (w.requestAnimationFrame || w.setTimeout)(function () {
        v2FitGeo(band, (tries || 0) + 1);
      });
      return;
    }
    /* SOFT is the smallest size the type is allowed to reach while a shorter
       wording of the arrival is still on the table; MIN is the hard floor once
       there is nothing left to shorten. Shedding "Mon," from a date costs the
       shopper far less than two more points of type. */
    var MAX = 12.5, SOFT = 11.4, MIN = 10.2;
    var eta = band.querySelector('.pp-cart__geo-eta');
    var forms = [null];
    if (eta) {
      var raw = eta.getAttribute('data-forms');
      if (raw) forms = raw.split('|');
    }

    function fits() { return band.scrollWidth <= band.clientWidth + 1; }
    function fitType(floor) {
      var size = MAX;
      band.style.fontSize = size + 'px';
      if (fits()) return true;
      /* Text width is very nearly linear in font-size, so one proportional
         jump lands on or just under the answer; the loop mops up rounding. */
      var need = band.scrollWidth;
      if (need > 0) {
        size = Math.max(floor, Math.floor(MAX * (band.clientWidth / need) * 10) / 10);
        band.style.fontSize = size + 'px';
      }
      while (!fits() && size > floor) {
        size = Math.max(floor, Math.round((size - 0.3) * 10) / 10);
        band.style.fontSize = size + 'px';
      }
      return fits();
    }

    var pre = band.querySelector('.pp-cart__geo-pre');
    if (pre) pre.style.display = '';

    for (var i = 0; i < forms.length; i++) {
      /* Always re-applied, never assumed: this runs again on every render, so
         a shortening an earlier pass needed at a narrower width has to be
         undone before deciding whether it is needed now. */
      if (eta && forms[i] !== null) eta.textContent = forms[i];
      if (fitType(i === forms.length - 1 ? MIN : SOFT)) return;
    }
    /* Nothing left to shorten and the type is at its floor — a long country
       name on a narrow phone ("United Arab Emirates" at 360px). Drop the
       "Delivery to" prefix rather than clip the arrival estimate or shave the
       shipping promise down to a wording that is no longer true. */
    if (pre) {
      pre.style.display = 'none';
      fitType(MIN);
    }
  }

  function v2Receipt(host, original, saved, delivery, grand) {
    var band = v2GeoBand();
    if (band) host.appendChild(band);

    var rc = el('div', 'pp-cart__rcpt');
    function row(label, cls) {
      var r = el('div', 'pp-cart__rcpt-row' + (cls ? ' ' + cls : ''));
      var l = el('span', 'pp-cart__rcpt-lbl');
      if (typeof label === 'string') l.appendChild(d.createTextNode(label));
      else l.appendChild(label);
      r.appendChild(l);
      var v = el('span', 'pp-cart__rcpt-v');
      r.appendChild(v);
      rc.appendChild(r);
      return v;
    }

    /* Subtotal — hidden on phones by CSS, where the three lines below tell
       the same story in less height. */
    var n = realItemCount();
    row(n === 1
      ? S('rcpt_subtotal_one', 'Subtotal · 1 item')
      : S('rcpt_subtotal_many', 'Subtotal · {n} items').replace('{n}', String(n)),
      'is-subtotal').textContent = money(original);

    if (saved > 0) {
      var lbl = el('span', 'pp-cart__rcpt-lbl');
      lbl.appendChild(d.createTextNode(S('rcpt_discount', 'Discount')));
      var pct = v2EarnedPct(saved);
      if (pct) {
        lbl.appendChild(el('span', 'pp-cart__rcpt-pill',
          S('percent_off_caps', '{percent}% OFF').replace('{percent}', pct)));
      }
      var dv = row(lbl);
      dv.className += ' pp-cart__rcpt-green';
      dv.textContent = '−' + money(saved);
    }

    var shipV = row(S('shipping_label', 'Delivery'));
    if (delivery && delivery.free) {
      shipV.textContent = S('shipping_free_label', 'Free');
      shipV.className += ' pp-cart__rcpt-green';
    } else if (delivery && delivery.minor != null) {
      shipV.textContent = money(delivery.minor);
    } else {
      shipV.textContent = S('shipping_calc_label', 'At checkout');
    }

    var tot = el('div', 'pp-cart__rcpt-total');
    tot.appendChild(el('span', null, S('total_label', 'Total')));
    var tv = el('span', 'pp-cart__rcpt-v', money(grand));
    tot.appendChild(tv);
    rc.appendChild(tot);
    host.appendChild(rc);
  }

  function renderFooter() {
    var host = root && root.querySelector('.pp-cart__footer');
    if (!host) return;
    host.textContent = '';
    if (!cart || !cart.items.length) return;

    /* Simplified footer. v2 stacked four rows (subtotal / save / delivery /
       total) which read as heavy for what is really one number. Now:
         · discount chip + saving   — only when a discount actually applies
         · subtotal                 — only when it differs from the total
         · Total                    — always, the one number that matters
         · delivery + lock          — merged into one small note below
       So the default no-discount footer is two lines instead of four. */
    var original = cart.original_total_price || cart.total_price;
    var saved = Math.max(0, original - cart.total_price);

    /* ONE FIGURE, THEN TWO SUPPORTING LINES.
       This stacked SIX rows: Delivery/Free, arrival, Total, a chip row, "You
       save", and a split beneath it. Tyler: "this footer/bottom part of the
       slide cart now looks too busy" — it was, and most of it said the same
       thing twice. The "free delivery" chip repeated the Delivery row verbatim;
       the "15% off" chip repeated both the struck-through price on every line
       above it AND the savings figure below it. A chip whose content is stated
       more precisely three lines away is decoration, and three greens (two
       chips plus the button) meant the eye landed on the two you can't press.
       Now: the total dominates, one line qualifies it, and the delivery promise
       goes LAST — it's reassurance, and reassurance is what should be read
       immediately before the button. */
    var delivery = deliveryEstimate();
    /* Hoisted: both the classic footer and v2's receipt price the same
       grand total, and the checkout button below reads it either way. */
    var grand = cart.total_price + ((delivery && delivery.minor) || 0);

    /* ONE COLUMN OF VALUES, RIGHT-ALIGNED — a receipt, which is the layout
       every shopper already knows how to read.
       The previous attempt put the saving flush right and the delivery promise
       flush left, so the two facts leaned away from each other with nothing to
       align to. Tyler: "'Free delivery·Arriving...' is skewed to the left and
       'You save...' is skewed to the right." Correct, and the fix isn't nudging
       either one — it's giving every figure the same right edge.
       It also splits the two savings, which was the other ask: the discount is
       its own signed line, and the delivery saving is the struck-through rate
       next to the word Free — the same "was / now" idiom the line items above
       already use, so nothing new has to be learned. No aggregate "You save"
       any more: each number now reconciles with a checkout line on its own,
       which is stronger than one merged figure that reconciles with nothing. */
    if (V2) {
      /* v2 replaces the whole sum/total stack with the geo band + receipt.
         The form, the button and the trust row below are SHARED — only the
         money block differs, which keeps the two arms from drifting. */
      v2Receipt(host, original, saved, delivery, grand);
    } else {
      function sumRow(label, cls) {
        var r = el('div', 'pp-cart__sum' + (cls ? ' ' + cls : ''));
        r.appendChild(el('span', 'pp-cart__sum-label', label));
        var v = el('span', 'pp-cart__sum-value');
        r.appendChild(v);
        host.appendChild(r);
        return v;
      }

      /* "Saved", not "Discount" — Tyler's call, and the better one: "Discount"
         names the mechanism, "Saved" names what the shopper gets out of it. The
         minus sign went with it; it was doing arithmetic notation's job in a row
         that is about benefit, and the green already says which direction it
         moves the total. */
      if (saved > 0) {
        sumRow(S('savings_label_row', 'Saved'), 'is-save').textContent = money(saved);
      }

      /* Peak-end: the drawer opening on a cart that has already WON something is
         the end of the shopping experience — one soft sweep draws the eye to the
         money once, then rests. No new text, no chip: the rows the footer
         already prices ARE the message; this only makes sure they're read.
         Delayed past the panel's 420ms slide-in so none of it plays off-screen;
         re-applied by any repaint inside the window so the live-rate footer
         rebuild restarts the sweep rather than silently ending it. */
      if (saved > 0 && Date.now() < savingsFlashUntil) {
        var sweepRow = host.querySelector('.pp-cart__sum.is-save');
        if (sweepRow) {
          var wait = Math.max(0, savingsFlashOpenAt + 520 - Date.now());
          setTimeout(function () {
            // The live-rate repaint may have replaced this node; a detached
            // node's class change is harmless and the repaint schedules its own.
            if (!sweepRow.parentNode) return;
            /* RESUME, don't restart. The drawer paints its footer ~3 times just
               after opening (first paint, live rate, gift settle); restarting
               the animation on each fresh node read as three flashes. A negative
               animation-delay starts this node's sweep partway through, so the
               repaints stitch into ONE continuous pass. */
            var elapsed = Date.now() - (savingsFlashOpenAt + 520);
            if (elapsed >= 2000) return;   // the sweep is already over
            sweepRow.style.animationDelay = (-Math.max(0, elapsed)) + 'ms';
            sweepRow.className += ' pp-cart__sum--flash';
          }, wait);
        }
      }

      var shipVal = sumRow(S('shipping_label', 'Delivery'));
      var shipSaved = deliverySavedMinor();
      if (delivery && delivery.free) {
        /* The rate they're NOT paying, struck through, then "Free". This is where
           the delivery saving lives now — it needs no sentence, and being a real
           struck figure rather than a claim about one makes it self-evidently
           checkable. Only when we can source the rate honestly; outside USD the
           synced bands are base-currency, so it degrades to a plain "Free". */
        if (shipSaved) {
          shipVal.appendChild(el('span', 'pp-cart__sum-was', money(shipSaved)));
        }
        shipVal.appendChild(el('strong', 'pp-cart__sum-free',
          S('shipping_free_label', 'Free')));
      } else if (delivery && delivery.minor != null) {
        shipVal.textContent = money(delivery.minor);
      } else {
        // Unknown (no matching band, or the band is in another currency) — say so
        // rather than invent a number.
        shipVal.textContent = S('shipping_calc_label', 'At checkout');
        shipVal.className += ' pp-cart__sum-value--soft';
      }

      /* The "Arriving Fri, Aug 14 – Fri, Aug 21" ETA line is NOT rendered
         (Tyler 2026-08-03): on a phone the footer is fighting the body for
         height and the ETA was the most expendable line in it. arrivalText()
         and the .pp-cart__eta styles stay intact for an easy re-add. */

      var total = el('div', 'pp-cart__total');
      total.appendChild(el('span', null, S('total_label', 'Total')));
      // When delivery is known, the total the shopper will actually pay includes
      // it — quoting a total that excludes a cost we just displayed would be the
      // same bait-and-switch we're trying to remove.
      total.appendChild(el('span', 'pp-cart__total-value', money(grand)));
      host.appendChild(total);
    }


    /* The amount ON the button restates the Total row above it on purpose:
       the figure a shopper commits to should be under their thumb at the
       moment of the click, not one glance up. Label-then-amount with a middot
       needs no grammar, so every locale composes the same way.
       cfg.checkout_amount:false removes it without a deploy. */
    /* "Checkout", not "Secure checkout" (Tyler 2026-08-02) — the security
       claim lives in the trust row right below; on the button it just made
       the label longer next to the amount. */
    var btn;
    if (V2) {
      /* "Secure Checkout" + a padlock, and NO amount (Tyler 2026-08-26).
         The receipt states the total on the line directly above, so putting
         it on the button too spent the drawer's most valuable control on a
         figure the eye had just read. The security claim moves ONTO the
         button because that is the instant it is needed. */
      btn = el('button', 'pp-cart__btn pp-cart__btn--checkout',
        S('checkout_label_secure', 'Secure Checkout'));
      btn.appendChild(icon(ICON.lock, 'pp-lock'));
    } else {
      var btnLabel = S('checkout_label', 'Checkout');
      if (CFG.checkout_amount !== false) btnLabel += ' · ' + money(grand);
      btn = el('button', 'pp-cart__btn pp-cart__btn--checkout', btnLabel);
    }
    btn.type = 'submit';
    if (_wigglePending) {
      _wigglePending = false;
      btn.className += ' is-wiggle';
      setTimeout(function () {
        btn.className = btn.className.replace(' is-wiggle', '');
      }, 600);
    }
    btn.addEventListener('click', function () {
      track.emit('cart_checkout_click', {
        value: cart.total_price / 100, currency: CFG.currency,
        qty: cart.item_count, cart_token: cart.token
      }, { clarity_tag: 'checkout_clicked' });
    });

    // A real form submit to Shopify's own /checkout — deliberately boring.
    var form = d.createElement('form');
    form.action = '/checkout';
    form.method = 'post';
    form.appendChild(btn);
    host.appendChild(form);

    // Payment marks are rendered by Liquid from Shopify's own SVG helper into a
    // <template>, then cloned — never hotlinked third-party badge images. The
    // list is filtered in Liquid to the marks our orders actually use, which is
    // what keeps this to a single row.
    /* PAYMENT MARKS: REJECTED TWICE — DO NOT RE-ADD. Tyler pulled the
       full-colour row ("their colors grab too much attention"), and when the
       2026-08-02 CRO pass restored them desaturated he pulled those too:
       "greyscale, small, and take up too much valuable screen real estate."
       The trust row alone is the wanted treatment. The Liquid <template> and
       .pp-cart__pay CSS remain for reference; nothing renders them. */
    var trust = (CFG.trust_row && CFG.trust_row.length) ? CFG.trust_row : [
      { icon: 'lock',  text: 'Secure checkout' },
      { icon: 'truck', text: 'Tracked delivery' },
      /* "30-day returns", taken verbatim from the published refund policy
         ("We have a 30-day return policy") rather than the vaguer
         "satisfaction guarantee" — a specific number is both a stronger claim
         and a checkable one, and it's the wording the policy page will confirm
         if a shopper follows it. Replaced "Card & envelope included", which was
         already stated on every line item above and so spent a trust slot
         repeating the cart back to itself. */
      { icon: 'returns', text: '30-day returns' }
    ];
    if (V2) {
      /* v2 trust bar, Grüns layout: icon left of a two-line text block,
         three across one row.
         The review figures are REAL and were measured 2026-08-26: the public
         review corpus behind pippy.co/reviews holds 12,742 published reviews,
         and 4.9 is the Loox average shoppers see on the product pages. Those
         are two different populations — the corpus itself averages 4.67 — so
         this pairs the on-site rating with the total review count, which is
         Tyler's explicit call. Do not "correct" one number without the other.
         ⚠ "money-back guarantee" is a stronger promise than the published
         "30-day return policy". Tyler kept it knowingly; if the policy page
         is ever the tiebreak, change the POLICY or this line, not silently. */
      trust = [
        { icon: 'star',    l1: S('trust_rev_1', '4.9 stars, 12,000+'), l2: S('trust_rev_2', 'Reviews') },
        { icon: 'truck',   l1: S('trust_ship_1', 'Tracked worldwide'), l2: S('trust_ship_2', 'delivery') },
        { icon: 'returns', l1: S('trust_gtee_1', '30-day money-'),     l2: S('trust_gtee_2', 'back guarantee') }
      ];
    }
    var trustRow = el('div', 'pp-cart__trust');
    trust.forEach(function (t) {
      var cell = el('div', 'pp-cart__trust-item');
      /* Translated trust_row tables in the metafield still name the old
         `back` icon; remap so every locale gets the return arrow. */
      var name = t.icon === 'back' ? 'returns' : t.icon;
      cell.appendChild(icon(ICON[name] || ICON.check, 'pp-cart__trust-icon'));
      if (t.l1) {
        /* Two lines, broken where the design breaks them — not left to the
           container width, which would wrap mid-phrase at some viewports. */
        var sp = el('span');
        sp.appendChild(d.createTextNode(t.l1));
        sp.appendChild(d.createElement('br'));
        sp.appendChild(d.createTextNode(t.l2 || ''));
        cell.appendChild(sp);
      } else {
        cell.appendChild(el('span', null, t.text));
      }
      trustRow.appendChild(cell);
    });
    host.appendChild(trustRow);

    /* Fit the delivery line HERE, not at the end of render(). The live
       shipping rate resolves after render() has finished and calls
       renderFooter() again on its own, which builds a BRAND NEW geo band —
       so a fit done in render() only ever measured the band that the rate
       was about to replace. */
    if (V2) v2FitGeo(host.querySelector('.pp-cart__geo'));
  }

  /* The theme's header badge counts a cart the drawer just changed —
     Dawn only refreshes it through its own add flow, so a removal in here
     left a phantom "1" until a hard refresh (Tyler 2026-08-02). Built
     span-by-span with textContent, same markup Dawn renders. */
  function syncThemeCartBadge() {
    try {
      var host = d.getElementById('cart-icon-bubble');
      if (!host || !cart) return;
      var n = cart.item_count || 0;
      var bubble = host.querySelector('.cart-count-bubble');
      if (n <= 0) { if (bubble) bubble.remove(); return; }
      if (!bubble) {
        bubble = d.createElement('div');
        bubble.className = 'cart-count-bubble';
        var num = d.createElement('span');
        num.setAttribute('aria-hidden', 'true');
        var sr = d.createElement('span');
        sr.className = 'visually-hidden';
        bubble.appendChild(num);
        bubble.appendChild(sr);
        host.appendChild(bubble);
      }
      var spans = bubble.querySelectorAll('span');
      if (spans[0]) spans[0].textContent = String(n);
      if (spans[1]) spans[1].textContent = String(n);
    } catch (e) { /* the badge is decoration; never break the cart over it */ }
  }

  // ───────────────────────────── render ────────────────────────────
  /* Distinct PRODUCTS, not lines and not units. The push-down rule is about
     a cart that has become a list worth reading — two of the same bouquet is
     still one thing chosen. Mechanism lines (the gift note, a free gift) are
     not products the shopper picked. */
  function v2DistinctProducts() {
    if (!cart) return 0;
    var seen = {}, n = 0;
    cart.items.forEach(function (i) {
      if (isMechanismLine(i)) return;
      var k = String(i.product_id);
      if (seen[k]) return;
      seen[k] = true;
      n++;
    });
    return n;
  }

  /* THE PHONE DOCK RULE (Tyler 2026-08-26).
       1 product  → the upsell box stays docked above the footer, always
                    visible: there is room, and it is the best chance to add.
       2+ products → it moves INTO the scrolling body, below every cart line,
                    so the cart the shopper is actually reviewing gets the
                    space; the box reappears when they scroll to the bottom.
     Only ever moves a POPULATED host — parking an empty band inside the body
     would leave a phantom gap under the last line. */
  function v2PlaceHost() {
    if (!root || !panelEl) return;
    var host = root.querySelector('.pp-cart__upsellhost');
    var body = root.querySelector('.pp-cart__body');
    var footer = root.querySelector('.pp-cart__footer');
    if (!host || !body || !footer) return;
    if (v2Phone() && (v2DistinctProducts() >= 2 || noteOpen) && host.firstChild) {
      if (host.parentNode !== body) body.appendChild(host);
    } else if (host.parentNode !== panelEl) {
      panelEl.insertBefore(host, footer);
    }
  }

  function render() {
    if (!root) return;
    dismissLossPop();
    var body = root.querySelector('.pp-cart__body');
    /* v2 may have parked the upsell host INSIDE the body on the previous
       render (the push-down state). `body.textContent = ''` below would
       destroy it, and the `root.querySelector` that repopulates it further
       down would then find nothing — the drawer would silently lose its
       upsells for the rest of the session. Rescue it back to the panel
       first; v2PlaceHost() re-parks it at the end of this render. */
    if (V2 && body && panelEl) {
      var _h = root.querySelector('.pp-cart__upsellhost');
      if (_h && _h.parentNode === body) {
        panelEl.insertBefore(_h, root.querySelector('.pp-cart__footer'));
      }
    }
    /* A full render empties the body below, which destroys any open note
       editor — so the auto-collapse it asked for expires here too, and the
       upsell section goes back to whatever the SHOPPER last chose. Reset
       before renderUpsells reads it. */
    noteOpen = false;
    body.textContent = '';

    var rewardHost = root.querySelector('.pp-cart__rewardhost');
    if (rewardHost) {
      rewardHost.textContent = '';
      renderReward(rewardHost);
    }

    // Gift notes are no longer a single cart-level block — each bouquet
    // carries its own, rendered inside its line by renderLineNote().
    renderLines(body);

    /* Upsells ALWAYS dock for a non-empty cart (fixed, not a variant);
       the A/B is the card style inside. Host cleared on EVERY render — a
       stale dock would survive the cart emptying. Empty carts keep the
       in-body gallery. */
    var upsellHost = root.querySelector('.pp-cart__upsellhost');
    if (upsellHost) upsellHost.textContent = '';
    if (cart && cart.items.length && upsellHost) {
      renderUpsells(upsellHost, upsellCardStyle() === 'carousel', true);
    } else {
      renderUpsells(body, false, false);
    }
    renderFooter();

    /* New items append at the BOTTOM (first-added-first), which can be below
       the fold — and the just-added pulse + affirmation are worthless unseen.
       Bring the fresh row into view; instant (not smooth) under
       prefers-reduced-motion. */
    if (justAdded() && _addedVariant) {
      try {
        var freshRow = body.querySelector('.pp-cart__line.is-just-added');
        if (freshRow) {
          var noMotion = w.matchMedia &&
            w.matchMedia('(prefers-reduced-motion: reduce)').matches;
          freshRow.scrollIntoView({
            block: 'nearest', behavior: noMotion ? 'auto' : 'smooth'
          });
        }
      } catch (e) { /* scroll assist only — never break the cart */ }
    }
    syncThemeCartBadge();

    /* Ask Shopify for the authoritative rate for this cart state, then repaint
       just the footer. The synced bands have already painted something, so the
       shopper never sees an empty or "calculating" delivery line. */
    fetchLiveRate().then(function (r) { if (r) renderFooter(); });

    announce();

    /* "YOUR CART — 3 ITEMS" lives in the white header row now (Tyler
       2026-08-03; it was the in-body lines-zone label). Same strings, so the
       nightly translations carry over. Empty cart falls back to the plain
       title — "Your cart — 0 items" would be the drawer leading with a
       negative. */
    var titleEl = root.querySelector('.pp-cart__title');
    if (titleEl) {
      var tn = cart ? realItemCount() : 0;
      // fillTemplate APPENDS — cleared first or every render stacks a copy.
      titleEl.textContent = '';
      if (tn > 0) {
        fillTemplate(titleEl, tn === 1
          ? S('cart_zone_one', 'Your cart — 1 item')
          : S('cart_zone_many', 'Your cart — {n} items'),
          { n: { text: String(tn) } });
      } else {
        titleEl.textContent = S('title', 'Your cart');
      }
    }

    /* LAST — the host must already be populated (renderUpsells ran above)
       and the body must already hold its lines, or the push-down lands in
       the wrong place. */
    if (V2) v2PlaceHost();
  }

  function build() {
    /* The ONE hook every v2 CSS rule hangs off. Without the flag this string
       is unchanged, so the ungated cart is byte-identical. */
    root = el('div', 'pp-cart' + (V2 ? ' pp-cart--v2' : ''));
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    /* aria-labelledby beats aria-label here: it points at the visible <h2>,
       so the dialog's accessible name can never drift from what's on screen. */
    root.setAttribute('aria-labelledby', 'pp-cart-title');
    root.hidden = true;

    var overlay = el('div', 'pp-cart__overlay');
    overlay.addEventListener('click', close);

    var panel = el('div', 'pp-cart__panel');
    panelEl = panel;
    /* Focusable but not tabbable, so opening can move focus INTO the dialog.
       Without this the screen reader stays wherever it was on the page behind
       and never announces that a dialog opened at all. */
    panel.setAttribute('tabindex', '-1');
    var header = el('div', 'pp-cart__header');
    /* The title is VISIBLE again — as the drawer's white top row (Tyler
       2026-08-03, supersedes the same-day screen-reader-only cut): "YOUR
       CART — 3 ITEMS" left, ✕ right, on white ABOVE the green reward band.
       This also retires the floating-✕-over-the-banner overlap for good.
       render() rewrites the text with the live count (the zone label that
       used to sit inside the body). Still the aria-labelledby target. */
    var h2 = el('h2', 'pp-cart__title', S('title', 'Your cart'));
    h2.id = 'pp-cart-title';
    header.appendChild(h2);
    var x = el('button', 'pp-cart__close');
    x.setAttribute('aria-label', A11Y('close_cart', 'Close cart'));
    x.appendChild(icon(ICON.close));
    x.addEventListener('click', close);
    header.appendChild(x);

    panel.appendChild(header);
    /* Outside the scrolling body, so it genuinely hugs the top and is always
       visible. It used to live INSIDE the body as a `position: sticky` block,
       which needed an outline hack to mask items scrolling under it and still
       left a gap above. A sibling is simpler and can go full-bleed. */
    panel.appendChild(el('div', 'pp-cart__rewardhost'));
    panel.appendChild(el('div', 'pp-cart__body'));
    /* Upsell dock (A/B variant 'dock', 2026-08-03): a SIBLING between body
       and footer, same reasoning as the reward host above — outside the
       scroll body it can dock and go full-bleed. Also what makes the
       horizontal rail safe this time: the old in-body rail forced
       overflow-x onto the scrolling body (the bug that killed it). */
    panel.appendChild(el('div', 'pp-cart__upsellhost'));
    panel.appendChild(el('div', 'pp-cart__footer'));

    /* Cart changes are the whole point of this UI and were entirely silent to
       a screen reader: the count and total repaint with no announcement, so
       adding a bouquet produced no feedback at all. Polite, not assertive —
       it should not interrupt someone mid-sentence. */
    liveEl = el('div', 'pp-cart__sr');
    liveEl.setAttribute('aria-live', 'polite');
    liveEl.setAttribute('aria-atomic', 'true');
    panel.appendChild(liveEl);

    root.appendChild(overlay);
    root.appendChild(panel);
    d.body.appendChild(root);

    /* Escape closes; Tab cycles WITHIN the panel. The trap is not optional
       once aria-modal="true" is set: that attribute tells assistive tech the
       rest of the page is inert, so tabbing out of an untrapped dialog lands
       the user somewhere the screen reader has already been told doesn't
       exist. Claiming to be modal without behaving like one is worse than
       not claiming it. */
    d.addEventListener('keydown', function (e) {
      if (!open) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab' || !panelEl) return;
      var f = focusables();
      if (!f.length) { e.preventDefault(); panelEl.focus(); return; }
      var first = f[0], last = f[f.length - 1];
      var cur = d.activeElement;
      var inside = panelEl.contains(cur);
      if (e.shiftKey && (cur === first || !inside)) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (cur === last || !inside)) {
        e.preventDefault(); first.focus();
      }
    });
  }

  var FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),'
    + 'input:not([disabled]):not([type="hidden"]),select:not([disabled]),'
    + '[tabindex]:not([tabindex="-1"])';
  function focusables() {
    if (!panelEl) return [];
    return Array.prototype.filter.call(
      panelEl.querySelectorAll(FOCUSABLE),
      function (n) {
        // offsetParent is null for display:none — a collapsed gift-note field
        // must not become an invisible tab stop.
        return n.offsetParent !== null || n === d.activeElement;
      });
  }

  /** Announce the cart's new state. Debounced by the caller's render cycle. */
  function announce() {
    if (!liveEl || !cart) return;
    var n = realItemCount();
    liveEl.textContent = (n === 1 ? '1 item' : n + ' items')
      + ', ' + (S('total_label', 'total')) + ' ' + money(cart.total_price);
  }

  // ─────────────────────── open / close / toast ────────────────────
  /* The theme's own Dawn <cart-drawer> is display:none'd by our CSS, but its
   * JS still runs: every cart-icon click and every add-to-cart calls its
   * open()/renderContents(), which adds `overflow-hidden` to <body>. Nobody
   * can close an invisible drawer, so the class stayed forever and the page
   * couldn't scroll after ours closed (Tyler, live on mobile 2026-08-02).
   * Strip its lock and its active state whenever we open or close. */
  function releaseThemeDrawer() {
    d.body.classList.remove('overflow-hidden');
    var cd = d.querySelector('cart-drawer');
    if (cd && cd.classList) cd.classList.remove('active', 'animate');
  }

  function openCart(reason) {
    /* Captured BEFORE the drawer paints, so close() can hand focus back to
       whatever the shopper actually pressed — usually an add-to-cart button
       partway down a product page. Without it, closing drops focus to the top
       of the document and a keyboard user has to travel back down. */
    lastFocus = d.activeElement;
    return getCart().then(function (c) {
      cart = c;
      /* Settle the gift before the first paint. A shopper can arrive with a
         qualifying cart built on a previous visit, or one that stopped
         qualifying since — neither goes through changeQty, so without this the
         drawer would open showing the wrong thing. */
      return syncFreeGift().then(function () { return cart; });
    }).then(function (c) {
      cart = c;
      savingsFlashOpenAt = Date.now();
      savingsFlashUntil = savingsFlashOpenAt + 3000;
      /* Re-arm the one-shot rail peek BEFORE render() — render reaches the
         peek check synchronously, so resetting after it would leave the flag
         armed for the rest of the open session and every later repaint
         (qty change, post-add) would peek again (review find, 2026-08-03). */
      _railPeeked = false;
      // A fresh open is a fresh session for the desktop auto-scroll too.
      _upsellAutoKilled = false;
      render();
      root.hidden = false;
      requestAnimationFrame(function () {
        root.classList.add('is-open');
        // After the panel is visible; focusing a hidden element is a no-op.
        if (panelEl) { try { panelEl.focus(); } catch (e) {} }
        // Now that widths are real, correct the chrome painted at 0px.
        if (_carResync) _carResync();
        if (_rewardRefit) _rewardRefit();
      });
      open = true;
      bumpVisitCount();
      d.body.classList.add('pp-cart-locked');
      releaseThemeDrawer();
      track.emit('cart_open', {
        qty: c.item_count, value: c.total_price / 100, currency: CFG.currency
      }, { reason: reason || 'manual',
           reward_copy: CFG.reward_nmiss !== false ? 'nmiss' : 'add',
           reward_endow: CFG.reward_endow !== false ? 'on' : 'off',
           upsell_cards: upsellCardStyle(),
           clarity_tag: 'cart_opened' });
    });
  }

  function close() {
    if (!open) return;
    dismissLossPop();
    // Nothing should keep gliding behind a closed drawer.
    stopUpsellAuto();
    root.classList.remove('is-open');
    open = false;
    d.body.classList.remove('pp-cart-locked');
    releaseThemeDrawer();
    /* Hand focus back before the drawer is hidden. If the trigger has since
       been removed from the DOM (an upsell row the shopper just added), fall
       back to the body rather than leaving focus on a hidden element. */
    if (lastFocus && d.body.contains(lastFocus) && lastFocus.focus) {
      try { lastFocus.focus(); } catch (e) {}
    }
    lastFocus = null;
    setTimeout(function () { if (!open) root.hidden = true; }, 420);
    track.emit('cart_close', cart ? { qty: cart.item_count } : null,
      { clarity_tag: 'cart_closed' });
  }

  /* ASOS-style reassurance toast, with SOFT urgency (Tyler's call, 2026-07-27).
   *
   * Two things at once:
   *   reassurance — "It's in the bag", so leaving to keep browsing feels safe
   *   soft nudge  — "Our bouquets sell fast", which motivates without asserting
   *                 anything falsifiable
   *
   * Explicitly NOT a countdown and NOT a fabricated stock number. We considered
   * a literal "held for 1 hour" timer and rejected it: we don't actually
   * reserve inventory, so it would be a false statement — and this audience
   * skews elderly and scam-wary, exactly the people a fake timer costs you.
   * Regulators (ACCC/FTC/EU) also now actively enforce against false urgency.
   * If we ever want hard scarcity, drive it from real Shopify inventory.
   *
   * Both strings are config-driven so the copy can be tuned without a deploy.
   *
   * A MEASURED LIMIT ON THAT FREEDOM (2026-08-04). The toast is centred at
   * bottom:26px, and the Gorgias launcher parks bottom-right at z-index
   * 2147483636 — above this toast's 9100 — so a toast wide enough to reach the
   * corner has its tail painted over. On a 390px phone the collision begins at
   * 210px of box width; the configured strings render 195px, so there are 15px
   * of room and no more. Lengthening `subtext` in config is a no-deploy change
   * that can cross that line with nobody reviewing it. No clicks are at stake
   * (this toast is pointer-events:none) — it is purely the confirmation being
   * half-hidden — which is why it is documented here rather than fixed with a
   * fourth launcher rule. Re-measure if you retune the copy.
   *
   * The three collisions that DID need moving the launcher are keyed on
   * `pp-cart-locked` / `pp-save-open` (demote, pp-cart.css) and `pp-atc-on`
   * (lift, sections/main-product.liquid). Those four are the complete set of
   * fixed-position elements we own — swept 2026-08-04. */
  function toast() {
    var t = CFG.toast || {};
    if (!t.enabled) return;
    var n = el('div', 'pp-cart__toast');
    var ic = el('span', 'pp-cart__toast-icon');
    ic.appendChild(icon(ICON.check));
    n.appendChild(ic);
    var txt = el('div', 'pp-cart__toast-text');
    txt.appendChild(el('span', null, t.text || "It's in the bag"));
    if (t.subtext) txt.appendChild(el('span', 'pp-cart__toast-sub', t.subtext));
    n.appendChild(txt);
    d.body.appendChild(n);
    requestAnimationFrame(function () { n.classList.add('is-in'); });
    setTimeout(function () {
      n.classList.remove('is-in');
      setTimeout(function () { n.remove(); }, 420);
    }, t.duration_ms || 3200);
  }

  /* ── detect adds from ANY source ───────────────────────────────────
   * Intercepting <form> submits only catches the theme's own add-to-cart.
   * On bundle products the button belongs to Kaching Bundles, which runs its
   * OWN fetch and then navigates to /cart — so our handler never saw the add,
   * and the drawer opened only to be destroyed by the navigation.
   *
   * Patching fetch/XHR catches every add regardless of who made it, without
   * touching Kaching's bundle logic (which decides WHICH variants to add — the
   * one thing we must not intercept, or multi-item bundles break).
   */
  function watchCartAdds() {
    var seen = function () {
      // Another actor changed the cart. Refresh and show the drawer rather
      // than letting the shopper get bounced to /cart.
      var prev = cart && cart.items.map(function (i) {
        return { v: i.variant_id, q: i.quantity };
      });
      getCart().then(function (c) {
        cart = c;
        // Which variant grew? That line gets the just-added flash.
        var grown = null;
        (c.items || []).forEach(function (i) {
          var was = 0;
          (prev || []).forEach(function (p) { if (p.v === i.variant_id) was += p.q; });
          if (i.quantity > was && !grown) grown = i.variant_id;
        });
        markAdded(grown);
        render();
        if (CFG.auto_open !== false && !open) {
          openCart('add_to_cart_external');
        } else if (!open && CFG.toast && CFG.toast.enabled) {
          /* Toast ONLY when the drawer stays closed. The desktop toast slot
             is the top-right corner — the exact corner the drawer slides
             into — so toast-plus-drawer always collides (the "ghost text
             over the reward bar" Tyler reported live 2026-08-02). */
          toast();
        }
      }).catch(function () {});
    };

    if (w.fetch) {
      /* Bind to the window explicitly. This file runs in strict mode, so a
         bare `fetch(...)` call arrives here with `this === undefined`, and
         forwarding that to native fetch throws "Illegal invocation" — which
         would break every request the cart makes through this wrapper, not
         just the add it's watching for. Also fail open: a wrapper that can
         throw must never take the underlying request down with it. */
      var _fetch = w.fetch.bind(w);
      w.fetch = function (input) {
        var p = _fetch.apply(null, arguments);
        try {
          var url = (typeof input === 'string' ? input : (input && input.url)) || '';
          if (url.indexOf('/cart/add') !== -1) {
            p.then(function (res) { if (res && res.ok) setTimeout(seen, 60); })
             .catch(function () {});
          }
        } catch (e) { /* observation only — never affect the caller */ }
        return p;
      };
    }
    var _open = w.XMLHttpRequest && w.XMLHttpRequest.prototype.open;
    if (_open) {
      w.XMLHttpRequest.prototype.open = function (method, url) {
        this.__ppIsAdd = String(url || '').indexOf('/cart/add') !== -1;
        if (this.__ppIsAdd) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) setTimeout(seen, 60);
          });
        }
        return _open.apply(this, arguments);
      };
    }
  }

  // ───────────────────────── ATC interception ──────────────────────
  /**
   * Is this a product add-to-cart form?
   *
   * Matching only on action containing "/cart/add" was too narrow. Kaching's
   * bundle form doesn't necessarily carry that action, so its submits sailed
   * straight past us and the browser navigated to /cart. Qikify managed to
   * intercept the same button, which proved the interception point existed and
   * mine was simply looking in the wrong place.
   *
   * A hidden `name="id"` variant input is the reliable signature of a Shopify
   * product form regardless of what the action says or which app decorated it.
   */
  function isProductForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = String(form.action || '');
    if (action.indexOf('/cart/add') !== -1) return true;
    if (form.getAttribute('data-type') === 'add-to-cart-form') return true;
    if (form.closest && form.closest('product-form')) return true;
    return !!form.querySelector('[name="id"]');
  }

  function submitAddToCart(form) {
    var fd = new FormData(form);
    return fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('add_failed_' + r.status);
        return r.json();
      })
      .then(function () {
        try {
          markAdded(parseInt(fd.get('id'), 10) || null);
          if (CFG.auto_open !== false) { openCart('add_to_cart'); return; }
          // Drawer staying closed — the toast is the only feedback. It never
          // shows alongside the drawer (they share the desktop top-right).
          if (CFG.toast && CFG.toast.enabled) toast();
          getCart().then(function (c) { cart = c; render(); });
        } catch (err) {
          getCart().then(function (c) { cart = c; render(); }).catch(function () {});
        }
      });
  }

  function bindAddToCart() {
    /* A PROGRAMMATIC form.submit() does NOT fire the submit event, so an app
       that adds via its own code and then calls form.submit() bypasses every
       listener. Patching the prototype is the only way to see it — and this is
       the most likely reason the redirect survived two previous fixes. */
    try {
      var _nativeSubmit = w.HTMLFormElement.prototype.submit;
      w.HTMLFormElement.prototype.submit = function () {
        if (isProductForm(this)) {
          submitAddToCart(this).catch(function () {
            _nativeSubmit.call(this);
          }.bind(this));
          return;
        }
        return _nativeSubmit.apply(this, arguments);
      };
    } catch (e) { /* never break the storefront */ }

    d.addEventListener('submit', function (e) {
      var form = e.target;
      if (!isProductForm(form)) return;
      e.preventDefault();
      var fd = new FormData(form);
      fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('add_failed_' + r.status);
          return r.json();
        })
        .then(function () {
          /* The fallback below is deliberately scoped to the ADD failing.
             Previously one broad .catch() wrapped the render too, so any error
             while opening the drawer fell through to form.submit() and threw
             the shopper onto /cart — the drawer would open and then the page
             navigated out from under it. A rendering bug must never become a
             redirect. */
          try {
            if (CFG.toast && CFG.toast.enabled) toast();
            if (CFG.auto_open !== false) { openCart('add_to_cart'); return; }
            getCart().then(function (c) { cart = c; render(); });
          } catch (err) {
            // Cart is updated server-side; just refresh what we show.
            getCart().then(function (c) { cart = c; render(); }).catch(function () {});
          }
        })
        .catch(function () { form.submit(); });   // only if the ADD itself failed
    }, true);

    /* Subfolder markets prefix every URL (/th-th/cart), so matching the bare
       "/cart" href only caught the primary market — 13 markets' cart icons
       fell through to the theme instead of this drawer. */
    var cartHref = ((w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/') + 'cart';
    d.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest(
        '[data-pp-cart-open], a[href="/cart"], a[href="' + cartHref + '"]');
      if (!t) return;
      e.preventDefault();
      openCart('icon');
    });
  }

  // ─────────────────── exit-intent cart save ───────────────────
  /**
   * Offer to email the shopper their cart when they look like they're leaving.
   *
   * NO DISCOUNT. The offer is the cart itself — a link that restores exactly
   * what they built, which is genuinely useful for a gift being chosen across
   * two devices or two sittings. Adding a code here would train shoppers to
   * abandon in order to earn one, and it would undercut the quantity ladder
   * that the whole rail is built to sell.
   *
   * TWO TRIGGERS, because desktop exit-intent doesn't exist on a phone:
   *   · Desktop — pointer leaves through the TOP of the viewport, toward the
   *     tabs and the address bar. Not the sides or bottom, which are just
   *     someone reaching for a scrollbar or another window.
   *   · Any device — the page was hidden for a while and has come BACK. Fired
   *     on return rather than on leave, which is the part that makes it
   *     acceptable: they are looking at the screen when it appears, and "want
   *     your cart emailed?" is a reasonable thing to find after stepping away.
   *     A modal thrown up as someone switches apps is just shouting at a
   *     screen nobody is watching.
   *
   * It asks once. Dismissed or submitted, it stays gone for 30 days — an
   * exit-intent popup that reappears is the reason people hate exit-intent
   * popups. Suppressed entirely with an empty cart, on checkout, and while the
   * drawer is open, since a shopper reading their cart doesn't need a second
   * layer on top of it.
   */
  var EXIT_KEY = 'pp_cart_saved_until';
  var EXIT_HIDDEN_MS = 30000;   // "stepped away", not "changed tab for a second"
  var _exitShown = false;
  var _hiddenAt = 0;
  var _exitRoot = null;
  var _exitPrevFocus = null;

  function exitSuppressed() {
    /* OFF, and opt-in rather than opt-out. Tyler, 2026-07-30: "seems a bit weird
       to build, especially when klaviyo already has a way to make sign up forms
       within klaviyo... maybe turn off Exit-intent capture for now" — and he's
       right that it duplicates paid-for tooling. Klaviyo's form builder does
       exit-intent natively, and its Shopify integration already fires onsite
       abandonment events carrying the cart, so the only thing this added was
       our own permalink in our own event.
       Left in place rather than deleted because the server endpoint, the
       Klaviyo metric and the dialog are all built and verified — re-enabling is
       one flag, and ripping it out to maybe re-add it later is the more
       expensive mistake. Requires an explicit true so it cannot come back by
       accident. */
    if (!(CFG.cart_save && CFG.cart_save.enabled === true)) return true;
    if (_exitShown || _exitRoot) return true;
    if (!cart || !cart.items || !cart.items.length) return true;
    if (open) return true;
    if (/\/(checkouts?|thank_you|orders)(\/|$)/.test(location.pathname)) return true;
    try {
      var until = parseInt(localStorage.getItem(EXIT_KEY) || '0', 10);
      if (until && Date.now() < until) return true;
    } catch (e) { /* private mode: no memory, so it may show once */ }
    return false;
  }

  function exitRemember() {
    try {
      localStorage.setItem(EXIT_KEY, String(Date.now() + 30 * 86400000));
    } catch (e) { /* private mode */ }
  }

  /* Shopify's own cart permalink, so the link restores quantities too and we
     don't have to rebuild the cart ourselves.

     THE GIFT-NOTE CHARGE LINE IS EXCLUDED. A permalink can only carry variant
     IDs and quantities — it cannot carry line item properties, so the $5 charge
     would come back without the message it pays for, and the shopper would
     return to a cart billing them for a card with nothing written on it. The
     bouquets come back; the note is re-offered on each of them, which is the
     honest version. */
  function recoveryUrl() {
    var parts = [];
    (cart.items || []).forEach(function (i) {
      if (i.variant_id && !isMechanismLine(i)) {
        parts.push(i.variant_id + ':' + (i.quantity || 1));
      }
    });
    return parts.length ? location.origin + '/cart/' + parts.join(',') : '';
  }

  function closeExit() {
    if (!_exitRoot) return;
    _exitRoot.remove();
    _exitRoot = null;
    d.body.classList.remove('pp-save-open');
    if (_exitPrevFocus && _exitPrevFocus.focus) {
      try { _exitPrevFocus.focus(); } catch (e) {}
    }
    _exitPrevFocus = null;
  }

  function showExitCapture(reason) {
    _exitShown = true;
    _exitPrevFocus = d.activeElement;

    var root = el('div', 'pp-save');
    var overlay = el('div', 'pp-save__overlay');
    overlay.addEventListener('click', function () { exitRemember(); closeExit(); });

    var box = el('div', 'pp-save__box');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'pp-save-title');
    box.tabIndex = -1;

    var x = el('button', 'pp-save__x');
    x.type = 'button';
    x.setAttribute('aria-label', CFG.cart_save && CFG.cart_save.dismiss_label
      || 'No thanks, close');
    x.appendChild(icon(ICON.close));
    x.addEventListener('click', function () { exitRemember(); closeExit(); });
    box.appendChild(x);

    var title = el('h2', 'pp-save__title',
      (CFG.cart_save && CFG.cart_save.title) || 'Want your cart emailed to you?');
    title.id = 'pp-save-title';
    box.appendChild(title);
    box.appendChild(el('p', 'pp-save__sub',
      (CFG.cart_save && CFG.cart_save.sub)
      || "We'll send a link that puts these bouquets straight back in your "
       + 'cart — handy if you want to finish on your phone or think it over.'));

    var form = d.createElement('form');
    form.className = 'pp-save__form';
    var input = d.createElement('input');
    input.type = 'email';
    input.required = true;
    input.className = 'pp-save__input';
    input.autocomplete = 'email';
    input.placeholder = (CFG.cart_save && CFG.cart_save.placeholder)
      || 'you@example.com';
    input.setAttribute('aria-label',
      (CFG.cart_save && CFG.cart_save.field_label) || 'Email address');
    var send = el('button', 'pp-save__send',
      (CFG.cart_save && CFG.cart_save.cta) || 'Email my cart');
    send.type = 'submit';
    form.appendChild(input);
    form.appendChild(send);

    /* Unticked, and separate. The email was given to receive ONE cart link;
       bundling marketing consent into that would be consent nobody gave.
       The flow gates on this flag — see _cart_save_email in server.py. */
    var optRow = el('label', 'pp-save__opt');
    var opt = d.createElement('input');
    opt.type = 'checkbox';
    optRow.appendChild(opt);
    optRow.appendChild(el('span', null,
      (CFG.cart_save && CFG.cart_save.consent_label)
      || 'Also send me occasional offers'));

    var status = el('div', 'pp-save__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (input.value || '').trim();
      if (!email || email.indexOf('@') < 1) {
        status.textContent = (CFG.cart_save && CFG.cart_save.err_email)
          || 'That email doesn’t look right.';
        status.className = 'pp-save__status is-error';
        input.focus();
        return;
      }
      send.disabled = true;
      status.className = 'pp-save__status';
      status.textContent = (CFG.cart_save && CFG.cart_save.sending) || 'Sending…';
      fetch((CFG.cart_save && CFG.cart_save.endpoint)
            || 'https://pippy.co/cart/save-email', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },  // simple request: no preflight
        body: JSON.stringify({
          email: email,
          consent: !!opt.checked,
          cart_token: cart.token,
          subtotal_minor: cart.total_price,
          currency: CFG.currency,
          recovery_url: recoveryUrl(),
          items: (cart.items || []).map(function (i) {
            return { title: i.product_title, variant_id: i.variant_id,
                     quantity: i.quantity, price_minor: i.price };
          })
        })
      }).then(function (r) { return r.ok ? r.json() : { ok: false }; })
        .then(function (j) {
          if (!j || !j.ok) throw new Error((j && j.error) || 'failed');
          exitRemember();
          track.emit('cart_save_email', {
            value: cart.total_price / 100, currency: CFG.currency,
            qty: cart.item_count
          }, { reason: reason, consent: !!opt.checked });
          form.remove();
          optRow.remove();
          status.className = 'pp-save__status is-ok';
          status.textContent = (CFG.cart_save && CFG.cart_save.done)
            || 'Sent — check your inbox. Your cart is safe here too.';
        })
        .catch(function (err) {
          send.disabled = false;
          status.className = 'pp-save__status is-error';
          /* Say which failure it was. "Something went wrong" makes a shopper
             retype an address that was never the problem. */
          status.textContent = /invalid_email/.test(String(err.message))
            ? ((CFG.cart_save && CFG.cart_save.err_email)
               || 'That email doesn’t look right.')
            : ((CFG.cart_save && CFG.cart_save.err_send)
               || 'Couldn’t send just now — your cart is still saved here.');
          try { console.warn('[pp-cart] cart save failed:', err); } catch (e2) {}
        });
    });

    box.appendChild(form);
    box.appendChild(optRow);
    box.appendChild(status);
    root.appendChild(overlay);
    root.appendChild(box);
    d.body.appendChild(root);
    _exitRoot = root;
    /* Demotes the Gorgias launcher behind this dialog — see the rule keyed on
       body.pp-save-open in pp-cart.css. NOT cosmetic: the launcher outranks
       this dialog's scrim, and a bubble floating on top of a scrim whose whole
       job is click-outside-to-dismiss is a trap — the tap that should close the
       dialog opens a chat instead. No overflow lock to go with it (unlike the
       drawer's pp-cart-locked): this is one field over a page the shopper was
       already reading, and freezing the scroll behind it would make a small ask
       feel like a takeover. */
    d.body.classList.add('pp-save-open');

    /* Same reasoning as the drawer's trap: aria-modal tells assistive tech the
       rest of the page is inert, so tabbing out would land the user somewhere
       the screen reader has been told doesn't exist. */
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { exitRemember(); closeExit(); return; }
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(box.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    input.focus();

    track.emit('cart_save_view', {
      value: cart.total_price / 100, currency: CFG.currency, qty: cart.item_count
    }, { reason: reason });
  }

  function bindExitIntent() {
    /* relatedTarget null + clientY <= 0 is the pointer crossing the TOP edge.
       Without the clientY check this fires on every trip to a scrollbar. */
    d.addEventListener('mouseout', function (e) {
      if (e.relatedTarget || e.clientY > 0) return;
      if (exitSuppressed()) return;
      showExitCapture('mouse_exit');
    });

    d.addEventListener('visibilitychange', function () {
      if (d.visibilityState === 'hidden') { _hiddenAt = Date.now(); return; }
      if (!_hiddenAt || Date.now() - _hiddenAt < EXIT_HIDDEN_MS) return;
      _hiddenAt = 0;
      if (exitSuppressed()) return;
      showExitCapture('returned');
    });
  }

  // ───────────────────────────── boot ──────────────────────────────
  function boot() {
    build();
    bindAddToCart();
    watchCartAdds();
    bindExitIntent();

    /* Arriving back from a retired /cart page (see the inline redirect in
       pp-cart.liquid) — pick up where the shopper expected to be, with the
       drawer open, so the round trip is invisible to them. */
    var flagged = false;
    try {
      flagged = sessionStorage.getItem('pp_open_cart') === '1';
      if (flagged) sessionStorage.removeItem('pp_open_cart');
    } catch (e) { /* private mode */ }

    getCart().then(function (c) {
      cart = c;
      render();
      if (flagged) openCart('cart_page_redirect');
    });
    w.PPCart = {
      open: openCart, close: close,
      refresh: function () { return getCart().then(function (c) { cart = c; render(); }); }
    };

    /* DETECTED LOCATION — the same source the PDP ship line uses, so the two
       surfaces name the same place. pp-geo.js resolves window.ppGeo once per
       page and caches for 24h, so this costs nothing extra here.
       Detected is a DEFAULT: v2Dest() lets an explicit pp_dest win, and this
       never writes to storage. Repaints the footer if the drawer is already
       open when the answer lands (the fetch is async and usually resolves
       after first paint). */
    if (V2) {
      var applyGeo = function (gg) {
        if (!gg || !gg.country) return;
        V2_GEO = gg;
        if (open && cart) renderFooter();
      };
      if (w.ppGeo && w.ppGeo.then) w.ppGeo.then(applyGeo);
      else d.addEventListener('pp:geo', function (ev) { applyGeo(ev.detail); });
    }

    /* BREAKPOINT LISTENER — v2 only, and the first one this file has ever
       had. The drawer picks its treatment from matchMedia at RENDER time and
       nothing re-ran on resize, so a shopper who opened the cart wide and
       then narrowed the window kept the desktop DOM at phone width (and vice
       versa). v2 has two real breakpoints — the rail at 1100 and the phone
       carousel at 560 — so both are watched.

       Guarded three ways so this can't thrash: only on an actual crossing
       (matchMedia fires on change, not on every pixel), only while the
       drawer is open, and only when there is a cart to draw. */
    if (V2 && w.matchMedia) {
      var mqs = [w.matchMedia('(min-width: 1100px)'), w.matchMedia('(max-width: 560px)')];
      var onMQ = function () { if (open && cart) render(); };
      mqs.forEach(function (mq) {
        if (mq.addEventListener) mq.addEventListener('change', onMQ);
        else if (mq.addListener) mq.addListener(onMQ);   // Safari < 14
      });
    }
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
})(window, document);
